import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  getDb,
  dbA,
  fsAll,
  fsSave,
  fsDel,
  fsOne,
  fsByMonth,
  FB_PROD,
  FB_TEST,
  FB_ADMIN,
  ADMIN_CPF,
  ADMIN_PW,
  signInWithGoogle,
} from "./lib/firebase.js";
import { GOLD, GOLD_L, GOLD_D, NAVY, NAVY_M, NAVY_L, GEO, TH, A } from "./lib/theme.js";
import {
  gid,
  fmt,
  now,
  pad,
  ymd,
  ym,
  addM,
  cleanCPF,
  fmtCPF,
  sysDark,
  MONTHS,
  vld,
  parseCfg,
  rebuildFat,
  sortItems,
} from "./lib/utils.js";
import {
  DEF_EXP,
  DEF_INC,
  DEF_NEW_USERS,
  DEF_AUSERS,
  DEF_UC,
  DEF_UB,
  SA_ALL,
  SA,
  SU,
  _store,
  storeGet,
  storeSave,
  storeDel,
} from "./lib/constants.js";
import { saveSess, loadSess, clearSess } from "./lib/session.js";

// ── Hash helper ───────────────────────────────────────────────
function h(s) {
  let x = 0;
  for (let i = 0; i < s.length; i++) {
    x = (x << 5) - x + s.charCodeAt(i);
    x |= 0;
  }
  return "h" + Math.abs(x).toString(36);
}

// ── UI Primitives ─────────────────────────────────────────────
function Err({ msg }) {
  return msg ? (
    <div
      style={{
        background: "rgba(139,50,50,0.15)",
        border: "1px solid rgba(139,50,50,0.4)",
        borderRadius: 6,
        padding: "8px 12px",
        marginBottom: 12,
        fontSize: 12,
        color: "#C05050",
        fontFamily: GEO,
      }}
    >
      ⚠ {msg}
    </div>
  ) : null;
}

function Btn({ children, onClick, disabled, style = {}, variant = "gold" }) {
  const base = {
    padding: "10px 20px",
    borderRadius: 7,
    border: "none",
    fontWeight: 700,
    fontSize: 13,
    cursor: "pointer",
    fontFamily: GEO,
    letterSpacing: "0.4px",
    transition: "opacity 0.2s",
    ...style,
    opacity: disabled ? 0.6 : 1,
  };
  const vs = {
    gold: {
      background: `linear-gradient(135deg,${GOLD_D},${GOLD})`,
      color: "#0D1A00",
    },
    ghost: {
      background: "transparent",
      border: "1px solid rgba(201,168,76,0.4)",
      color: GOLD_L,
    },
    danger: {
      background: "rgba(139,58,58,0.8)",
      color: "#fff",
      border: "none",
    },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{ ...base, ...vs[variant] }}
    >
      {children}
    </button>
  );
}

function Modal({ onClose, title, children, dark, maxW = 640 }) {
  const th = TH(dark);
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.72)",
        display: "flex",
        alignItems: "flex-end",
        justifyContent: "center",
        zIndex: 500,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: th.sur,
          borderRadius: "16px 16px 0 0",
          padding: "1.5rem",
          width: "100%",
          maxWidth: maxW,
          boxSizing: "border-box",
          maxHeight: "92vh",
          overflowY: "auto",
          boxShadow: `0 -4px 40px rgba(0,0,0,0.5)`,
          borderTop: `2px solid ${GOLD}`,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 18,
          }}
        >
          <h3
            style={{
              fontSize: 15,
              fontWeight: 700,
              margin: 0,
              color: th.txt,
              fontFamily: GEO,
              letterSpacing: "0.5px",
            }}
          >
            {title}
          </h3>
          <button
            onClick={onClose}
            style={{
              background: "transparent",
              border: `1px solid ${th.bdr}`,
              cursor: "pointer",
              fontSize: 14,
              color: th.t2,
              borderRadius: "50%",
              width: 28,
              height: 28,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Toggle({ value, onChange }) {
  return (
    <div
      onClick={() => onChange(!value)}
      style={{
        width: 44,
        height: 24,
        borderRadius: 12,
        background: value ? GOLD : "rgba(100,100,100,0.3)",
        cursor: "pointer",
        position: "relative",
        transition: "background 0.2s",
        flexShrink: 0,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 3,
          left: value ? 23 : 3,
          width: 18,
          height: 18,
          borderRadius: "50%",
          background: "#fff",
          boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
          transition: "left 0.2s",
        }}
      />
    </div>
  );
}

function SortBar({ value, onChange, dark }) {
  const th = TH(dark);
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        padding: "6px 10px",
        borderRadius: 6,
        border: `1px solid ${th.ib}`,
        fontSize: 12,
        background: th.inp,
        color: th.txt,
        outline: "none",
        fontFamily: GEO,
      }}
    >
      <option value="date_desc">Data (recente)</option>
      <option value="date_asc">Data (antigo)</option>
      <option value="amount_desc">Valor (maior)</option>
      <option value="amount_asc">Valor (menor)</option>
      <option value="alpha_asc">A→Z</option>
      <option value="alpha_desc">Z→A</option>
    </select>
  );
}

function MultiSel({ label, options, selected, onChange, cmap, dark }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const fn = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);
  const th = TH(dark);
  const allSel = selected.length === 0;
  const toggle = (v) => {
    if (v === "all") {
      onChange([]);
      return;
    }
    onChange(
      selected.includes(v) ? selected.filter((s) => s !== v) : [...selected, v],
    );
  };
  return (
    <div ref={ref} style={{ position: "relative", userSelect: "none" }}>
      <button
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          padding: "6px 12px",
          borderRadius: 6,
          border: `1px solid ${selected.length > 0 ? GOLD : th.ib}`,
          background: selected.length > 0 ? "rgba(201,168,76,0.08)" : th.inp,
          color: selected.length > 0 ? th.acc : th.t2,
          fontSize: 12,
          cursor: "pointer",
          whiteSpace: "nowrap",
          fontFamily: GEO,
        }}
      >
        {label}
        {!allSel && ` (${selected.length})`} {open ? "▲" : "▼"}
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            background: th.sur,
            border: `1px solid ${th.bdr}`,
            borderRadius: 8,
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            zIndex: 400,
            minWidth: 180,
            padding: "6px 0",
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          <label
            onClick={() => toggle("all")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 14px",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: allSel ? 700 : 400,
              color: allSel ? th.acc : th.txt,
              background: allSel ? "rgba(201,168,76,0.08)" : "transparent",
              fontFamily: GEO,
            }}
          >
            <input
              type="checkbox"
              readOnly
              checked={allSel}
              style={{ accentColor: GOLD }}
            />{" "}
            Todos
          </label>
          <div style={{ height: 1, background: th.bdr }} />
          {options.map((o) => {
            const sel = selected.includes(o.value),
              col = cmap ? cmap[o.value] : null;
            return (
              <label
                key={o.value}
                onClick={() => toggle(o.value)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 14px",
                  cursor: "pointer",
                  fontSize: 12,
                  background: sel ? "rgba(201,168,76,0.06)" : "transparent",
                  fontFamily: GEO,
                }}
              >
                <input
                  type="checkbox"
                  readOnly
                  checked={sel}
                  style={{ accentColor: GOLD }}
                />
                <span
                  style={{
                    fontWeight: sel ? 600 : 400,
                    color: sel && col ? col : th.txt,
                  }}
                >
                  {o.label}
                </span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Gauge({ paid, total, dark }) {
  const ref = useRef(null);
  const pct = total > 0 ? Math.min(paid / total, 1) : 0;
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d"),
      cx = c.width / 2,
      cy = c.height / 2,
      r = cx * 0.76,
      lw = cx * 0.16;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.beginPath();
    ctx.arc(cx, cy, r, -Math.PI * 0.75, Math.PI * 0.75);
    ctx.strokeStyle = dark ? "rgba(201,168,76,0.15)" : "rgba(154,122,46,0.12)";
    ctx.lineWidth = lw;
    ctx.lineCap = "round";
    ctx.stroke();
    if (pct > 0) {
      ctx.beginPath();
      ctx.arc(
        cx,
        cy,
        r,
        -Math.PI * 0.75,
        -Math.PI * 0.75 + Math.PI * 1.5 * pct,
      );
      const g = ctx.createLinearGradient(0, 0, c.width, 0);
      g.addColorStop(0, GOLD_D);
      g.addColorStop(1, GOLD_L);
      ctx.strokeStyle = g;
      ctx.lineWidth = lw;
      ctx.lineCap = "round";
      ctx.stroke();
    }
    ctx.fillStyle = dark ? "#F0E6C8" : "#1A1209";
    ctx.font = `bold ${Math.round(cx * 0.28)}px ${GEO}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(Math.round(pct * 100) + "%", cx, cy);
  }, [paid, total, dark]);
  return (
    <canvas ref={ref} width={120} height={120} style={{ display: "block" }} />
  );
}

// ── CatGrid ───────────────────────────────────────────────────
function CatGrid({ cats, val, onChange, th }) {
  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 5 }}
    >
      {cats.map((c) => (
        <button
          key={c.id}
          onClick={() => onChange(c.id)}
          style={{
            padding: "8px 4px",
            borderRadius: 6,
            cursor: "pointer",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
            border:
              val === c.id ? `1.5px solid ${GOLD}` : `1px solid ${th.bdr}`,
            background: val === c.id ? "rgba(201,168,76,0.1)" : th.s2,
          }}
        >
          <span style={{ fontSize: 14 }}>{c.icon}</span>
          <span
            style={{
              fontSize: 9,
              textAlign: "center",
              color: th.txt,
              fontWeight: val === c.id ? 700 : 400,
              fontFamily: GEO,
            }}
          >
            {c.label}
          </span>
        </button>
      ))}
    </div>
  );
}

// ── TxFormModal ───────────────────────────────────────────────
function TxFormModal({
  form,
  setForm,
  editId,
  formErr,
  onSave,
  onClose,
  dark,
  expCats,
  incCats,
  appUsers,
  uc,
  th,
  isMobile,
}) {
  const f = form;
  const sI = {
    width: "100%",
    padding: "9px 11px",
    borderRadius: 6,
    border: `1px solid ${th.ib}`,
    fontSize: 13,
    boxSizing: "border-box",
    background: th.inp,
    color: th.txt,
    outline: "none",
    fontFamily: GEO,
  };
  const sL = {
    fontSize: 10,
    color: th.t2,
    display: "block",
    marginBottom: 5,
    fontFamily: GEO,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  };
  const tabBtn = (v, l, col) => (
    <button
      onClick={() =>
        setForm((x) => ({
          ...x,
          type: v,
          category: v === "income" ? incCats[0]?.id : expCats[0]?.id,
        }))
      }
      style={{
        flex: 1,
        padding: "9px",
        borderRadius: 7,
        border: `1.5px solid ${f.type === v ? GOLD : th.bdr}`,
        cursor: "pointer",
        fontWeight: f.type === v ? 700 : 400,
        fontSize: 13,
        background: f.type === v ? "rgba(201,168,76,0.1)" : "transparent",
        color: f.type === v ? th.acc : th.t2,
        fontFamily: GEO,
      }}
    >
      {l}
    </button>
  );
  return (
    <Modal
      title={editId ? "Editar Lançamento" : "Novo Lançamento"}
      onClose={onClose}
      dark={dark}
    >
      <Err msg={formErr} />
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {tabBtn("expense", "Saída")}
        {tabBtn("income", "Entrada")}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <div>
          <label style={sL}>Descrição *</label>
          <input
            value={f.desc}
            onChange={(e) => setForm((x) => ({ ...x, desc: e.target.value }))}
            style={sI}
          />
        </div>
        <div>
          <label style={sL}>Valor (R$) *</label>
          <input
            type="number"
            value={f.amount}
            onChange={(e) => setForm((x) => ({ ...x, amount: e.target.value }))}
            style={sI}
          />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={sL}>Categoria *</label>
        <CatGrid
          cats={f.type === "income" ? incCats : expCats}
          val={f.category}
          onChange={(v) => setForm((x) => ({ ...x, category: v }))}
          th={th}
        />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <div>
          <label style={sL}>Responsável</label>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {appUsers.map((u) => (
              <button
                key={u}
                onClick={() => setForm((x) => ({ ...x, user: u }))}
                style={{
                  padding: "5px 10px",
                  borderRadius: 5,
                  border: `1px solid ${f.user === u ? GOLD : th.ib}`,
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: f.user === u ? 700 : 400,
                  background:
                    f.user === u ? "rgba(201,168,76,0.1)" : "transparent",
                  color: f.user === u ? th.acc : th.t2,
                  fontFamily: GEO,
                }}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
        <div>
          <label style={sL}>Data</label>
          <input
            type="date"
            value={f.date}
            onChange={(e) => setForm((x) => ({ ...x, date: e.target.value }))}
            style={sI}
          />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={sL}>Observação</label>
        <textarea
          value={f.obs || ""}
          onChange={(e) => setForm((x) => ({ ...x, obs: e.target.value }))}
          rows={2}
          style={{ ...sI, resize: "vertical" }}
        />
      </div>
      <div
        style={{
          background: th.s2,
          borderRadius: 8,
          padding: "12px 14px",
          marginBottom: 16,
          border: `1px solid ${th.bdr}`,
        }}
      >
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "pointer",
            marginBottom: f.paid ? 10 : 0,
          }}
        >
          <input
            type="checkbox"
            checked={f.paid}
            onChange={(e) => setForm((x) => ({ ...x, paid: e.target.checked }))}
            style={{ width: 16, height: 16, accentColor: GOLD }}
          />
          <span style={{ fontSize: 13, color: th.txt, fontFamily: GEO }}>
            {f.type === "income" ? "Já recebido" : "Já pago"}
          </span>
        </label>
        {f.paid && f.type === "expense" && (
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            {["Débito", "Crédito"].map((m) => (
              <button
                key={m}
                onClick={() => setForm((x) => ({ ...x, payMethod: m }))}
                style={{
                  flex: 1,
                  padding: "7px",
                  borderRadius: 6,
                  border: `1px solid ${f.payMethod === m ? GOLD : th.ib}`,
                  cursor: "pointer",
                  fontWeight: f.payMethod === m ? 600 : 400,
                  background:
                    f.payMethod === m ? "rgba(201,168,76,0.1)" : "transparent",
                  color: f.payMethod === m ? th.acc : th.t2,
                  fontSize: 12,
                  fontFamily: GEO,
                }}
              >
                {m}
              </button>
            ))}
          </div>
        )}
        {f.paid && (
          <div>
            <label style={sL}>Data do pagamento</label>
            <input
              type="date"
              value={f.payDate || f.date}
              onChange={(e) =>
                setForm((x) => ({ ...x, payDate: e.target.value }))
              }
              style={{ ...sI, padding: "7px 10px" }}
            />
          </div>
        )}
      </div>
      <Btn onClick={onSave} style={{ width: "100%", padding: "12px" }}>
        {editId ? "Salvar Alterações" : "Adicionar Lançamento"}
      </Btn>
    </Modal>
  );
}

// ── RecFormModal ──────────────────────────────────────────────
function RecFormModal({
  recForm,
  setRecForm,
  editRecId,
  recErr,
  onSave,
  onClose,
  dark,
  expCats,
  appUsers,
  uc,
  th,
}) {
  const rf = recForm;
  const sI = {
    width: "100%",
    padding: "9px 11px",
    borderRadius: 6,
    border: `1px solid ${th.ib}`,
    fontSize: 13,
    boxSizing: "border-box",
    background: th.inp,
    color: th.txt,
    outline: "none",
    fontFamily: GEO,
  };
  const sL = {
    fontSize: 10,
    color: th.t2,
    display: "block",
    marginBottom: 5,
    fontFamily: GEO,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  };
  return (
    <Modal
      title={editRecId ? "Editar Conta Fixa" : "Nova Conta Fixa"}
      onClose={onClose}
      dark={dark}
    >
      <Err msg={recErr} />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <div>
          <label style={sL}>Descrição *</label>
          <input
            value={rf.desc}
            onChange={(e) =>
              setRecForm((x) => ({ ...x, desc: e.target.value }))
            }
            style={sI}
          />
        </div>
        <div>
          <label style={sL}>Valor (R$) *</label>
          <input
            type="number"
            value={rf.amount}
            onChange={(e) =>
              setRecForm((x) => ({ ...x, amount: e.target.value }))
            }
            style={sI}
          />
        </div>
      </div>
      <div style={{ marginBottom: 12 }}>
        <label style={sL}>Categoria</label>
        <CatGrid
          cats={expCats}
          val={rf.category}
          onChange={(v) => setRecForm((x) => ({ ...x, category: v }))}
          th={th}
        />
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginBottom: 16,
        }}
      >
        <div>
          <label style={sL}>Responsável</label>
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
            {appUsers.map((u) => (
              <button
                key={u}
                onClick={() => setRecForm((x) => ({ ...x, user: u }))}
                style={{
                  padding: "5px 10px",
                  borderRadius: 5,
                  border: `1px solid ${rf.user === u ? GOLD : th.ib}`,
                  cursor: "pointer",
                  fontSize: 11,
                  fontWeight: rf.user === u ? 700 : 400,
                  background:
                    rf.user === u ? "rgba(201,168,76,0.1)" : "transparent",
                  color: rf.user === u ? th.acc : th.t2,
                  fontFamily: GEO,
                }}
              >
                {u}
              </button>
            ))}
          </div>
        </div>
        {!editRecId && (
          <div>
            <label style={sL}>Início</label>
            <input
              type="month"
              value={rf.startYM}
              onChange={(e) =>
                setRecForm((x) => ({ ...x, startYM: e.target.value }))
              }
              style={sI}
            />
          </div>
        )}
      </div>
      <div style={{ marginBottom: 16 }}>
        <label style={sL}>Observação</label>
        <textarea
          value={rf.obs || ""}
          onChange={(e) => setRecForm((x) => ({ ...x, obs: e.target.value }))}
          rows={2}
          style={{ ...sI, resize: "vertical" }}
        />
      </div>
      <Btn onClick={onSave} style={{ width: "100%", padding: "12px" }}>
        {editRecId ? "Atualizar" : "Criar Conta Fixa"}
      </Btn>
    </Modal>
  );
}

// ── EmpForm ───────────────────────────────────────────────────
function EmpForm({ onSave, onClose, dark, appUsers, uc, empErr, setEmpErr }) {
  const th = TH(dark);
  const sI = {
    width: "100%",
    padding: "9px 11px",
    borderRadius: 6,
    border: `1px solid ${th.ib}`,
    fontSize: 13,
    boxSizing: "border-box",
    background: th.inp,
    color: th.txt,
    outline: "none",
    fontFamily: GEO,
  };
  const sL = {
    fontSize: 10,
    color: th.t2,
    display: "block",
    marginBottom: 5,
    fontFamily: GEO,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  };
  const [dir, setDir] = useState("borrow");
  const [desc, setDesc] = useState("");
  const [total, setTotal] = useState("");
  const [parc, setParc] = useState("");
  const [pType, setPType] = useState("parceled");
  const [juros, setJuros] = useState("");
  const [tj, setTj] = useState(false);
  const [eDate, setEDate] = useState(ymd(now));
  const [sDate, setSDate] = useState(ymd(now));
  const [usr, setUsr] = useState(appUsers[0] || "");
  const [pe, setPe] = useState([]);
  const [step, setStep] = useState(1);
  const tN = parseFloat(total) || 0,
    jN = parseFloat(juros) || 0,
    nP = parseInt(parc) || 0;
  function calc() {
    if (pType === "single")
      return [{ idx: 0, value: tN, date: sDate, paid: false }];
    if (!tN || !nP) return [];
    const b = tN / nP;
    return Array.from({ length: nP }, (_, i) => {
      const d = new Date(sDate + "T12:00");
      d.setMonth(d.getMonth() + i);
      return {
        idx: i,
        value: tj ? +(b * (1 + jN / 100)).toFixed(2) : +b.toFixed(2),
        date: ymd(d),
        paid: false,
      };
    });
  }
  function go() {
    const e = vld([
      { val: desc, label: "Descrição" },
      { val: total, label: "Valor total" },
      ...(pType === "parceled" ? [{ val: parc, label: "Parcelas" }] : []),
    ]);
    if (e) {
      setEmpErr(e);
      return;
    }
    setEmpErr("");
    setPe(calc());
    setStep(2);
  }
  function save() {
    onSave({
      id: gid(),
      desc,
      direction: dir,
      totalOriginal: tN,
      totalParcelas: +pe.reduce((a, p) => a + p.value, 0).toFixed(2),
      parcelas: pType === "single" ? 1 : nP,
      parcType: pType,
      juros: tj ? jN : 0,
      entryDate: eDate,
      startDate: sDate,
      user: usr,
      items: pe,
      createdAt: ymd(now),
    });
  }
  const tabBtn = (d, l) => (
    <button
      onClick={() => setDir(d)}
      style={{
        flex: 1,
        padding: "9px",
        borderRadius: 7,
        border: `1.5px solid ${dir === d ? GOLD : th.bdr}`,
        cursor: "pointer",
        fontWeight: dir === d ? 700 : 400,
        fontSize: 13,
        background: dir === d ? "rgba(201,168,76,0.1)" : "transparent",
        color: dir === d ? th.acc : th.t2,
        fontFamily: GEO,
      }}
    >
      {l}
    </button>
  );
  const typeBtn = (t, l) => (
    <button
      onClick={() => setPType(t)}
      style={{
        flex: 1,
        padding: "7px",
        borderRadius: 6,
        border: `1px solid ${pType === t ? GOLD : th.ib}`,
        cursor: "pointer",
        fontWeight: pType === t ? 600 : 400,
        background: pType === t ? "rgba(201,168,76,0.1)" : "transparent",
        color: pType === t ? th.acc : th.t2,
        fontSize: 12,
        fontFamily: GEO,
      }}
    >
      {l}
    </button>
  );
  return (
    <Modal title="Novo Empréstimo" onClose={onClose} dark={dark}>
      <Err msg={empErr} />
      {step === 1 ? (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {tabBtn("borrow", "Pegar emprestado")}
            {tabBtn("lend", "Emprestar")}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div style={{ gridColumn: "span 2" }}>
              <label style={sL}>Descrição *</label>
              <input
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                style={sI}
              />
            </div>
            <div>
              <label style={sL}>Valor total *</label>
              <input
                type="number"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                style={sI}
              />
            </div>
            <div>
              <label style={sL}>Data</label>
              <input
                type="date"
                value={eDate}
                onChange={(e) => setEDate(e.target.value)}
                style={sI}
              />
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={sL}>Responsável</label>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {appUsers.map((u) => (
                  <button
                    key={u}
                    onClick={() => setUsr(u)}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 5,
                      border: `1px solid ${usr === u ? GOLD : th.ib}`,
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: usr === u ? 700 : 400,
                      background:
                        usr === u ? "rgba(201,168,76,0.1)" : "transparent",
                      color: usr === u ? th.acc : th.t2,
                      fontFamily: GEO,
                    }}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div
            style={{
              background: th.s2,
              borderRadius: 8,
              padding: "12px 14px",
              marginBottom: 14,
              border: `1px solid ${th.bdr}`,
            }}
          >
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              {typeBtn("parceled", "Parcelado")}
              {typeBtn("single", "À vista")}
            </div>
            {pType === "parceled" && (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 8,
                    marginBottom: 8,
                  }}
                >
                  <div>
                    <label style={sL}>Parcelas *</label>
                    <input
                      type="number"
                      value={parc}
                      onChange={(e) => setParc(e.target.value)}
                      style={sI}
                    />
                  </div>
                  <div>
                    <label style={sL}>Início</label>
                    <input
                      type="date"
                      value={sDate}
                      onChange={(e) => setSDate(e.target.value)}
                      style={sI}
                    />
                  </div>
                </div>
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={tj}
                    onChange={(e) => setTj(e.target.checked)}
                    style={{ accentColor: GOLD, width: 16, height: 16 }}
                  />
                  <span
                    style={{ fontSize: 13, color: th.txt, fontFamily: GEO }}
                  >
                    Tem juros?
                  </span>
                </label>
                {tj && (
                  <>
                    <label style={{ ...sL, marginTop: 8 }}>Taxa (%)</label>
                    <input
                      type="number"
                      value={juros}
                      onChange={(e) => setJuros(e.target.value)}
                      style={sI}
                    />
                  </>
                )}
              </>
            )}
            {pType === "single" && (
              <>
                <label style={sL}>Data</label>
                <input
                  type="date"
                  value={sDate}
                  onChange={(e) => setSDate(e.target.value)}
                  style={sI}
                />
              </>
            )}
          </div>
          <Btn onClick={go} style={{ width: "100%", padding: "12px" }}>
            Revisar →
          </Btn>
        </>
      ) : (
        <>
          <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 14 }}>
            {pe.map((p, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: th.t2,
                    minWidth: 20,
                    fontFamily: GEO,
                  }}
                >
                  {i + 1}.
                </span>
                <span style={{ fontSize: 12, color: th.t2, minWidth: 75 }}>
                  {new Date(p.date + "T12:00").toLocaleDateString("pt-BR", {
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <input
                  type="number"
                  value={p.value}
                  onChange={(e) =>
                    setPe((prev) =>
                      prev.map((x, j) =>
                        j === i
                          ? { ...x, value: parseFloat(e.target.value) || 0 }
                          : x,
                      ),
                    )
                  }
                  style={{ ...sI, flex: 1, padding: "6px 8px" }}
                />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn
              onClick={() => setStep(1)}
              variant="ghost"
              style={{ flex: 1, padding: "12px" }}
            >
              ← Voltar
            </Btn>
            <Btn onClick={save} style={{ flex: 2, padding: "12px" }}>
              Confirmar
            </Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── FinForm ───────────────────────────────────────────────────
function FinForm({
  onSave,
  onClose,
  dark,
  appUsers,
  uc,
  expCats,
  finErr,
  setFinErr,
}) {
  const th = TH(dark);
  const sI = {
    width: "100%",
    padding: "9px 11px",
    borderRadius: 6,
    border: `1px solid ${th.ib}`,
    fontSize: 13,
    boxSizing: "border-box",
    background: th.inp,
    color: th.txt,
    outline: "none",
    fontFamily: GEO,
  };
  const sL = {
    fontSize: 10,
    color: th.t2,
    display: "block",
    marginBottom: 5,
    fontFamily: GEO,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  };
  const [desc, setDesc] = useState("");
  const [total, setTotal] = useState("");
  const [parc, setParc] = useState("");
  const [juros, setJuros] = useState("");
  const [tj, setTj] = useState(false);
  const [sDate, setSDate] = useState(ymd(now));
  const [cat, setCat] = useState(expCats[0]?.id || "moradia");
  const [usr, setUsr] = useState(appUsers[0] || "");
  const [pe, setPe] = useState([]);
  const [step, setStep] = useState(1);
  const tN = parseFloat(total) || 0,
    jN = parseFloat(juros) || 0,
    nP = parseInt(parc) || 0;
  function calc() {
    if (!tN || !nP) return [];
    const b = tN / nP;
    return Array.from({ length: nP }, (_, i) => {
      const d = new Date(sDate + "T12:00");
      d.setMonth(d.getMonth() + i);
      return {
        idx: i,
        value: tj ? +(b * (1 + jN / 100)).toFixed(2) : +b.toFixed(2),
        date: ymd(d),
        paid: false,
      };
    });
  }
  function go() {
    const e = vld([
      { val: desc, label: "Descrição" },
      { val: total, label: "Valor total" },
      { val: parc, label: "Parcelas" },
    ]);
    if (e) {
      setFinErr(e);
      return;
    }
    setFinErr("");
    setPe(calc());
    setStep(2);
  }
  function save() {
    onSave({
      id: gid(),
      desc,
      totalOriginal: tN,
      totalParcelas: +pe.reduce((a, p) => a + p.value, 0).toFixed(2),
      parcelas: nP,
      juros: tj ? jN : 0,
      startDate: sDate,
      category: cat,
      user: usr,
      items: pe,
      createdAt: ymd(now),
    });
  }
  return (
    <Modal title="Novo Financiamento" onClose={onClose} dark={dark}>
      <Err msg={finErr} />
      {step === 1 ? (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div style={{ gridColumn: "span 2" }}>
              <label style={sL}>Descrição *</label>
              <input
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                style={sI}
              />
            </div>
            <div>
              <label style={sL}>Valor total *</label>
              <input
                type="number"
                value={total}
                onChange={(e) => setTotal(e.target.value)}
                style={sI}
              />
            </div>
            <div>
              <label style={sL}>Parcelas *</label>
              <input
                type="number"
                value={parc}
                onChange={(e) => setParc(e.target.value)}
                style={sI}
              />
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={sL}>1ª Parcela</label>
              <input
                type="date"
                value={sDate}
                onChange={(e) => setSDate(e.target.value)}
                style={sI}
              />
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={sL}>Responsável</label>
              <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                {appUsers.map((u) => (
                  <button
                    key={u}
                    onClick={() => setUsr(u)}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 5,
                      border: `1px solid ${usr === u ? GOLD : th.ib}`,
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: usr === u ? 700 : 400,
                      background:
                        usr === u ? "rgba(201,168,76,0.1)" : "transparent",
                      color: usr === u ? th.acc : th.t2,
                      fontFamily: GEO,
                    }}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={sL}>Categoria das parcelas</label>
            <CatGrid cats={expCats} val={cat} onChange={setCat} th={th} />
          </div>
          <div
            style={{
              background: th.s2,
              borderRadius: 8,
              padding: "12px 14px",
              marginBottom: 14,
              border: `1px solid ${th.bdr}`,
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={tj}
                onChange={(e) => setTj(e.target.checked)}
                style={{ accentColor: GOLD, width: 16, height: 16 }}
              />
              <span style={{ fontSize: 13, color: th.txt, fontFamily: GEO }}>
                Tem juros?
              </span>
            </label>
            {tj && (
              <>
                <label style={{ ...sL, marginTop: 8 }}>Taxa/parcela (%)</label>
                <input
                  type="number"
                  value={juros}
                  onChange={(e) => setJuros(e.target.value)}
                  style={sI}
                />
              </>
            )}
          </div>
          <Btn onClick={go} style={{ width: "100%", padding: "12px" }}>
            Revisar parcelas →
          </Btn>
        </>
      ) : (
        <>
          <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 14 }}>
            {pe.map((p, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: th.t2,
                    minWidth: 20,
                    fontFamily: GEO,
                  }}
                >
                  {i + 1}.
                </span>
                <span style={{ fontSize: 12, color: th.t2, minWidth: 75 }}>
                  {new Date(p.date + "T12:00").toLocaleDateString("pt-BR", {
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <input
                  type="number"
                  value={p.value}
                  onChange={(e) =>
                    setPe((prev) =>
                      prev.map((x, j) =>
                        j === i
                          ? { ...x, value: parseFloat(e.target.value) || 0 }
                          : x,
                      ),
                    )
                  }
                  style={{ ...sI, flex: 1, padding: "6px 8px" }}
                />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn
              onClick={() => setStep(1)}
              variant="ghost"
              style={{ flex: 1, padding: "12px" }}
            >
              ← Voltar
            </Btn>
            <Btn onClick={save} style={{ flex: 2, padding: "12px" }}>
              Confirmar
            </Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── CcInstModal ───────────────────────────────────────────────
function CcInstModal({ src, dark, onConfirm, onClose }) {
  const th = TH(dark);
  const sI = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 6,
    border: `1px solid ${th.ib}`,
    fontSize: 13,
    boxSizing: "border-box",
    background: th.inp,
    color: th.txt,
    outline: "none",
    fontFamily: GEO,
  };
  const oYM = src.date.slice(0, 7),
    bYM = addM(oYM, 1);
  const [pType, setPType] = useState("single");
  const [nP, setNP] = useState("");
  const [juros, setJuros] = useState("");
  const [tj, setTj] = useState(false);
  const [parcs, setParcs] = useState([]);
  const [step, setStep] = useState(1);
  const [err, setErr] = useState("");
  const tN = src.amount || 0,
    jN = parseFloat(juros) || 0,
    nPi = parseInt(nP) || 0;
  function calc() {
    if (pType === "single")
      return [{ idx: 0, value: tN, bym: bYM, paid: false }];
    if (!tN || !nPi) return [];
    const b = tN / nPi;
    return Array.from({ length: nPi }, (_, i) => ({
      idx: i,
      value: tj ? +(b * (1 + jN / 100)).toFixed(2) : +b.toFixed(2),
      bym: addM(bYM, i),
      paid: false,
    }));
  }
  function goRev() {
    if (pType === "parceled" && !nP) {
      setErr("Preencha o nº de parcelas");
      return;
    }
    setErr("");
    setParcs(calc());
    setStep(2);
  }
  const tBtn = (t, l) => (
    <button
      onClick={() => setPType(t)}
      style={{
        flex: 1,
        padding: "9px",
        borderRadius: 6,
        border: `1px solid ${pType === t ? GOLD : th.ib}`,
        cursor: "pointer",
        fontWeight: pType === t ? 600 : 400,
        background: pType === t ? "rgba(201,168,76,0.1)" : "transparent",
        color: pType === t ? th.acc : th.t2,
        fontSize: 13,
        fontFamily: GEO,
      }}
    >
      {l}
    </button>
  );
  return (
    <Modal
      title="Pagamento via Crédito"
      onClose={onClose}
      dark={dark}
      maxW={460}
    >
      <Err msg={err} />
      {step === 1 ? (
        <>
          <div
            style={{
              background: "rgba(201,168,76,0.08)",
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 14,
              fontSize: 13,
              color: th.acc,
              fontFamily: GEO,
              border: `1px solid ${th.bdr}`,
            }}
          >
            Compra de <strong>{fmt(tN)}</strong> em <strong>{oYM}</strong>.
            Fatura em <strong>{bYM}</strong>.
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
            {tBtn("single", "À vista")}
            {tBtn("parceled", "Parcelado")}
          </div>
          {pType === "parceled" && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
                marginBottom: 12,
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: 10,
                    color: th.t2,
                    display: "block",
                    marginBottom: 4,
                    fontFamily: GEO,
                    textTransform: "uppercase",
                    letterSpacing: "0.6px",
                  }}
                >
                  Nº de parcelas
                </label>
                <input
                  type="number"
                  value={nP}
                  onChange={(e) => setNP(e.target.value)}
                  placeholder="12"
                  style={sI}
                />
              </div>
              <div style={{ display: "flex", alignItems: "flex-end" }}>
                <div
                  style={{
                    background: th.s2,
                    borderRadius: 6,
                    padding: "8px 10px",
                    fontSize: 12,
                    color: th.t2,
                    width: "100%",
                    fontFamily: GEO,
                  }}
                >
                  Início: <strong style={{ color: th.acc }}>{bYM}</strong>
                </div>
              </div>
            </div>
          )}
          {pType === "parceled" && (
            <div
              style={{
                background: th.s2,
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 14,
                border: `1px solid ${th.bdr}`,
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={tj}
                  onChange={(e) => setTj(e.target.checked)}
                  style={{ accentColor: GOLD, width: 16, height: 16 }}
                />
                <span style={{ fontSize: 13, color: th.txt, fontFamily: GEO }}>
                  Tem juros?
                </span>
              </label>
              {tj && (
                <>
                  <label
                    style={{
                      fontSize: 10,
                      color: th.t2,
                      display: "block",
                      marginBottom: 4,
                      marginTop: 8,
                      fontFamily: GEO,
                      textTransform: "uppercase",
                    }}
                  >
                    Taxa/parcela (%)
                  </label>
                  <input
                    type="number"
                    value={juros}
                    onChange={(e) => setJuros(e.target.value)}
                    placeholder="2.5"
                    style={sI}
                  />
                </>
              )}
            </div>
          )}
          <Btn onClick={goRev} style={{ width: "100%", padding: "12px" }}>
            Revisar →
          </Btn>
        </>
      ) : (
        <>
          <div style={{ maxHeight: 260, overflowY: "auto", marginBottom: 14 }}>
            {parcs.map((p, i) => (
              <div
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: th.t2,
                    minWidth: 20,
                    fontFamily: GEO,
                  }}
                >
                  {i + 1}.
                </span>
                <span style={{ fontSize: 12, color: th.t2, minWidth: 60 }}>
                  {p.bym}
                </span>
                <input
                  type="number"
                  value={p.value}
                  onChange={(e) =>
                    setParcs((prev) =>
                      prev.map((x, j) =>
                        j === i
                          ? { ...x, value: parseFloat(e.target.value) || 0 }
                          : x,
                      ),
                    )
                  }
                  style={{ ...sI, flex: 1, padding: "6px 8px" }}
                />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn
              onClick={() => setStep(1)}
              variant="ghost"
              style={{ flex: 1, padding: "12px" }}
            >
              ← Voltar
            </Btn>
            <Btn
              onClick={() => onConfirm({ parcelas: parcs, originYM: oYM })}
              style={{ flex: 2, padding: "12px" }}
            >
              Confirmar
            </Btn>
          </div>
        </>
      )}
    </Modal>
  );
}

// ── BulkPayModal ──────────────────────────────────────────────
function BulkPayModal({ items, onConfirm, onClose, dark }) {
  const th = TH(dark);
  const sI = {
    width: "100%",
    padding: "8px 10px",
    borderRadius: 6,
    border: `1px solid ${th.ib}`,
    fontSize: 13,
    boxSizing: "border-box",
    background: th.inp,
    color: th.txt,
    outline: "none",
    fontFamily: GEO,
  };
  const [gM, setGM] = useState("Débito");
  const [gSM, setGSM] = useState(true);
  const [gPD, setGPD] = useState(ymd(now));
  const tBtn = (m, l) => (
    <button
      onClick={() => setGM(m)}
      style={{
        flex: 1,
        padding: "8px",
        borderRadius: 6,
        border: `1px solid ${gM === m ? GOLD : th.ib}`,
        cursor: "pointer",
        fontWeight: gM === m ? 600 : 400,
        background: gM === m ? "rgba(201,168,76,0.1)" : "transparent",
        color: gM === m ? th.acc : th.t2,
        fontSize: 12,
        fontFamily: GEO,
      }}
    >
      {l}
    </button>
  );
  return (
    <Modal
      title={`Pagar ${items.length} lançamentos`}
      onClose={onClose}
      dark={dark}
      maxW={460}
    >
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        {tBtn("Débito", "Débito")}
        {tBtn("Crédito", "Crédito")}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: gSM ? 16 : 8 }}>
        <button
          onClick={() => setGSM(true)}
          style={{
            flex: 1,
            padding: "7px",
            borderRadius: 6,
            border: `1px solid ${gSM ? GOLD : th.ib}`,
            cursor: "pointer",
            fontWeight: gSM ? 600 : 400,
            background: gSM ? "rgba(201,168,76,0.1)" : "transparent",
            color: gSM ? th.acc : th.t2,
            fontSize: 12,
            fontFamily: GEO,
          }}
        >
          No mês
        </button>
        <button
          onClick={() => setGSM(false)}
          style={{
            flex: 1,
            padding: "7px",
            borderRadius: 6,
            border: `1px solid ${!gSM ? GOLD : th.ib}`,
            cursor: "pointer",
            fontWeight: !gSM ? 600 : 400,
            background: !gSM ? "rgba(201,168,76,0.1)" : "transparent",
            color: !gSM ? th.acc : th.t2,
            fontSize: 12,
            fontFamily: GEO,
          }}
        >
          Outra data
        </button>
      </div>
      {!gSM && (
        <input
          type="date"
          value={gPD}
          onChange={(e) => setGPD(e.target.value)}
          style={{ ...sI, marginBottom: 16 }}
        />
      )}
      <Btn
        onClick={() =>
          onConfirm(
            items.map((r) => ({
              ...r,
              payMethod: gM,
              payDate: gSM ? r.date : gPD,
              payTiming: gSM
                ? "ontime"
                : gPD.slice(0, 7) > r.date.slice(0, 7)
                  ? "late"
                  : "early",
              payAmount: r.amount,
            })),
          )
        }
        style={{ width: "100%", padding: "12px" }}
      >
        Confirmar
      </Btn>
    </Modal>
  );
}

// ── SettingsView ──────────────────────────────────────────────
function SettingsView({
  expCats,
  setExpCats,
  incCats,
  setIncCats,
  appUsers,
  setAppUsers,
  uc,
  setUc,
  ub,
  setUb,
  pgs,
  setPgs,
  pgColors,
  setPgColors,
  db,
  adminDb,
  userId,
  accountId,
  toast,
  sc,
  sI,
  sL,
  th,
}) {
  const DEF_PG_KEYS = [
    "home",
    "transactions",
    "credit",
    "overdue",
    "fixed",
    "dashboard",
    "settings",
  ];
  const DEF_PG_LABELS = {
    home: "Início",
    transactions: "Lançamentos",
    credit: "Fatura",
    overdue: "Em Atraso",
    fixed: "Fixo",
    dashboard: "Relatório",
    settings: "Config",
  };
  const [tab, setTab] = useState("cats");
  const [nCL, setNCL] = useState("");
  const [nCI, setNCI] = useState("📦");
  const [nCC, setNCC] = useState(GOLD);
  const [nCT, setNCT] = useState("exp");
  const [nU, setNU] = useState("");
  const [nUC, setNUC] = useState(GOLD);
  const [nUB, setNUB] = useState("#F5EDD5");
  const [saving, setSaving] = useState(false);
  async function saveGlobal() {
    if (!db) {
      toast("Sem conexão com Firebase.", "warn");
      return;
    }
    setSaving(true);
    try {
      await fsSave(db, "config", "main", {
        expCats,
        incCats,
        users: appUsers,
        userColors: uc,
        userBg: ub,
        pageCfg: pgs,
      });
      toast("Configurações salvas.");
    } catch (e) {
      toast("Erro: " + e.message, "warn");
    }
    setSaving(false);
  }
  async function saveColors(nc) {
    setPgColors(nc);
    if (!adminDb) return;
    try {
      await fsSave(adminDb, "userPrefs", `${userId}__${accountId}`, {
        userId,
        accountId,
        pgColors: nc,
        updatedAt: ymd(now),
      });
    } catch (e) {
      console.warn(e);
    }
  }
  const tabBtn = (id, l) => (
    <button
      onClick={() => setTab(id)}
      style={{
        padding: "7px 14px",
        borderRadius: 6,
        border: "none",
        cursor: "pointer",
        fontWeight: tab === id ? 700 : 400,
        background: tab === id ? GOLD : "transparent",
        color: tab === id ? "#0D1A00" : th.t2,
        fontSize: 12,
        fontFamily: GEO,
      }}
    >
      {l}
    </button>
  );
  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 3,
          marginBottom: 20,
          background: th.s2,
          borderRadius: 8,
          padding: 4,
          border: `1px solid ${th.bdr}`,
        }}
      >
        {[
          ["cats", "Categorias"],
          ["people", "Pessoas"],
          ["pages", "Páginas"],
        ].map(([id, l]) => tabBtn(id, l))}
      </div>
      {tab === "cats" && (
        <div>
          <div
            style={{ ...sc, marginBottom: 12, borderLeft: `3px solid ${GOLD}` }}
          >
            <div style={{ fontSize: 12, color: th.t2, fontFamily: GEO }}>
              Categorias são compartilhadas entre todos os usuários desta conta.
            </div>
          </div>
          <div style={{ ...sc, marginBottom: 12 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: th.txt,
                marginBottom: 12,
                fontFamily: GEO,
              }}
            >
              Nova categoria
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                marginBottom: 10,
              }}
            >
              <div>
                <label style={sL}>Nome</label>
                <input
                  value={nCL}
                  onChange={(e) => setNCL(e.target.value)}
                  style={sI}
                />
              </div>
              <div>
                <label style={sL}>Ícone</label>
                <input
                  value={nCI}
                  onChange={(e) => setNCI(e.target.value)}
                  style={sI}
                />
              </div>
              <div>
                <label style={sL}>Cor</label>
                <input
                  type="color"
                  value={nCC}
                  onChange={(e) => setNCC(e.target.value)}
                  style={{ ...sI, padding: "4px", height: 40 }}
                />
              </div>
              <div>
                <label style={sL}>Tipo</label>
                <select
                  value={nCT}
                  onChange={(e) => setNCT(e.target.value)}
                  style={sI}
                >
                  <option value="exp">Saída</option>
                  <option value="inc">Entrada</option>
                </select>
              </div>
            </div>
            <Btn
              onClick={() => {
                if (!nCL.trim()) return;
                const c = {
                  id: gid(),
                  label: nCL.trim(),
                  icon: nCI,
                  color: nCC,
                };
                if (nCT === "exp") setExpCats((p) => [...p, c]);
                else setIncCats((p) => [...p, c]);
                setNCL("");
                setNCI("📦");
              }}
            >
              Adicionar
            </Btn>
          </div>
          {[
            ["Saídas", expCats, "exp"],
            ["Entradas", incCats, "inc"],
          ].map(([t, cats, type]) => (
            <div key={type} style={{ ...sc, marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: th.acc,
                  marginBottom: 10,
                  fontFamily: GEO,
                }}
              >
                {t}
              </div>
              {cats.map((c) => (
                <div
                  key={c.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "7px 0",
                    borderBottom: `1px solid ${th.bdr}`,
                  }}
                >
                  <span>{c.icon}</span>
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      color: th.txt,
                      fontFamily: GEO,
                    }}
                  >
                    {c.label}
                  </span>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      background: c.color,
                      display: "inline-block",
                    }}
                  />
                  <button
                    onClick={() =>
                      type === "exp"
                        ? setExpCats((p) => p.filter((x) => x.id !== c.id))
                        : setIncCats((p) => p.filter((x) => x.id !== c.id))
                    }
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#8A3A3A",
                      fontSize: 13,
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ))}
          <Btn
            onClick={saveGlobal}
            disabled={saving}
            style={{ width: "100%", padding: "12px" }}
          >
            {saving ? "Salvando..." : "Salvar Categorias"}
          </Btn>
        </div>
      )}
      {tab === "people" && (
        <div>
          <div
            style={{ ...sc, marginBottom: 12, borderLeft: `3px solid ${GOLD}` }}
          >
            <div style={{ fontSize: 12, color: th.t2, fontFamily: GEO }}>
              Pessoas são compartilhadas entre todos os usuários desta conta.
            </div>
          </div>
          <div style={{ ...sc, marginBottom: 12 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: th.txt,
                marginBottom: 12,
                fontFamily: GEO,
              }}
            >
              Nova pessoa
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr auto auto",
                gap: 8,
                alignItems: "flex-end",
              }}
            >
              <div>
                <label style={sL}>Nome</label>
                <input
                  value={nU}
                  onChange={(e) => setNU(e.target.value)}
                  placeholder="Nome"
                  style={sI}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <label style={{ ...sL, display: "block", textAlign: "center" }}>
                  Texto
                </label>
                <input
                  type="color"
                  value={nUC}
                  onChange={(e) => setNUC(e.target.value)}
                  style={{
                    width: 40,
                    height: 40,
                    padding: 2,
                    border: `1px solid ${th.ib}`,
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <label style={{ ...sL, display: "block", textAlign: "center" }}>
                  Fundo
                </label>
                <input
                  type="color"
                  value={nUB}
                  onChange={(e) => setNUB(e.target.value)}
                  style={{
                    width: 40,
                    height: 40,
                    padding: 2,
                    border: `1px solid ${th.ib}`,
                    borderRadius: 6,
                    cursor: "pointer",
                  }}
                />
              </div>
            </div>
            <Btn
              onClick={() => {
                if (!nU.trim() || appUsers.includes(nU.trim())) return;
                const u = nU.trim();
                setAppUsers((p) => [...p, u]);
                setUc((p) => ({ ...p, [u]: nUC }));
                setUb((p) => ({ ...p, [u]: nUB }));
                setNU("");
              }}
              style={{ marginTop: 10 }}
            >
              Adicionar
            </Btn>
          </div>
          <div style={{ ...sc, marginBottom: 12 }}>
            {appUsers.map((u) => (
              <div
                key={u}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "9px 0",
                  borderBottom: `1px solid ${th.bdr}`,
                }}
              >
                <span
                  style={{
                    background: ub[u] || "rgba(201,168,76,0.1)",
                    color: uc[u] || th.acc,
                    borderRadius: 5,
                    padding: "3px 12px",
                    fontSize: 12,
                    fontWeight: 600,
                    flex: 1,
                    fontFamily: GEO,
                  }}
                >
                  {u}
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <label
                    style={{ fontSize: 10, color: th.t2, fontFamily: GEO }}
                  >
                    Texto
                  </label>
                  <input
                    type="color"
                    value={uc[u] || GOLD}
                    onChange={(e) =>
                      setUc((p) => ({ ...p, [u]: e.target.value }))
                    }
                    style={{
                      width: 28,
                      height: 24,
                      padding: 0,
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                  />
                  <label
                    style={{ fontSize: 10, color: th.t2, fontFamily: GEO }}
                  >
                    Fundo
                  </label>
                  <input
                    type="color"
                    value={ub[u] || "#F5EDD5"}
                    onChange={(e) =>
                      setUb((p) => ({ ...p, [u]: e.target.value }))
                    }
                    style={{
                      width: 28,
                      height: 24,
                      padding: 0,
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                  />
                </div>
                <button
                  onClick={() => {
                    setAppUsers((p) => p.filter((x) => x !== u));
                    setUc((p) => {
                      const n = { ...p };
                      delete n[u];
                      return n;
                    });
                    setUb((p) => {
                      const n = { ...p };
                      delete n[u];
                      return n;
                    });
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#8A3A3A",
                    fontSize: 13,
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
          <Btn
            onClick={saveGlobal}
            disabled={saving}
            style={{ width: "100%", padding: "12px" }}
          >
            {saving ? "Salvando..." : "Salvar Pessoas"}
          </Btn>
        </div>
      )}
      {tab === "pages" && (
        <div>
          <div
            style={{ ...sc, marginBottom: 12, borderLeft: `3px solid ${GOLD}` }}
          >
            <div style={{ fontSize: 12, color: th.t2, fontFamily: GEO }}>
              A <strong>cor de fundo</strong> é individual por usuário e fica
              salva no servidor.
            </div>
          </div>
          {DEF_PG_KEYS.map((k) => {
            const myColor = pgColors[k] || "";
            const lbl = pgs[k]?.label || DEF_PG_LABELS[k] || k;
            return (
              <div key={k} style={{ ...sc, marginBottom: 10 }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto auto auto",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <div>
                    <label style={{ ...sL, marginBottom: 3 }}>
                      {DEF_PG_LABELS[k]}
                    </label>
                    <input
                      value={lbl}
                      onChange={(e) =>
                        setPgs((p) => ({
                          ...p,
                          [k]: { ...p[k], label: e.target.value },
                        }))
                      }
                      style={sI}
                    />
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <label
                      style={{ ...sL, display: "block", textAlign: "center" }}
                    >
                      Minha cor
                    </label>
                    <input
                      type="color"
                      value={myColor || GOLD}
                      onChange={(e) => {
                        const nc = { ...pgColors, [k]: e.target.value };
                        saveColors(nc);
                      }}
                      style={{
                        width: 36,
                        height: 36,
                        padding: 2,
                        border: `1px solid ${th.ib}`,
                        borderRadius: 6,
                        cursor: "pointer",
                      }}
                    />
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <label
                      style={{ ...sL, display: "block", textAlign: "center" }}
                    >
                      Limpar
                    </label>
                    <button
                      onClick={() => {
                        const nc = { ...pgColors };
                        delete nc[k];
                        saveColors(nc);
                      }}
                      style={{
                        background: "none",
                        border: `1px solid ${th.bdr}`,
                        borderRadius: 6,
                        padding: "7px 8px",
                        cursor: "pointer",
                        fontSize: 11,
                        color: th.t2,
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <label
                      style={{ ...sL, display: "block", textAlign: "center" }}
                    >
                      Reset
                    </label>
                    <button
                      onClick={() => {
                        setPgs((p) => ({ ...p, [k]: {} }));
                        const nc = { ...pgColors };
                        delete nc[k];
                        saveColors(nc);
                      }}
                      style={{
                        background: "none",
                        border: `1px solid ${th.bdr}`,
                        borderRadius: 6,
                        padding: "7px 8px",
                        cursor: "pointer",
                        fontSize: 11,
                        color: th.t2,
                      }}
                    >
                      ↺
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          <Btn
            onClick={saveGlobal}
            disabled={saving}
            style={{ width: "100%", padding: "12px" }}
          >
            {saving ? "Salvando..." : "Salvar Nomes das Páginas"}
          </Btn>
        </div>
      )}
    </div>
  );
}

// ── AdminPanel ────────────────────────────────────────────────
function AdminPanel({
  dark,
  setDark,
  initAccs,
  initUsrs,
  onSave,
  onDelete,
  onLogout,
}) {
  const th = TH(dark);
  const [view, setView] = useState("home");
  const [accs, setAccs] = useState(initAccs || []);
  const [usrs, setUsrs] = useState(initUsrs || []);
  const [toastMsg, setToastMsg] = useState("");
  const [eAcc, setEAcc] = useState(null);
  const [accSteps, setAccSteps] = useState([]);
  const [cfgStatus, setCfgStatus] = useState(null);
  const [eUsr, setEUsr] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [diag, setDiag] = useState(null);
  const [aS, setAS] = useState("");
  const [uS, setUS] = useState("");
  const [aFSt, setAFSt] = useState("all");
  const [uFSt, setUFSt] = useState("all");
  const [repData, setRepData] = useState({});
  const [repLoading, setRepLoading] = useState(false);
  const [repPMode, setRepPMode] = useState("month");
  const [repMonth, setRepMonth] = useState(ym(now));
  const [repFrom, setRepFrom] = useState(
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`,
  );
  const [repTo, setRepTo] = useState(ymd(now));
  const [repView, setRepView] = useState("monthly");
  const [repAccFilter, setRepAccFilter] = useState("");
  const [repPage, setRepPage] = useState(0);
  const REP_PAGE_SIZE = 10;
  const sI = {
    width: "100%",
    padding: "9px 12px",
    borderRadius: 6,
    border: `1px solid ${th.ib}`,
    fontSize: 13,
    boxSizing: "border-box",
    background: th.inp,
    color: th.txt,
    outline: "none",
    fontFamily: GEO,
  };
  const sL = {
    fontSize: 10,
    color: th.t2,
    display: "block",
    marginBottom: 5,
    fontFamily: GEO,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  };
  function msg(m) {
    setToastMsg(m);
    setTimeout(() => setToastMsg(""), 3500);
  }
  useEffect(() => {
    setAccSteps([]);
    setCfgStatus(null);
  }, [eAcc?.id]);
  function validateCfg() {
    const raw =
      typeof eAcc.firebaseConfig === "string"
        ? eAcc.firebaseConfig
        : JSON.stringify(eAcc.firebaseConfig || "");
    try {
      const cfg = JSON.parse(raw);
      const missing = ["apiKey", "projectId", "authDomain", "appId"].filter(
        (k) => !cfg[k],
      );
      if (missing.length) {
        setCfgStatus({ err: `Campos faltando: ${missing.join(", ")}` });
        return;
      }
      setEAcc((p) => ({
        ...p,
        projectId: cfg.projectId,
        firebaseConfig: JSON.stringify(cfg, null, 2),
      }));
      setCfgStatus("ok");
      setAccSteps((p) => (p.includes(5) ? p : [...p, 5]));
    } catch {
      setCfgStatus({ err: "JSON inválido. Verifique o formato." });
    }
  }
  async function sv(c, d) {
    await onSave(c, d.id, d);
  }
  async function subAcc() {
    if (!eAcc.name.trim()) {
      msg("⚠ Nome obrigatório");
      return;
    }
    let a = { ...eAcc };
    if (a.firebaseConfig) {
      try {
        const c =
          typeof a.firebaseConfig === "string"
            ? JSON.parse(a.firebaseConfig)
            : a.firebaseConfig;
        if (c.projectId) a.projectId = c.projectId;
        a.firebaseConfig = JSON.stringify(c);
      } catch {
        msg("⚠ Firebase Config inválido");
        return;
      }
    }
    const nl = accs.find((x) => x.id === a.id)
      ? accs.map((x) => (x.id === a.id ? a : x))
      : [...accs, a];
    setAccs(nl);
    await sv("accounts", a);
    const ul = usrs.map((u) => {
      const had = (u.accountIds || []).includes(a.id),
        has = (a.userIds || []).includes(u.id);
      if (had === has) return u;
      const ids = has
        ? [...(u.accountIds || []).filter((i) => i !== a.id), a.id]
        : (u.accountIds || []).filter((i) => i !== a.id);
      return { ...u, accountIds: ids };
    });
    const ch = ul.filter(
      (u, i) => JSON.stringify(u) !== JSON.stringify(usrs[i]),
    );
    setUsrs(ul);
    await Promise.all(ch.map((u) => sv("users", u)));
    setEAcc(null);
    msg("✓ Conta salva");
  }
  async function subUsr() {
    const hasCpf = !!cleanCPF(eUsr.cpf);
    const hasEmail = !!(eUsr.email || "").trim();
    if (!eUsr.name.trim() || (!hasCpf && !hasEmail)) {
      msg("⚠ Nome e CPF ou e-mail obrigatórios");
      return;
    }
    if (
      hasCpf &&
      usrs.find(
        (u) => cleanCPF(u.cpf) === cleanCPF(eUsr.cpf) && u.id !== eUsr.id,
      )
    ) {
      msg("⚠ CPF já cadastrado");
      return;
    }
    const u = { ...eUsr, cpf: hasCpf ? cleanCPF(eUsr.cpf) : "" };
    if (u._t) {
      u.tempPassword = h(u._t);
    }
    if (u._p) {
      u.password = h(u._p);
      u.hasSetOwnPassword = true;
    }
    delete u._t;
    delete u._p;
    const ul = usrs.find((x) => x.id === u.id)
      ? usrs.map((x) => (x.id === u.id ? u : x))
      : [...usrs, u];
    setUsrs(ul);
    await sv("users", u);
    const al = accs.map((a) => {
      const had = (a.userIds || []).includes(u.id),
        has = (u.accountIds || []).includes(a.id);
      if (had === has) return a;
      const ids = has
        ? [...(a.userIds || []).filter((i) => i !== u.id), u.id]
        : (a.userIds || []).filter((i) => i !== u.id);
      return { ...a, userIds: ids };
    });
    const ch = al.filter(
      (a, i) => JSON.stringify(a) !== JSON.stringify(accs[i]),
    );
    setAccs(al);
    await Promise.all(ch.map((a) => sv("accounts", a)));
    setEUsr(null);
    msg("✓ Usuário salvo");
  }
  async function delA(id) {
    await onDelete("accounts", id);
    setAccs((p) => p.filter((a) => a.id !== id));
    const ul = usrs.map((u) => ({
      ...u,
      accountIds: (u.accountIds || []).filter((i) => i !== id),
    }));
    setUsrs(ul);
    await Promise.all(ul.map((u) => sv("users", u)));
    msg("Conta removida");
  }
  async function delU(id) {
    await onDelete("users", id);
    setUsrs((p) => p.filter((u) => u.id !== id));
    const al = accs.map((a) => ({
      ...a,
      userIds: (a.userIds || []).filter((i) => i !== id),
    }));
    setAccs(al);
    await Promise.all(al.map((a) => sv("accounts", a)));
    msg("Usuário removido");
  }
  async function runDiag(a) {
    setDiag({ loading: true, name: a.name });
    const r = {
      name: a.name,
      projectId: a.projectId || "—",
      hasConfig: !!a.firebaseConfig,
      configPreview: "",
      lancamentos: "?",
      error: "",
    };
    try {
      const cfg = parseCfg(a.firebaseConfig);
      if (!cfg) throw new Error("Config inválida");
      r.configPreview = JSON.stringify(cfg, null, 2).slice(0, 300);
      r.projectId = cfg.projectId || "—";
      const db = getDb(cfg);
      const snap = await db.collection("lancamentos").get();
      r.lancamentos = snap.size;
    } catch (e) {
      r.error = e.message;
    }
    setDiag({ loading: false, ...r });
  }
  const fA = accs.filter((a) => {
    const nm =
      !aS || (a.name + a.projectId).toLowerCase().includes(aS.toLowerCase());
    const st =
      aFSt === "all" ||
      (aFSt === "active" && a.active && !a.blocked) ||
      (aFSt === "inactive" && !a.active && !a.blocked) ||
      (aFSt === "blocked" && a.blocked);
    return nm && st;
  });
  const fU = usrs.filter((u) => {
    const nm =
      !uS ||
      u.name.toLowerCase().includes(uS.toLowerCase()) ||
      (u.cpf || "").includes(cleanCPF(uS));
    const st =
      uFSt === "all" ||
      (uFSt === "active" && u.active && !u.blocked) ||
      (uFSt === "inactive" && !u.active && !u.blocked) ||
      (uFSt === "blocked" && u.blocked);
    return nm && st;
  });

  function repDateRange() {
    if (repPMode === "month") {
      const [ry, rm] = repMonth.split("-").map(Number);
      const last = new Date(ry, rm, 0).getDate();
      return { from: `${repMonth}-01`, to: `${repMonth}-${pad(last)}` };
    }
    return { from: repFrom, to: repTo };
  }
  function repPrevMonth() {
    const [ry, rm] = repMonth.split("-").map(Number);
    const d = new Date(ry, rm - 2, 1);
    const py = d.getFullYear(),
      pm = d.getMonth() + 1;
    const last = new Date(py, pm, 0).getDate();
    return {
      from: `${py}-${pad(pm)}-01`,
      to: `${py}-${pad(pm)}-${pad(last)}`,
      ym: `${py}-${pad(pm)}`,
    };
  }
  async function loadReport() {
    setRepLoading(true);
    const { from, to } = repDateRange();
    const prev = repPrevMonth();
    const results = {};
    await Promise.allSettled(
      accs.map(async (a) => {
        results[a.id] = { loading: true };
        try {
          const cfg = parseCfg(a.firebaseConfig);
          if (!cfg) throw new Error("Config inválida");
          const db = getDb(cfg);
          const [snapCur, snapPrev, snapAll] = await Promise.all([
            db
              .collection("lancamentos")
              .where("date", ">=", from)
              .where("date", "<=", to)
              .get(),
            db
              .collection("lancamentos")
              .where("date", ">=", prev.from)
              .where("date", "<=", prev.to)
              .get(),
            db.collection("lancamentos").get(),
          ]);
          results[a.id] = {
            curRecords: snapCur.size,
            prevRecords: snapPrev.size,
            totalRecords: snapAll.size,
            loading: false,
          };
        } catch (e) {
          results[a.id] = { error: e.message, loading: false };
        }
      }),
    );
    setRepData(results);
    setRepLoading(false);
  }
  const SB = ({ ok, blocked, label }) => (
    <span
      style={{
        fontSize: 10,
        background: blocked
          ? "rgba(139,58,58,0.15)"
          : ok
            ? "rgba(58,139,90,0.15)"
            : "rgba(139,120,58,0.15)",
        color: blocked ? "#8A3A3A" : ok ? "#3A8A5A" : "#8A7A3A",
        borderRadius: 4,
        padding: "2px 8px",
        fontWeight: 600,
        fontFamily: GEO,
      }}
    >
      {label}
    </span>
  );

  return (
    <div style={{ fontFamily: GEO, background: th.bg, minHeight: "100vh" }}>
      <div
        style={{
          background: th.sur,
          borderBottom: `1px solid ${th.bdr}`,
          padding: "14px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <img
            src={dark ? A.logoHD : A.logoHL}
            alt="Fortuny One"
            style={{ height: 42, objectFit: "contain" }}
          />
          <div style={{ width: 1, height: 36, background: th.bdr }} />
          <div>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: th.acc,
                fontFamily: GEO,
                letterSpacing: "1px",
                textTransform: "uppercase",
              }}
            >
              Painel Admin
            </div>
            <div style={{ fontSize: 10, color: th.t2 }}>
              {new Date().toLocaleDateString("pt-BR", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {toastMsg && (
            <div
              style={{
                fontSize: 11,
                color: th.acc,
                fontWeight: 600,
                background: th.s2,
                borderRadius: 6,
                padding: "4px 12px",
                border: `1px solid ${th.bdr}`,
                fontFamily: GEO,
              }}
            >
              {toastMsg}
            </div>
          )}
          <button
            onClick={() => setDark((d) => !d)}
            style={{
              background: th.s2,
              border: `1px solid ${th.bdr}`,
              borderRadius: 8,
              width: 34,
              height: 34,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
            }}
          >
            <img
              src={dark ? A.icL : A.icD}
              alt=""
              style={{ width: 18, height: 18, objectFit: "contain" }}
            />
          </button>
          <Btn
            onClick={onLogout}
            variant="danger"
            style={{ padding: "8px 14px", fontSize: 12 }}
          >
            Sair
          </Btn>
        </div>
      </div>
      <div style={{ maxWidth: 1000, margin: "0 auto", padding: "1.5rem" }}>
        <div
          style={{
            display: "flex",
            gap: 3,
            marginBottom: 24,
            background: th.s2,
            borderRadius: 8,
            padding: 4,
            border: `1px solid ${th.bdr}`,
          }}
        >
          {[
            ["home", "Visão Geral"],
            ["accounts", "Contas"],
            ["users", "Usuários"],
            ["report", "Relatório"],
          ].map(([id, l]) => (
            <button
              key={id}
              onClick={() => setView(id)}
              style={{
                padding: "9px 18px",
                borderRadius: 6,
                border: "none",
                cursor: "pointer",
                fontWeight: view === id ? 700 : 400,
                background: view === id ? GOLD : "transparent",
                color: view === id ? "#0D1A00" : th.t2,
                fontSize: 13,
                fontFamily: GEO,
              }}
            >
              {l}
            </button>
          ))}
        </div>

        {view === "home" && (
          <div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))",
                gap: 12,
                marginBottom: 20,
              }}
            >
              {[
                [
                  "Contas",
                  accs.length,
                  `${accs.filter((a) => a.active && !a.blocked).length} ativas`,
                ],
                [
                  "Usuários",
                  usrs.length,
                  `${usrs.filter((u) => u.active && !u.blocked).length} ativos`,
                ],
              ].map(([l, v, s]) => (
                <div
                  key={l}
                  style={{
                    background: th.sur,
                    border: `1px solid ${th.bdr}`,
                    borderRadius: 10,
                    padding: "20px",
                    borderTop: `3px solid ${GOLD}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      color: th.t2,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      marginBottom: 8,
                      fontFamily: GEO,
                    }}
                  >
                    {l}
                  </div>
                  <div
                    style={{
                      fontSize: 28,
                      fontWeight: 800,
                      color: th.acc,
                      fontFamily: GEO,
                    }}
                  >
                    {v}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: th.t2,
                      marginTop: 4,
                      fontFamily: GEO,
                    }}
                  >
                    {s}
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                background: th.sur,
                border: `1px solid ${th.bdr}`,
                borderRadius: 10,
                padding: "18px",
                marginBottom: 14,
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: th.acc,
                  marginBottom: 12,
                  fontFamily: GEO,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Contas
              </div>
              {accs.map((a) => {
                const us = usrs.filter((u) => (a.userIds || []).includes(u.id));
                return (
                  <div
                    key={a.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "9px 0",
                      borderBottom: `1px solid ${th.bdr}`,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: th.txt,
                          fontFamily: GEO,
                        }}
                      >
                        {a.name}{" "}
                        <span
                          style={{
                            fontSize: 11,
                            color: th.t2,
                            fontWeight: 400,
                          }}
                        >
                          · {a.label}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: th.t2 }}>
                        {a.projectId} · {us.length} usuário
                        {us.length !== 1 ? "s" : ""}
                      </div>
                    </div>
                    <SB
                      ok={a.active && !a.blocked}
                      blocked={a.blocked}
                      label={
                        a.blocked ? "Bloqueada" : a.active ? "Ativa" : "Inativa"
                      }
                    />
                  </div>
                );
              })}
            </div>
            <div
              style={{
                background: th.sur,
                border: `1px solid ${th.bdr}`,
                borderRadius: 10,
                padding: "18px",
              }}
            >
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: th.acc,
                  marginBottom: 12,
                  fontFamily: GEO,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Usuários
              </div>
              {usrs.map((u) => {
                const uA = accs.filter((a) =>
                  (u.accountIds || []).includes(a.id),
                );
                return (
                  <div
                    key={u.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "9px 0",
                      borderBottom: `1px solid ${th.bdr}`,
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: th.txt,
                          fontFamily: GEO,
                        }}
                      >
                        {u.name}
                      </div>
                      <div style={{ fontSize: 11, color: th.t2 }}>
                        CPF: {fmtCPF(u.cpf)} ·{" "}
                        {uA.map((a) => a.name).join(", ") || "Sem conta"}
                      </div>
                    </div>
                    <SB
                      ok={u.active && !u.blocked}
                      blocked={u.blocked}
                      label={
                        u.blocked ? "Bloqueado" : u.active ? "Ativo" : "Inativo"
                      }
                    />
                    {u.hasSetOwnPassword && (
                      <span
                        style={{
                          fontSize: 10,
                          background: "rgba(201,168,76,0.1)",
                          color: th.acc,
                          borderRadius: 4,
                          padding: "2px 7px",
                          fontWeight: 600,
                          fontFamily: GEO,
                        }}
                      >
                        Senha ✓
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {view === "accounts" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <h3
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: th.txt,
                  margin: 0,
                  fontFamily: GEO,
                }}
              >
                Contas ({accs.length})
              </h3>
              <Btn
                onClick={() =>
                  setEAcc({
                    id: gid(),
                    name: "",
                    projectId: "",
                    label: "",
                    active: true,
                    blocked: false,
                    createdAt: ymd(now),
                    userIds: [],
                    firebaseConfig: "",
                  })
                }
              >
                + Nova conta
              </Btn>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <input
                value={aS}
                onChange={(e) => setAS(e.target.value)}
                placeholder="Buscar por nome ou projeto..."
                style={{ ...sI, maxWidth: 280, marginBottom: 0 }}
              />
              {["all", "active", "inactive", "blocked"].map((s) => {
                const lbl = {
                  all: "Todos",
                  active: "Ativas",
                  inactive: "Inativas",
                  blocked: "Bloqueadas",
                }[s];
                return (
                  <button
                    key={s}
                    onClick={() => setAFSt(s)}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 20,
                      border: `1px solid ${aFSt === s ? GOLD : th.bdr}`,
                      background:
                        aFSt === s ? "rgba(201,168,76,0.12)" : "transparent",
                      color: aFSt === s ? th.acc : th.t2,
                      fontWeight: aFSt === s ? 700 : 400,
                      fontSize: 12,
                      cursor: "pointer",
                      fontFamily: GEO,
                    }}
                  >
                    {lbl}
                  </button>
                );
              })}
              <span style={{ fontSize: 11, color: th.t2, fontFamily: GEO }}>
                {fA.length} resultado{fA.length !== 1 ? "s" : ""}
              </span>
            </div>
            {fA.map((a) => {
              const us = usrs.filter((u) => (a.userIds || []).includes(u.id));
              return (
                <div
                  key={a.id}
                  style={{
                    background: th.sur,
                    border: `1px solid ${a.blocked ? "rgba(139,58,58,0.4)" : th.bdr}`,
                    borderRadius: 10,
                    padding: "18px",
                    marginBottom: 10,
                    borderLeft: `3px solid ${a.blocked ? "#8A3A3A" : a.active ? GOLD : th.bdr}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: th.txt,
                            fontFamily: GEO,
                          }}
                        >
                          {a.name}
                        </span>
                        <SB
                          ok={a.active && !a.blocked}
                          blocked={a.blocked}
                          label={
                            a.blocked
                              ? "Bloqueada"
                              : a.active
                                ? "Ativa"
                                : "Inativa"
                          }
                        />
                      </div>
                      <div style={{ fontSize: 11, color: th.t2 }}>
                        {a.projectId || "—"} · {a.label}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Btn
                        onClick={() => setEAcc({ ...a })}
                        style={{ padding: "7px 12px", fontSize: 12 }}
                      >
                        Editar
                      </Btn>
                      <Btn
                        onClick={() => runDiag(a)}
                        variant="ghost"
                        style={{ padding: "7px 12px", fontSize: 12 }}
                      >
                        Diagnóstico
                      </Btn>
                      <Btn
                        onClick={() =>
                          setConfirmDel({
                            type: "account",
                            id: a.id,
                            name: a.name,
                          })
                        }
                        variant="danger"
                        style={{ padding: "7px 12px", fontSize: 12 }}
                      >
                        Remover
                      </Btn>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 20,
                      marginBottom: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        cursor: "pointer",
                      }}
                    >
                      <Toggle
                        value={a.active}
                        onChange={async (v) => {
                          const u = { ...a, active: v };
                          setAccs((p) => p.map((x) => (x.id === a.id ? u : x)));
                          await sv("accounts", u);
                          msg(v ? "Ativa" : "Inativa");
                        }}
                      />
                      <span
                        style={{ fontSize: 12, color: th.txt, fontFamily: GEO }}
                      >
                        Ativa
                      </span>
                    </label>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        cursor: "pointer",
                      }}
                    >
                      <Toggle
                        value={a.blocked}
                        onChange={async (v) => {
                          const u = { ...a, blocked: v };
                          setAccs((p) => p.map((x) => (x.id === a.id ? u : x)));
                          await sv("accounts", u);
                          msg(v ? "Bloqueada" : "Desbloqueada");
                        }}
                      />
                      <span
                        style={{
                          fontSize: 12,
                          color: a.blocked ? "#8A3A3A" : th.txt,
                          fontFamily: GEO,
                        }}
                      >
                        Bloqueada
                      </span>
                    </label>
                  </div>
                  <div
                    style={{
                      background: th.s2,
                      borderRadius: 6,
                      padding: "9px 12px",
                      border: `1px solid ${th.bdr}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        color: th.t2,
                        fontWeight: 600,
                        marginBottom: 5,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontFamily: GEO,
                      }}
                    >
                      Usuários ({us.length})
                    </div>
                    {us.length === 0 ? (
                      <div
                        style={{ fontSize: 12, color: th.t2, fontFamily: GEO }}
                      >
                        Nenhum
                      </div>
                    ) : (
                      <div
                        style={{ display: "flex", gap: 5, flexWrap: "wrap" }}
                      >
                        {us.map((u) => (
                          <span
                            key={u.id}
                            style={{
                              background: th.sur,
                              border: `1px solid ${th.bdr}`,
                              borderRadius: 4,
                              padding: "2px 8px",
                              fontSize: 12,
                              color: th.txt,
                              fontFamily: GEO,
                            }}
                          >
                            {u.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {view === "users" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <h3
                style={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: th.txt,
                  margin: 0,
                  fontFamily: GEO,
                }}
              >
                Usuários ({usrs.length})
              </h3>
              <Btn
                onClick={() =>
                  setEUsr({
                    id: gid(),
                    name: "",
                    cpf: "",
                    active: true,
                    blocked: false,
                    tempPassword: "",
                    password: "",
                    hasSetOwnPassword: false,
                    accountIds: [],
                    createdAt: ymd(now),
                    _t: "",
                    _p: "",
                  })
                }
              >
                + Novo usuário
              </Btn>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
              }}
            >
              <input
                value={uS}
                onChange={(e) => setUS(e.target.value)}
                placeholder="Buscar por nome ou CPF..."
                style={{ ...sI, maxWidth: 280, marginBottom: 0 }}
              />
              {["all", "active", "inactive", "blocked"].map((s) => {
                const lbl = {
                  all: "Todos",
                  active: "Ativos",
                  inactive: "Inativos",
                  blocked: "Bloqueados",
                }[s];
                return (
                  <button
                    key={s}
                    onClick={() => setUFSt(s)}
                    style={{
                      padding: "6px 14px",
                      borderRadius: 20,
                      border: `1px solid ${uFSt === s ? GOLD : th.bdr}`,
                      background:
                        uFSt === s ? "rgba(201,168,76,0.12)" : "transparent",
                      color: uFSt === s ? th.acc : th.t2,
                      fontWeight: uFSt === s ? 700 : 400,
                      fontSize: 12,
                      cursor: "pointer",
                      fontFamily: GEO,
                    }}
                  >
                    {lbl}
                  </button>
                );
              })}
              <span style={{ fontSize: 11, color: th.t2, fontFamily: GEO }}>
                {fU.length} resultado{fU.length !== 1 ? "s" : ""}
              </span>
            </div>
            {fU.map((u) => {
              const uA = accs.filter((a) =>
                (u.accountIds || []).includes(a.id),
              );
              return (
                <div
                  key={u.id}
                  style={{
                    background: th.sur,
                    border: `1px solid ${u.blocked ? "rgba(139,58,58,0.4)" : th.bdr}`,
                    borderRadius: 10,
                    padding: "18px",
                    marginBottom: 10,
                    borderLeft: `3px solid ${u.blocked ? "#8A3A3A" : u.active ? GOLD : th.bdr}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      flexWrap: "wrap",
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          marginBottom: 4,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: th.txt,
                            fontFamily: GEO,
                          }}
                        >
                          {u.name}
                        </span>
                        <SB
                          ok={u.active && !u.blocked}
                          blocked={u.blocked}
                          label={
                            u.blocked
                              ? "Bloqueado"
                              : u.active
                                ? "Ativo"
                                : "Inativo"
                          }
                        />
                        {u.hasSetOwnPassword && (
                          <span
                            style={{
                              fontSize: 10,
                              background: "rgba(201,168,76,0.1)",
                              color: th.acc,
                              borderRadius: 4,
                              padding: "2px 7px",
                              fontWeight: 600,
                              fontFamily: GEO,
                            }}
                          >
                            Senha ✓
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: th.t2 }}>
                        CPF: {fmtCPF(u.cpf)} · {u.createdAt}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Btn
                        onClick={() => setEUsr({ ...u, _t: "", _p: "" })}
                        style={{ padding: "7px 12px", fontSize: 12 }}
                      >
                        Editar
                      </Btn>
                      <Btn
                        onClick={() =>
                          setConfirmDel({
                            type: "user",
                            id: u.id,
                            name: u.name,
                          })
                        }
                        variant="danger"
                        style={{ padding: "7px 12px", fontSize: 12 }}
                      >
                        Remover
                      </Btn>
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      gap: 20,
                      marginBottom: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        cursor: "pointer",
                      }}
                    >
                      <Toggle
                        value={u.active}
                        onChange={async (v) => {
                          const upd = { ...u, active: v };
                          setUsrs((p) =>
                            p.map((x) => (x.id === u.id ? upd : x)),
                          );
                          await sv("users", upd);
                          msg(v ? "Ativo" : "Inativo");
                        }}
                      />
                      <span
                        style={{ fontSize: 12, color: th.txt, fontFamily: GEO }}
                      >
                        Ativo
                      </span>
                    </label>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        cursor: "pointer",
                      }}
                    >
                      <Toggle
                        value={u.blocked}
                        onChange={async (v) => {
                          const upd = { ...u, blocked: v };
                          setUsrs((p) =>
                            p.map((x) => (x.id === u.id ? upd : x)),
                          );
                          await sv("users", upd);
                          msg(v ? "Bloqueado" : "Desbloqueado");
                        }}
                      />
                      <span
                        style={{
                          fontSize: 12,
                          color: u.blocked ? "#8A3A3A" : th.txt,
                          fontFamily: GEO,
                        }}
                      >
                        Bloqueado
                      </span>
                    </label>
                  </div>
                  <div
                    style={{
                      background: th.s2,
                      borderRadius: 6,
                      padding: "9px 12px",
                      border: `1px solid ${th.bdr}`,
                    }}
                  >
                    <div
                      style={{
                        fontSize: 10,
                        color: th.t2,
                        fontWeight: 600,
                        marginBottom: 5,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        fontFamily: GEO,
                      }}
                    >
                      Contas
                    </div>
                    {uA.length === 0 ? (
                      <div
                        style={{ fontSize: 12, color: th.t2, fontFamily: GEO }}
                      >
                        Sem acesso
                      </div>
                    ) : (
                      <div
                        style={{ display: "flex", gap: 5, flexWrap: "wrap" }}
                      >
                        {uA.map((a) => (
                          <span
                            key={a.id}
                            style={{
                              background: "rgba(201,168,76,0.1)",
                              color: th.acc,
                              borderRadius: 4,
                              padding: "3px 9px",
                              fontSize: 12,
                              fontWeight: 600,
                              fontFamily: GEO,
                            }}
                          >
                            {a.name}
                            {a.blocked ? " 🔒" : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {view === "report" && (() => {
          const { from, to } = repDateRange();
          const prev = repPrevMonth();
          const daysInPeriod =
            Math.round(
              (new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24),
            ) + 1;
          const daysInPrev =
            Math.round(
              (new Date(prev.to) - new Date(prev.from)) /
                (1000 * 60 * 60 * 24),
            ) + 1;

          const loaded = Object.keys(repData).length > 0;
          const accList = repAccFilter
            ? accs.filter((a) => a.id === repAccFilter)
            : accs;
          const visibleAccs = accList.filter((_, i) => {
            const start = repPage * REP_PAGE_SIZE;
            return i >= start && i < start + REP_PAGE_SIZE;
          });
          const totalPages = Math.ceil(accList.length / REP_PAGE_SIZE);

          const top5ByRecords = [...accs]
            .filter((a) => repData[a.id]?.curRecords != null)
            .sort(
              (a, b) =>
                (repData[b.id]?.curRecords || 0) -
                (repData[a.id]?.curRecords || 0),
            )
            .slice(0, 5);

          const top5ByTotal = [...accs]
            .filter((a) => repData[a.id]?.totalRecords != null)
            .sort(
              (a, b) =>
                (repData[b.id]?.totalRecords || 0) -
                (repData[a.id]?.totalRecords || 0),
            )
            .slice(0, 5);

          const totalCurRecords = accs.reduce(
            (s, a) => s + (repData[a.id]?.curRecords || 0),
            0,
          );
          const totalPrevRecords = accs.reduce(
            (s, a) => s + (repData[a.id]?.prevRecords || 0),
            0,
          );

          const chipSt = (active) => ({
            padding: "6px 14px",
            borderRadius: 20,
            border: `1px solid ${active ? GOLD : th.bdr}`,
            background: active ? "rgba(201,168,76,0.12)" : "transparent",
            color: active ? th.acc : th.t2,
            fontWeight: active ? 700 : 400,
            fontSize: 12,
            cursor: "pointer",
            fontFamily: GEO,
          });

          const cardSt = {
            background: th.sur,
            border: `1px solid ${th.bdr}`,
            borderRadius: 10,
            padding: "18px 20px",
            borderTop: `3px solid ${GOLD}`,
          };

          const thSt = {
            padding: "8px 12px",
            fontSize: 11,
            color: th.t2,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.5px",
            borderBottom: `1px solid ${th.bdr}`,
            textAlign: "left",
            fontFamily: GEO,
            background: th.s2,
            whiteSpace: "nowrap",
          };
          const tdSt = {
            padding: "9px 12px",
            fontSize: 12,
            color: th.txt,
            borderBottom: `1px solid ${th.bdr}`,
            fontFamily: GEO,
          };

          return (
            <div>
              {/* Summary cards */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill,minmax(180px,1fr))",
                  gap: 12,
                  marginBottom: 24,
                }}
              >
                {[
                  [
                    "Usuários",
                    usrs.length,
                    usrs.filter((u) => u.active && !u.blocked).length,
                  ],
                  [
                    "Contas",
                    accs.length,
                    accs.filter((a) => a.active && !a.blocked).length,
                  ],
                ].map(([l, total, active]) => (
                  <div key={l} style={cardSt}>
                    <div
                      style={{ fontSize: 11, color: th.t2, fontFamily: GEO }}
                    >
                      {l}
                    </div>
                    <div
                      style={{
                        fontSize: 28,
                        fontWeight: 700,
                        color: th.txt,
                        fontFamily: GEO,
                        lineHeight: 1.2,
                      }}
                    >
                      {total}
                    </div>
                    <div
                      style={{ fontSize: 12, color: th.acc, fontFamily: GEO }}
                    >
                      {active} ativo{active !== 1 ? "s" : ""}
                    </div>
                  </div>
                ))}
              </div>

              {/* Period filter bar */}
              <div
                style={{
                  background: th.sur,
                  border: `1px solid ${th.bdr}`,
                  borderRadius: 10,
                  padding: "16px 20px",
                  marginBottom: 20,
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontSize: 11,
                    color: th.t2,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontFamily: GEO,
                  }}
                >
                  Período
                </span>
                <button
                  onClick={() => setRepPMode("month")}
                  style={chipSt(repPMode === "month")}
                >
                  Mês fixo
                </button>
                <button
                  onClick={() => setRepPMode("custom")}
                  style={chipSt(repPMode === "custom")}
                >
                  Período livre
                </button>
                {repPMode === "month" ? (
                  <input
                    type="month"
                    value={repMonth}
                    onChange={(e) => setRepMonth(e.target.value)}
                    style={{
                      ...sI,
                      width: "auto",
                      marginBottom: 0,
                      padding: "5px 10px",
                    }}
                  />
                ) : (
                  <div
                    style={{ display: "flex", gap: 8, alignItems: "center" }}
                  >
                    <input
                      type="date"
                      value={repFrom}
                      onChange={(e) => setRepFrom(e.target.value)}
                      style={{
                        ...sI,
                        width: "auto",
                        marginBottom: 0,
                        padding: "5px 10px",
                      }}
                    />
                    <span style={{ fontSize: 11, color: th.t2 }}>até</span>
                    <input
                      type="date"
                      value={repTo}
                      onChange={(e) => setRepTo(e.target.value)}
                      style={{
                        ...sI,
                        width: "auto",
                        marginBottom: 0,
                        padding: "5px 10px",
                      }}
                    />
                  </div>
                )}
                <Btn
                  onClick={loadReport}
                  style={{ padding: "7px 18px", fontSize: 12 }}
                >
                  {repLoading ? "Carregando..." : "Atualizar"}
                </Btn>
                {loaded && (
                  <span style={{ fontSize: 11, color: th.acc, fontFamily: GEO }}>
                    De {from} até {to}
                  </span>
                )}
              </div>

              {/* Top 5 blocks */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 14,
                  marginBottom: 24,
                }}
              >
                {/* Top 5 por registros no período */}
                <div style={cardSt}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: th.acc,
                      fontFamily: GEO,
                      marginBottom: 12,
                    }}
                  >
                    Top 5 — Registros no período
                  </div>
                  {!loaded ? (
                    <div style={{ fontSize: 12, color: th.t2, fontFamily: GEO }}>
                      Clique em Atualizar para carregar.
                    </div>
                  ) : top5ByRecords.length === 0 ? (
                    <div style={{ fontSize: 12, color: th.t2, fontFamily: GEO }}>
                      Sem dados.
                    </div>
                  ) : (
                    top5ByRecords.map((a, i) => {
                      const v = repData[a.id]?.curRecords || 0;
                      const max = repData[top5ByRecords[0].id]?.curRecords || 1;
                      return (
                        <div key={a.id} style={{ marginBottom: 10 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: 3,
                            }}
                          >
                            <span style={{ fontSize: 12, color: th.txt, fontFamily: GEO }}>
                              {i + 1}. {a.name}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: th.acc, fontFamily: GEO }}>
                              {v.toLocaleString("pt-BR")}
                            </span>
                          </div>
                          <div style={{ height: 4, background: th.bdr, borderRadius: 2 }}>
                            <div style={{ height: 4, width: `${(v / max) * 100}%`, background: GOLD, borderRadius: 2 }} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Top 5 por total acumulado */}
                <div style={cardSt}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: th.acc,
                      fontFamily: GEO,
                      marginBottom: 12,
                    }}
                  >
                    Top 5 — Total acumulado de registros
                  </div>
                  {!loaded ? (
                    <div style={{ fontSize: 12, color: th.t2, fontFamily: GEO }}>
                      Clique em Atualizar para carregar.
                    </div>
                  ) : top5ByTotal.length === 0 ? (
                    <div style={{ fontSize: 12, color: th.t2, fontFamily: GEO }}>
                      Sem dados.
                    </div>
                  ) : (
                    top5ByTotal.map((a, i) => {
                      const v = repData[a.id]?.totalRecords || 0;
                      const max = repData[top5ByTotal[0].id]?.totalRecords || 1;
                      return (
                        <div key={a.id} style={{ marginBottom: 10 }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: 3,
                            }}
                          >
                            <span style={{ fontSize: 12, color: th.txt, fontFamily: GEO }}>
                              {i + 1}. {a.name}
                            </span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: th.acc, fontFamily: GEO }}>
                              {v.toLocaleString("pt-BR")}
                            </span>
                          </div>
                          <div style={{ height: 4, background: th.bdr, borderRadius: 2 }}>
                            <div style={{ height: 4, width: `${(v / max) * 100}%`, background: GOLD, borderRadius: 2 }} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Account filter + summary totals */}
              <div
                style={{
                  background: th.sur,
                  border: `1px solid ${th.bdr}`,
                  borderRadius: 10,
                  padding: "16px 20px",
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    flexWrap: "wrap",
                    marginBottom: 16,
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      color: th.t2,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: "0.5px",
                      fontFamily: GEO,
                    }}
                  >
                    Filtrar por conta
                  </span>
                  <select
                    value={repAccFilter}
                    onChange={(e) => {
                      setRepAccFilter(e.target.value);
                      setRepPage(0);
                    }}
                    style={{
                      ...sI,
                      width: "auto",
                      marginBottom: 0,
                      padding: "6px 10px",
                      minWidth: 200,
                    }}
                  >
                    <option value="">Todas as contas</option>
                    {accs.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Summary totals */}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))",
                    gap: 12,
                  }}
                >
                  {[
                    [
                      `Registros no período (${from} → ${to})`,
                      loaded ? totalCurRecords.toLocaleString("pt-BR") : "—",
                      loaded && daysInPeriod > 1
                        ? `Média: ${(totalCurRecords / daysInPeriod).toFixed(1)}/dia`
                        : null,
                      GOLD,
                    ],
                    [
                      `Registros mês anterior (${prev.ym})`,
                      loaded ? totalPrevRecords.toLocaleString("pt-BR") : "—",
                      loaded && daysInPrev > 1
                        ? `Média: ${(totalPrevRecords / daysInPrev).toFixed(1)}/dia`
                        : null,
                      th.txt,
                    ],
                  ].map(([label, value, sub, color]) => (
                    <div
                      key={label}
                      style={{
                        background: th.s2,
                        borderRadius: 8,
                        padding: "12px 16px",
                        border: `1px solid ${th.bdr}`,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 10,
                          color: th.t2,
                          fontFamily: GEO,
                          marginBottom: 4,
                          textTransform: "uppercase",
                          letterSpacing: "0.4px",
                        }}
                      >
                        {label}
                      </div>
                      <div
                        style={{
                          fontSize: 20,
                          fontWeight: 700,
                          color: color,
                          fontFamily: GEO,
                        }}
                      >
                        {value}
                      </div>
                      {sub && (
                        <div
                          style={{
                            fontSize: 11,
                            color: th.t2,
                            fontFamily: GEO,
                            marginTop: 2,
                          }}
                        >
                          {sub}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Full accounts table */}
              <div
                style={{
                  background: th.sur,
                  border: `1px solid ${th.bdr}`,
                  borderRadius: 10,
                  overflow: "hidden",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    padding: "14px 20px",
                    borderBottom: `1px solid ${th.bdr}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: th.txt,
                      fontFamily: GEO,
                    }}
                  >
                    Todas as contas
                  </span>
                  <span style={{ fontSize: 11, color: th.t2, fontFamily: GEO }}>
                    {accList.length} conta{accList.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr>
                        {[
                          "Conta",
                          "Usuários",
                          "Total registros",
                          `Registros ${repPMode === "month" ? repMonth : "período"} (méd/dia)`,
                          `Registros ${prev.ym} (méd/dia)`,
                        ].map((h) => (
                          <th key={h} style={thSt}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {visibleAccs.map((a) => {
                        const d = repData[a.id];
                        const cur = d?.curRecords;
                        const prv = d?.prevRecords;
                        const tot = d?.totalRecords;
                        const err = d?.error;
                        const loading = d?.loading;
                        const fmtR = (v, days) =>
                          v == null
                            ? "—"
                            : `${v.toLocaleString("pt-BR")} (${(v / days).toFixed(1)})`;
                        return (
                          <tr key={a.id}>
                            <td style={tdSt}>
                              <div
                                style={{
                                  fontWeight: 600,
                                  color: th.txt,
                                  fontFamily: GEO,
                                }}
                              >
                                {a.name}
                              </div>
                              <div style={{ fontSize: 10, color: th.t2 }}>
                                {a.projectId}
                              </div>
                              {err && (
                                <div
                                  style={{ fontSize: 10, color: "#8A3A3A" }}
                                >
                                  {err}
                                </div>
                              )}
                            </td>
                            <td style={{ ...tdSt, textAlign: "center" }}>
                              {(a.userIds || []).length}
                            </td>
                            <td style={{ ...tdSt, textAlign: "right" }}>
                              {loading
                                ? "..."
                                : tot != null
                                  ? tot.toLocaleString("pt-BR")
                                  : "—"}
                            </td>
                            <td style={{ ...tdSt, textAlign: "right" }}>
                              {loading ? "..." : fmtR(cur, daysInPeriod)}
                            </td>
                            <td style={{ ...tdSt, textAlign: "right" }}>
                              {loading ? "..." : fmtR(prv, daysInPrev)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {totalPages > 1 && (
                  <div
                    style={{
                      padding: "12px 20px",
                      borderTop: `1px solid ${th.bdr}`,
                      display: "flex",
                      gap: 6,
                      alignItems: "center",
                    }}
                  >
                    {Array.from({ length: totalPages }, (_, i) => (
                      <button
                        key={i}
                        onClick={() => setRepPage(i)}
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 6,
                          border: `1px solid ${repPage === i ? GOLD : th.bdr}`,
                          background:
                            repPage === i
                              ? "rgba(201,168,76,0.12)"
                              : "transparent",
                          color: repPage === i ? th.acc : th.t2,
                          fontWeight: repPage === i ? 700 : 400,
                          fontSize: 12,
                          cursor: "pointer",
                          fontFamily: GEO,
                        }}
                      >
                        {i + 1}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </div>

      {eAcc && (
        <Modal
          title={
            accs.find((a) => a.id === eAcc.id) ? "Editar Conta" : "Nova Conta"
          }
          onClose={() => setEAcc(null)}
          dark={dark}
          maxW={780}
        >
          {!accs.find((a) => a.id === eAcc.id) && (() => {
            const pid = eAcc.projectId || "";
            const consoleBase = pid
              ? `https://console.firebase.google.com/project/${pid}`
              : "https://console.firebase.google.com";
            const steps = [
              {
                title: "Criar projeto no Firebase",
                desc: 'Acesse o console → "Criar projeto" → dê um nome → desative Google Analytics se quiser → clique "Criar projeto".',
                link: "https://console.firebase.google.com/",
                linkLabel: "Abrir Firebase Console",
              },
              {
                title: "Ativar Firestore Database",
                desc: 'Menu lateral → "Firestore Database" → "Criar banco de dados" → região "southamerica-east1" → Modo Produção → "Criar".',
                link: pid ? `${consoleBase}/firestore` : null,
                linkLabel: "Abrir Firestore",
              },
              {
                title: "Registrar app web e copiar config",
                desc: 'Engrenagem ⚙ → "Configurações do projeto" → "Adicionar app" → ícone Web (</>)→ dê um nome → copie o bloco firebaseConfig.',
                link: pid ? `${consoleBase}/settings/general` : null,
                linkLabel: "Configurações do projeto",
              },
              {
                title: "Colar o firebaseConfig abaixo e validar",
                desc: "Cole o JSON no campo Firebase Config e clique em Validar para verificar os campos e importar o Project ID automaticamente.",
                link: null,
              },
            ];
            const total = steps.length;
            const done = accSteps.length;
            return (
              <div
                style={{
                  marginBottom: 18,
                  borderRadius: 10,
                  border: `1px solid ${done === total ? "rgba(201,168,76,0.5)" : th.bdr}`,
                  overflow: "hidden",
                  background: done === total ? "rgba(201,168,76,0.04)" : "transparent",
                }}
              >
                <div
                  style={{
                    padding: "9px 14px",
                    background: "rgba(201,168,76,0.08)",
                    borderBottom: `1px solid ${th.bdr}`,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <span style={{ fontSize: 14 }}>📋</span>
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: th.acc,
                      fontFamily: GEO,
                      letterSpacing: "0.6px",
                      textTransform: "uppercase",
                    }}
                  >
                    Checklist — Configuração Firebase
                  </span>
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: 10,
                      color: done === total ? GOLD_D : th.t2,
                      fontFamily: GEO,
                      fontWeight: done === total ? 700 : 400,
                    }}
                  >
                    {done}/{total} concluídos
                  </span>
                </div>
                <div style={{ padding: "12px 14px" }}>
                  {steps.map((step, i) => {
                    const isDone = accSteps.includes(i);
                    return (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          gap: 10,
                          marginBottom: i < steps.length - 1 ? 12 : 0,
                          alignItems: "flex-start",
                        }}
                      >
                        <button
                          onClick={() =>
                            setAccSteps((p) =>
                              isDone
                                ? p.filter((x) => x !== i)
                                : [...p, i],
                            )
                          }
                          style={{
                            flexShrink: 0,
                            width: 20,
                            height: 20,
                            borderRadius: 4,
                            border: `1.5px solid ${isDone ? GOLD : th.bdr}`,
                            background: isDone
                              ? GOLD
                              : "transparent",
                            color: isDone ? "#0D1A00" : "transparent",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginTop: 1,
                            lineHeight: 1,
                          }}
                        >
                          ✓
                        </button>
                        <div style={{ flex: 1 }}>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: isDone ? th.t2 : th.txt,
                              fontFamily: GEO,
                              textDecoration: isDone ? "line-through" : "none",
                              opacity: isDone ? 0.6 : 1,
                            }}
                          >
                            {i + 1}. {step.title}
                          </div>
                          {!isDone && (
                            <div
                              style={{
                                fontSize: 11,
                                color: th.t2,
                                marginTop: 2,
                                lineHeight: 1.5,
                              }}
                            >
                              {step.desc}
                            </div>
                          )}
                          {!isDone && step.link && (
                            <a
                              href={step.link}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                fontSize: 10,
                                color: GOLD_D,
                                textDecoration: "none",
                                display: "inline-block",
                                marginTop: 4,
                                fontFamily: GEO,
                              }}
                            >
                              {step.linkLabel} ↗
                            </a>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div style={{ gridColumn: "span 2" }}>
              <label style={sL}>Nome *</label>
              <input
                value={eAcc.name}
                onChange={(e) =>
                  setEAcc((p) => ({ ...p, name: e.target.value }))
                }
                style={sI}
              />
            </div>
            <div>
              <label style={sL}>Project ID Firebase</label>
              <input
                value={eAcc.projectId}
                onChange={(e) =>
                  setEAcc((p) => ({ ...p, projectId: e.target.value }))
                }
                placeholder="ex: contas-lar"
                style={sI}
              />
            </div>
            <div>
              <label style={sL}>Label / Ambiente</label>
              <input
                value={eAcc.label}
                onChange={(e) =>
                  setEAcc((p) => ({ ...p, label: e.target.value }))
                }
                placeholder="ex: Produção"
                style={sI}
              />
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={sL}>Firebase Config (JSON)</label>
            <textarea
              value={
                typeof eAcc.firebaseConfig === "object"
                  ? JSON.stringify(eAcc.firebaseConfig, null, 2)
                  : eAcc.firebaseConfig || ""
              }
              onChange={(e) => {
                setEAcc((p) => ({ ...p, firebaseConfig: e.target.value }));
                setCfgStatus(null);
              }}
              rows={7}
              placeholder={'{\n  "apiKey": "...",\n  "projectId": "..."\n}'}
              style={{
                ...sI,
                resize: "vertical",
                fontFamily: "monospace",
                fontSize: 11,
                borderColor:
                  cfgStatus === "ok"
                    ? "#5A8A3A"
                    : cfgStatus?.err
                      ? "#8A3A3A"
                      : th.ib,
              }}
            />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                marginTop: 7,
              }}
            >
              <button
                onClick={validateCfg}
                style={{
                  padding: "6px 16px",
                  borderRadius: 6,
                  border: `1px solid ${GOLD_D}`,
                  background: "rgba(154,122,46,0.1)",
                  color: GOLD_D,
                  fontSize: 11,
                  fontWeight: 700,
                  cursor: "pointer",
                  fontFamily: GEO,
                }}
              >
                ✦ Validar Config
              </button>
              {cfgStatus === "ok" && (
                <span
                  style={{ fontSize: 11, color: "#5A8A3A", fontFamily: GEO }}
                >
                  ✓ Config válido — Project ID importado
                </span>
              )}
              {cfgStatus?.err && (
                <span
                  style={{ fontSize: 11, color: "#8A3A3A", fontFamily: GEO }}
                >
                  ⚠ {cfgStatus.err}
                </span>
              )}
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={sL}>Usuários com acesso</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {usrs.map((u) => {
                const sel = (eAcc.userIds || []).includes(u.id);
                return (
                  <button
                    key={u.id}
                    onClick={() =>
                      setEAcc((p) => ({
                        ...p,
                        userIds: sel
                          ? p.userIds.filter((i) => i !== u.id)
                          : [...(p.userIds || []), u.id],
                      }))
                    }
                    style={{
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: `1.5px solid ${sel ? GOLD : th.bdr}`,
                      background: sel ? "rgba(201,168,76,0.1)" : th.s2,
                      color: sel ? th.acc : th.t2,
                      fontWeight: sel ? 700 : 400,
                      cursor: "pointer",
                      fontSize: 12,
                      fontFamily: GEO,
                    }}
                  >
                    {sel ? "✓ " : ""}
                    {u.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 20,
              marginBottom: 14,
              flexWrap: "wrap",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                cursor: "pointer",
              }}
            >
              <Toggle
                value={eAcc.active}
                onChange={(v) => setEAcc((p) => ({ ...p, active: v }))}
              />
              <span style={{ fontSize: 12, color: th.txt, fontFamily: GEO }}>
                Ativa
              </span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                cursor: "pointer",
              }}
            >
              <Toggle
                value={eAcc.blocked}
                onChange={(v) => setEAcc((p) => ({ ...p, blocked: v }))}
              />
              <span
                style={{
                  fontSize: 12,
                  color: eAcc.blocked ? "#8A3A3A" : th.txt,
                  fontFamily: GEO,
                }}
              >
                Bloqueada
              </span>
            </label>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn
              onClick={() => setEAcc(null)}
              variant="ghost"
              style={{ flex: 1, padding: "10px" }}
            >
              Cancelar
            </Btn>
            <Btn onClick={subAcc} style={{ flex: 2, padding: "10px" }}>
              Salvar
            </Btn>
          </div>
        </Modal>
      )}
      {eUsr && (
        <Modal
          title={
            usrs.find((u) => u.id === eUsr.id)
              ? "Editar Usuário"
              : "Novo Usuário"
          }
          onClose={() => setEUsr(null)}
          dark={dark}
          maxW={580}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div>
              <label style={sL}>Nome *</label>
              <input
                value={eUsr.name}
                onChange={(e) =>
                  setEUsr((p) => ({ ...p, name: e.target.value }))
                }
                style={sI}
              />
            </div>
            <div>
              <label style={sL}>CPF *</label>
              <input
                value={fmtCPF(eUsr.cpf || "")}
                onChange={(e) =>
                  setEUsr((p) => ({ ...p, cpf: cleanCPF(e.target.value) }))
                }
                placeholder="000.000.000-00"
                maxLength={14}
                style={sI}
              />
            </div>
            <div style={{ gridColumn: "span 2" }}>
              <label style={sL}>Email Google (login)</label>
              <input
                value={eUsr.email || ""}
                onChange={(e) =>
                  setEUsr((p) => ({ ...p, email: e.target.value.trim() }))
                }
                placeholder="usuario@gmail.com"
                type="email"
                style={sI}
              />
            </div>
          </div>
          <div
            style={{
              background: th.s2,
              borderRadius: 8,
              padding: "12px 14px",
              marginBottom: 12,
              border: `1px solid ${th.bdr}`,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                color: th.acc,
                marginBottom: 10,
                fontFamily: GEO,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              Senhas
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
              }}
            >
              <div>
                <label style={sL}>Senha provisória</label>
                <input
                  type="password"
                  value={eUsr._t || ""}
                  onChange={(e) =>
                    setEUsr((p) => ({ ...p, _t: e.target.value }))
                  }
                  placeholder="Repassar ao usuário..."
                  style={sI}
                />
                <div
                  style={{
                    fontSize: 10,
                    color: th.t2,
                    marginTop: 3,
                    fontFamily: GEO,
                  }}
                >
                  Trocada no 1º acesso.
                </div>
              </div>
              <div>
                <label style={sL}>Redefinir senha</label>
                <input
                  type="password"
                  value={eUsr._p || ""}
                  onChange={(e) =>
                    setEUsr((p) => ({ ...p, _p: e.target.value }))
                  }
                  placeholder="Vazio = manter..."
                  style={sI}
                />
                <div
                  style={{
                    fontSize: 10,
                    color: th.t2,
                    marginTop: 3,
                    fontFamily: GEO,
                  }}
                >
                  {eUsr.hasSetOwnPassword ? "✓ Senha própria" : "Aguardando"}
                </div>
              </div>
            </div>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={sL}>Contas com acesso</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {accs.map((a) => {
                const sel = (eUsr.accountIds || []).includes(a.id);
                return (
                  <button
                    key={a.id}
                    onClick={() =>
                      setEUsr((p) => ({
                        ...p,
                        accountIds: sel
                          ? p.accountIds.filter((i) => i !== a.id)
                          : [...(p.accountIds || []), a.id],
                      }))
                    }
                    style={{
                      padding: "5px 12px",
                      borderRadius: 6,
                      border: `1.5px solid ${sel ? GOLD : th.bdr}`,
                      background: sel ? "rgba(201,168,76,0.1)" : th.s2,
                      color: sel ? th.acc : th.t2,
                      fontWeight: sel ? 700 : 400,
                      cursor: "pointer",
                      fontSize: 12,
                      fontFamily: GEO,
                    }}
                  >
                    {sel ? "✓ " : ""}
                    {a.name}{" "}
                    <span style={{ fontSize: 10, opacity: 0.7 }}>
                      ({a.label})
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div
            style={{
              display: "flex",
              gap: 20,
              marginBottom: 14,
              flexWrap: "wrap",
            }}
          >
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                cursor: "pointer",
              }}
            >
              <Toggle
                value={eUsr.active}
                onChange={(v) => setEUsr((p) => ({ ...p, active: v }))}
              />
              <span style={{ fontSize: 12, color: th.txt, fontFamily: GEO }}>
                Ativo
              </span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                cursor: "pointer",
              }}
            >
              <Toggle
                value={eUsr.blocked}
                onChange={(v) => setEUsr((p) => ({ ...p, blocked: v }))}
              />
              <span
                style={{
                  fontSize: 12,
                  color: eUsr.blocked ? "#8A3A3A" : th.txt,
                  fontFamily: GEO,
                }}
              >
                Bloqueado
              </span>
            </label>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 7,
                cursor: "pointer",
              }}
            >
              <Toggle
                value={!!eUsr.isAdmin}
                onChange={(v) => setEUsr((p) => ({ ...p, isAdmin: v }))}
              />
              <span
                style={{
                  fontSize: 12,
                  color: eUsr.isAdmin ? GOLD_D : th.txt,
                  fontFamily: GEO,
                }}
              >
                Admin
              </span>
            </label>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn
              onClick={() => setEUsr(null)}
              variant="ghost"
              style={{ flex: 1, padding: "10px" }}
            >
              Cancelar
            </Btn>
            <Btn onClick={subUsr} style={{ flex: 2, padding: "10px" }}>
              Salvar
            </Btn>
          </div>
        </Modal>
      )}
      {diag && (
        <Modal
          title="Diagnóstico"
          onClose={() => setDiag(null)}
          dark={dark}
          maxW={500}
        >
          {diag.loading ? (
            <div
              style={{
                textAlign: "center",
                padding: "2rem",
                color: th.t2,
                fontFamily: GEO,
              }}
            >
              Verificando...
            </div>
          ) : (
            <div>
              <div style={{ display: "grid", gap: 8, marginBottom: 16 }}>
                {[
                  ["Conta", diag.name],
                  ["Project ID", diag.projectId],
                  ["Config", diag.hasConfig ? "Presente" : "Ausente"],
                  ["Lançamentos", diag.lancamentos],
                ].map(([l, v]) => (
                  <div
                    key={l}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      padding: "7px 0",
                      borderBottom: `1px solid ${th.bdr}`,
                    }}
                  >
                    <span
                      style={{ fontSize: 12, color: th.t2, fontFamily: GEO }}
                    >
                      {l}
                    </span>
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        color: th.txt,
                        fontFamily: GEO,
                      }}
                    >
                      {String(v)}
                    </span>
                  </div>
                ))}
              </div>
              {diag.error && (
                <div
                  style={{
                    background: "rgba(139,58,58,0.1)",
                    borderRadius: 6,
                    padding: "10px",
                    fontSize: 12,
                    color: "#8A3A3A",
                    fontFamily: GEO,
                    marginBottom: 12,
                  }}
                >
                  Erro: {diag.error}
                </div>
              )}
              {diag.configPreview && (
                <pre
                  style={{
                    background: th.s2,
                    borderRadius: 6,
                    padding: "10px",
                    fontSize: 11,
                    color: th.txt,
                    overflow: "auto",
                    margin: 0,
                    fontFamily: "monospace",
                    border: `1px solid ${th.bdr}`,
                  }}
                >
                  {diag.configPreview}
                </pre>
              )}
              {!diag.error && diag.lancamentos !== "?" && (
                <div
                  style={{
                    background: "rgba(58,139,90,0.1)",
                    borderRadius: 6,
                    padding: "10px",
                    marginTop: 12,
                    fontSize: 12,
                    color: "#3A8A5A",
                    fontFamily: GEO,
                  }}
                >
                  Conexão estabelecida com sucesso.
                </div>
              )}
            </div>
          )}
        </Modal>
      )}
      {confirmDel && (
        <Modal
          title="Confirmar exclusão"
          onClose={() => setConfirmDel(null)}
          dark={dark}
          maxW={400}
        >
          <p
            style={{
              fontSize: 14,
              color: th.txt,
              marginBottom: 16,
              fontFamily: GEO,
            }}
          >
            Confirma a exclusão de <strong>"{confirmDel.name}"</strong>?
          </p>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn
              onClick={() => setConfirmDel(null)}
              variant="ghost"
              style={{ flex: 1, padding: "10px" }}
            >
              Cancelar
            </Btn>
            <Btn
              onClick={() => {
                confirmDel.type === "account"
                  ? delA(confirmDel.id)
                  : delU(confirmDel.id);
                setConfirmDel(null);
              }}
              variant="danger"
              style={{ flex: 1, padding: "10px" }}
            >
              Confirmar
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── LoginScreen ───────────────────────────────────────────────
function LoginScreen({ dark, setDark, onLogin }) {
  const th = TH(dark);
  const [cpf, setCpf] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("login");
  const [pendU, setPendU] = useState(null);
  const [pendAccs, setPendAccs] = useState([]);
  const [pendAdminData, setPendAdminData] = useState(null);
  const [newPw, setNewPw] = useState("");
  const sI = {
    width: "100%",
    padding: "13px 14px",
    borderRadius: 8,
    border: `1px solid rgba(201,168,76,0.3)`,
    fontSize: 14,
    boxSizing: "border-box",
    background: "rgba(255,255,255,0.07)",
    color: dark ? GOLD_L : "#1A1209",
    outline: "none",
    marginBottom: 12,
    fontFamily: GEO,
    backdropFilter: "blur(4px)",
  };

  async function doGoogleLogin() {
    setLoading(true);
    setErr("");
    let usrs = storeGet("users"),
      accs = storeGet("accounts");
    try {
      const adb = dbA();
      const [lu, la] = await Promise.all([
        fsAll(adb, "users"),
        fsAll(adb, "accounts"),
      ]);
      if (lu.length) {
        usrs = lu;
        _store.users = lu.map((u) => ({ ...u }));
      }
      if (la.length) {
        accs = la;
        _store.accounts = la.map((a) => ({ ...a }));
      }
    } catch (e) {
      console.warn("Firebase indisponível:", e.message);
    }
    let email;
    try {
      email = await signInWithGoogle(FB_ADMIN);
    } catch (e) {
      const code = e?.code || "";
      if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request"
      ) {
        setErr("Login cancelado.");
      } else if (code === "auth/unauthorized-domain") {
        setErr(
          "Domínio não autorizado. Adicione este domínio em Authentication → Settings → Authorized domains no projeto appfinancaadmin.",
        );
      } else if (code === "auth/popup-blocked") {
        setErr("Popup bloqueado pelo navegador. Permita popups para este site.");
      } else {
        setErr(`Erro: ${e?.message || code || "desconhecido"}`);
      }
      setLoading(false);
      return;
    }
    const usr = usrs.find((u) => u.email === email);
    if (!usr) {
      setErr("Email não cadastrado. Contate o administrador.");
      setLoading(false);
      return;
    }
    if (usr.blocked) {
      setErr("Usuário bloqueado.");
      setLoading(false);
      return;
    }
    if (!usr.active) {
      setErr("Usuário inativo.");
      setLoading(false);
      return;
    }
    await routeUser(usr, accs, usrs);
    setLoading(false);
  }

  async function routeUser(usr, accs, usrs) {
    const isAdmin = !!usr.isAdmin;
    const uAccs = accs.filter(
      (a) => (usr.accountIds || []).includes(a.id) && a.active && !a.blocked,
    );
    if (isAdmin && uAccs.length === 0) {
      saveSess({ type: "admin", userId: usr.id });
      onLogin({ type: "admin", accounts: accs, users: usrs });
    } else if (isAdmin && uAccs.length > 0) {
      setPendU(usr);
      setPendAccs(uAccs);
      setPendAdminData({ accounts: accs, users: usrs });
      setPhase("chooseRole");
    } else if (uAccs.length === 1) {
      await connect(usr, uAccs[0], uAccs);
    } else if (uAccs.length > 1) {
      setPendU(usr);
      setPendAccs(uAccs);
      setPhase("selectacc");
    } else {
      setErr("Nenhuma conta disponível.");
    }
  }

  async function doLogin() {
    const raw = cleanCPF(cpf);
    if (!raw || !pw) {
      setErr("Preencha CPF e senha.");
      return;
    }
    setLoading(true);
    setErr("");
    let usrs = storeGet("users"),
      accs = storeGet("accounts");
    try {
      const adb = dbA();
      const [lu, la] = await Promise.all([
        fsAll(adb, "users"),
        fsAll(adb, "accounts"),
      ]);
      if (lu.length) {
        usrs = lu;
        _store.users = lu.map((u) => ({ ...u }));
      }
      if (la.length) {
        accs = la;
        _store.accounts = la.map((a) => ({ ...a }));
      }
    } catch (e) {
      console.warn("Firebase indisponível:", e.message);
    }
    // Fallback: acesso admin por CPF hardcoded (emergência)
    if (raw === ADMIN_CPF) {
      if (h(pw) !== h(ADMIN_PW)) {
        setErr("Senha incorreta.");
        setLoading(false);
        return;
      }
      saveSess({ type: "admin" });
      onLogin({ type: "admin", accounts: accs, users: usrs });
      setLoading(false);
      return;
    }
    const usr = usrs.find((u) => cleanCPF(u.cpf) === raw);
    if (!usr) {
      setErr("Usuário não encontrado.");
      setLoading(false);
      return;
    }
    if (usr.blocked) {
      setErr("Usuário bloqueado.");
      setLoading(false);
      return;
    }
    if (!usr.active) {
      setErr("Usuário inativo.");
      setLoading(false);
      return;
    }
    const pw2 = h(pw);
    if (pw2 !== usr.password && pw2 !== usr.tempPassword) {
      setErr("Senha incorreta.");
      setLoading(false);
      return;
    }
    if (!usr.hasSetOwnPassword && pw2 === usr.tempPassword) {
      setPendU(usr);
      setPendAccs(accs);
      setPhase("newpw");
      setLoading(false);
      return;
    }
    await routeUser(usr, accs, usrs);
    setLoading(false);
  }

  async function confirmNewPw() {
    if (!newPw || newPw.length < 6) {
      setErr("Mínimo 6 caracteres.");
      return;
    }
    setLoading(true);
    const upd = { ...pendU, password: h(newPw), hasSetOwnPassword: true };
    storeSave("users", upd.id, upd);
    try {
      await fsSave(dbA(), "users", upd.id, upd);
    } catch {}
    const accs = storeGet("accounts");
    const uAccs = accs.filter(
      (a) => (upd.accountIds || []).includes(a.id) && a.active && !a.blocked,
    );
    if (uAccs.length === 1) {
      await connect(upd, uAccs[0], uAccs);
    } else {
      setPendU(upd);
      setPendAccs(uAccs);
      setPhase("selectacc");
    }
    setLoading(false);
  }

  async function connect(usr, acc, allAccs) {
    if (acc.blocked) {
      setErr(`Conta "${acc.name}" bloqueada.`);
      return;
    }
    const cfg = parseCfg(acc.firebaseConfig);
    if (!cfg || !cfg.apiKey || !cfg.projectId) {
      setErr(`Config Firebase inválida para "${acc.name}".`);
      return;
    }
    let db = null,
      adminDb = null;
    try {
      db = getDb(cfg);
      adminDb = dbA();
    } catch (e) {
      console.warn(e);
    }
    let pgColors = {};
    if (adminDb) {
      try {
        const pref = await fsOne(adminDb, "userPrefs", `${usr.id}__${acc.id}`);
        if (pref?.pgColors) pgColors = pref.pgColors;
      } catch {}
    }
    saveSess({
      type: "user",
      cpf: usr.cpf,
      name: usr.name,
      userId: usr.id,
      accountId: acc.id,
    });
    onLogin({
      type: "user",
      user: usr,
      account: acc,
      allUserAccs: allAccs,
      db,
      adminDb,
      pgColors,
    });
  }

  const bgSrc = dark ? A.bgDD : A.bgLD;
  const logoSrc = dark ? A.logoHDL : A.logoHLL;
  const wrap = {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    fontFamily: GEO,
    overflow: "hidden",
    background: dark ? NAVY : "#F5F0E8",
  };
  const card = {
    position: "relative",
    zIndex: 2,
    background: dark ? "rgba(13,33,55,0.93)" : "rgba(255,252,245,0.93)",
    borderRadius: 16,
    padding: "3rem 2.5rem",
    maxWidth: 420,
    width: "100%",
    boxSizing: "border-box",
    margin: "0 20px",
    boxShadow: `0 24px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(201,168,76,0.25)`,
    backdropFilter: "blur(20px)",
    border: `1px solid rgba(201,168,76,0.2)`,
    textAlign: "center",
  };

  if (phase === "chooseRole")
    return (
      <div style={wrap}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${bgSrc})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.25,
          }}
        />
        <div style={card}>
          <img
            src={logoSrc}
            alt="Fortuny One"
            style={{ height: 90, marginBottom: 28, objectFit: "contain" }}
          />
          <h2
            style={{
              margin: "0 0 6px",
              fontSize: 18,
              fontWeight: 700,
              color: th.acc,
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            Bem-vindo, {pendU?.name}
          </h2>
          <p style={{ color: th.t2, fontSize: 13, margin: "0 0 24px" }}>
            Como deseja acessar?
          </p>
          <Err msg={err} />
          <button
            onClick={() => {
              saveSess({ type: "admin", userId: pendU.id });
              onLogin({
                type: "admin",
                accounts: pendAdminData?.accounts,
                users: pendAdminData?.users,
              });
            }}
            style={{
              width: "100%",
              padding: "14px 18px",
              borderRadius: 8,
              border: `1px solid rgba(201,168,76,0.35)`,
              background: `linear-gradient(135deg,${GOLD_D},${GOLD})`,
              color: "#0D1A00",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
              marginBottom: 10,
              fontFamily: GEO,
            }}
          >
            ⚙ Painel Admin
          </button>
          <button
            onClick={async () => {
              setLoading(true);
              if (pendAccs.length === 1) {
                await connect(pendU, pendAccs[0], pendAccs);
              } else {
                setPhase("selectacc");
              }
              setLoading(false);
            }}
            disabled={loading}
            style={{
              width: "100%",
              padding: "14px 18px",
              borderRadius: 8,
              border: `1px solid rgba(201,168,76,0.25)`,
              background: "rgba(201,168,76,0.05)",
              color: th.txt,
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
              marginBottom: 10,
              fontFamily: GEO,
              opacity: loading ? 0.7 : 1,
            }}
          >
            👤 Minhas Contas
          </button>
          <button
            onClick={() => setPhase("login")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: th.t2,
              fontSize: 12,
              marginTop: 8,
              fontFamily: GEO,
            }}
          >
            ← Voltar
          </button>
        </div>
      </div>
    );

  if (phase === "selectacc")
    return (
      <div style={wrap}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${bgSrc})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.25,
          }}
        />
        <div style={card}>
          <img
            src={logoSrc}
            alt="Fortuny One"
            style={{ height: 90, marginBottom: 28, objectFit: "contain" }}
          />
          <h2
            style={{
              margin: "0 0 6px",
              fontSize: 18,
              fontWeight: 700,
              color: th.acc,
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            Selecionar Conta
          </h2>
          <p style={{ color: th.t2, fontSize: 13, margin: "0 0 24px" }}>
            Olá, {pendU?.name}. Escolha o ambiente:
          </p>
          <Err msg={err} />
          {pendAccs.map((acc) => (
            <button
              key={acc.id}
              onClick={async () => {
                setLoading(true);
                await connect(pendU, acc, pendAccs);
                setLoading(false);
              }}
              disabled={loading}
              style={{
                width: "100%",
                padding: "14px 18px",
                borderRadius: 8,
                border: `1px solid rgba(201,168,76,0.25)`,
                background: "rgba(201,168,76,0.05)",
                color: th.txt,
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                marginBottom: 10,
                textAlign: "left",
                display: "flex",
                alignItems: "center",
                gap: 14,
                fontFamily: GEO,
                opacity: loading ? 0.7 : 1,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: `linear-gradient(135deg,${GOLD_D},${GOLD})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#0D1A00",
                  fontSize: 14,
                  fontWeight: 700,
                  flexShrink: 0,
                }}
              >
                {acc.name[0]}
              </div>
              <div>
                <div style={{ fontWeight: 700, color: th.acc }}>{acc.name}</div>
                <div style={{ fontSize: 11, color: th.t2, fontWeight: 400 }}>
                  {acc.label} · {acc.projectId}
                </div>
              </div>
            </button>
          ))}
          <button
            onClick={() => setPhase("login")}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: th.t2,
              fontSize: 12,
              marginTop: 8,
              fontFamily: GEO,
            }}
          >
            ← Voltar
          </button>
        </div>
      </div>
    );

  if (phase === "newpw")
    return (
      <div style={wrap}>
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url(${bgSrc})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.25,
          }}
        />
        <div style={card}>
          <img
            src={logoSrc}
            alt="Fortuny One"
            style={{ height: 90, marginBottom: 28, objectFit: "contain" }}
          />
          <h2
            style={{
              margin: "0 0 6px",
              fontSize: 18,
              fontWeight: 700,
              color: th.acc,
              letterSpacing: "1px",
              textTransform: "uppercase",
            }}
          >
            Criar Senha
          </h2>
          <p style={{ color: th.t2, fontSize: 13, margin: "0 0 24px" }}>
            Olá, {pendU?.name}. Defina sua senha pessoal.
          </p>
          <Err msg={err} />
          <input
            type="password"
            placeholder="Nova senha (mín. 6 caracteres)"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && confirmNewPw()}
            style={sI}
          />
          <Btn
            onClick={confirmNewPw}
            disabled={loading}
            style={{ width: "100%", padding: "13px", fontSize: 15 }}
          >
            {loading ? "Salvando..." : "Confirmar Senha"}
          </Btn>
        </div>
      </div>
    );

  return (
    <div style={wrap}>
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage: `url(${bgSrc})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: dark ? 0.3 : 0.18,
        }}
      />
      <div style={card}>
        <img
          src={logoSrc}
          alt="Fortuny One"
          style={{ height: 110, marginBottom: 16, objectFit: "contain" }}
        />
        <div
          style={{
            width: 40,
            height: 1,
            background: `linear-gradient(90deg,transparent,${GOLD},transparent)`,
            margin: "0 auto 24px",
          }}
        />
        <Err msg={err} />
        {/* Google Sign-In */}
        <button
          onClick={doGoogleLogin}
          disabled={loading}
          style={{
            width: "100%",
            padding: "13px",
            borderRadius: 8,
            border: `1px solid rgba(201,168,76,0.3)`,
            background: dark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.9)",
            color: dark ? "#F0E6C8" : "#1A1209",
            fontSize: 14,
            fontWeight: 600,
            cursor: loading ? "default" : "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 10,
            marginBottom: 16,
            fontFamily: GEO,
            opacity: loading ? 0.7 : 1,
            backdropFilter: "blur(4px)",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18">
            <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z"/>
            <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2a4.8 4.8 0 0 1-7.18-2.54H1.83v2.07A8 8 0 0 0 8.98 17z"/>
            <path fill="#FBBC05" d="M4.5 10.52a4.8 4.8 0 0 1 0-3.04V5.41H1.83a8 8 0 0 0 0 7.18z"/>
            <path fill="#EA4335" d="M8.98 4.18c1.17 0 2.23.4 3.06 1.2l2.3-2.3A8 8 0 0 0 1.83 5.4L4.5 7.49a4.77 4.77 0 0 1 4.48-3.31z"/>
          </svg>
          {loading ? "Aguarde..." : "Entrar com Google"}
        </button>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 16,
          }}
        >
          <div style={{ flex: 1, height: 1, background: `rgba(201,168,76,0.2)` }} />
          <span style={{ fontSize: 11, color: th.t2, fontFamily: GEO }}>ou</span>
          <div style={{ flex: 1, height: 1, background: `rgba(201,168,76,0.2)` }} />
        </div>
        <p
          style={{
            color: th.t2,
            fontSize: 11,
            margin: "0 0 14px",
            letterSpacing: "0.5px",
          }}
        >
          CPF e senha
        </p>
        <input
          placeholder="CPF"
          value={fmtCPF(cpf)}
          onChange={(e) => setCpf(e.target.value)}
          maxLength={14}
          style={sI}
        />
        <input
          type="password"
          placeholder="Senha"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && doLogin()}
          style={sI}
        />
        <Btn
          onClick={doLogin}
          disabled={loading}
          style={{
            width: "100%",
            padding: "13px",
            fontSize: 15,
            marginBottom: 20,
          }}
        >
          {loading ? "Entrando..." : "Entrar"}
        </Btn>
        <button
          onClick={() => setDark((d) => !d)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto",
          }}
        >
          <img
            src={dark ? A.icL : A.icD}
            alt="tema"
            style={{ width: 24, height: 24, objectFit: "contain" }}
          />
        </button>
      </div>
    </div>
  );
}

// ── FinancialApp ──────────────────────────────────────────────
function FinancialApp({
  dark,
  setDark,
  db,
  adminDb,
  user,
  account,
  allUserAccs,
  onLogout,
  onSwitchAccount,
  initPgColors,
}) {
  const th = TH(dark);
  const [hidden, setHidden] = useState(false);
  const [ready, setReady] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [stMsg, setStMsg] = useState({ msg: "", type: "" });
  const [txs, setTxs] = useState([]);
  const [recs, setRecs] = useState([]);
  const [emps, setEmps] = useState([]);
  const [fins, setFins] = useState([]);
  const [meta, setMeta] = useState({ saldoTotal: 0, lastAccumulatedYM: "" });
  const [expCats, setExpCats] = useState(DEF_EXP);
  const [incCats, setIncCats] = useState(DEF_INC);
  const [appUsers, setAppUsers] = useState([]);
  const [uc, setUc] = useState({});
  const [ub, setUb] = useState({});
  const [pgs, setPgs] = useState({});
  const [pgColors, setPgColors] = useState(initPgColors || {});
  const allCats = useMemo(() => [...expCats, ...incCats], [expCats, incCats]);
  const isMobile = window.innerWidth < 700;
  const [loadedMonths, setLoadedMonths] = useState(new Set());
  const [allMonthsLoaded, setAllMonthsLoaded] = useState(false);
  const [sectionsLoaded, setSectionsLoaded] = useState({
    recs: false,
    emps: false,
    fins: false,
  });

  // Navigation
  const [view, setView] = useState("home");
  const [subView, setSubView] = useState(null); // recurring | loans | financing
  const actualView = subView || view;

  useEffect(() => {
    setView("home");
    setSubView(null);
  }, [account.id]);

  const [filterMode, setFilterMode] = useState("month");
  const [selMonth, setSelMonth] = useState(now.getMonth());
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [dateFrom, setDateFrom] = useState(
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-01`,
  );
  const [dateTo, setDateTo] = useState(ymd(now));
  const [dashU, setDashU] = useState([]);
  const [dashC, setDashC] = useState([]);
  const [fltU, setFltU] = useState([]);
  const [fltC, setFltC] = useState([]);
  const [fltS, setFltS] = useState("");
  const [fltSort, setFltSort] = useState("date_desc");
  const [ccU, setCcU] = useState([]);
  const [ccC, setCcC] = useState([]);
  const [ccS, setCcS] = useState("");
  const [ccSort, setCcSort] = useState("date_desc");
  const [recS, setRecS] = useState("");
  const [recSort, setRecSort] = useState("alpha_asc");
  const [empS, setEmpS] = useState("");
  const [empSort, setEmpSort] = useState("date_desc");
  const [finS, setFinS] = useState("");
  const [finSort, setFinSort] = useState("date_desc");
  const [ovdS, setOvdS] = useState("");
  const [ovdSort, setOvdSort] = useState("date_asc");
  const [fatNav, setFatNav] = useState(null);
  const [selIds, setSelIds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showRecForm, setShowRecForm] = useState(false);
  const [showEmpForm, setShowEmpForm] = useState(false);
  const [showFinForm, setShowFinForm] = useState(false);
  const [showPay, setShowPay] = useState(null);
  const [showUnpay, setShowUnpay] = useState(null);
  const [showDetail, setShowDetail] = useState(null);
  const [showBulkPay, setShowBulkPay] = useState(false);
  const [showBulkDel, setShowBulkDel] = useState(false);
  const [showCcInst, setShowCcInst] = useState(null);
  const [showAccMenu, setShowAccMenu] = useState(false);
  const [editSaldo, setEditSaldo] = useState(false);
  const [saldoInp, setSaldoInp] = useState("");
  const [infoTip, setInfoTip] = useState(null);
  const [form, setForm] = useState(null);
  const [recForm, setRecForm] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editRecId, setEditRecId] = useState(null);
  const [formErr, setFormErr] = useState("");
  const [recErr, setRecErr] = useState("");
  const [empErr, setEmpErr] = useState("");
  const [finErr, setFinErr] = useState("");
  const [showArchEmp, setShowArchEmp] = useState(false);
  const [showArchFin, setShowArchFin] = useState(false);
  const chartRef = useRef(null),
    pieRef = useRef(null),
    cInst = useRef(null),
    pInst = useRef(null);

  const sc = {
    background: th.sur,
    border: `1px solid ${th.bdr}`,
    borderRadius: 10,
    padding: "16px",
  };
  const sI = {
    width: "100%",
    padding: "9px 11px",
    borderRadius: 6,
    border: `1px solid ${th.ib}`,
    fontSize: 13,
    boxSizing: "border-box",
    background: th.inp,
    color: th.txt,
    outline: "none",
    fontFamily: GEO,
  };
  const sL = {
    fontSize: 10,
    color: th.t2,
    display: "block",
    marginBottom: 5,
    fontFamily: GEO,
    textTransform: "uppercase",
    letterSpacing: "0.6px",
  };
  const H = (x) => (hidden ? "•••••" : x);
  const blankTx = () => ({
    type: "expense",
    desc: "",
    amount: "",
    category: expCats[0]?.id || "moradia",
    user: appUsers[0] || "",
    date: ymd(now),
    paid: false,
    payMethod: "Débito",
    payDate: "",
    payTiming: "",
    payAmount: "",
    obs: "",
    recurring: false,
    recurringId: "",
    creditBilled: false,
  });
  const blankRec = () => ({
    type: "expense",
    desc: "",
    amount: "",
    category: expCats[0]?.id || "moradia",
    user: appUsers[0] || "",
    startYM: ym(now),
    active: true,
    obs: "",
  });

  function toast(msg, type = "ok", dur = 3000) {
    setStMsg({ msg, type });
    setTimeout(() => setStMsg({ msg: "", type: "" }), dur);
  }
  function navTo(v, sub = null) {
    setView(sub ? v : "home");
    setSubView(sub ? sub : v === "home" ? null : v);
    if (!sub && v !== "home") setView(v);
  }

  // Fix navTo: simpler approach
  function goTo(v) {
    setSubView(null);
    setView(v);
  }
  function goSub(s) {
    setSubView(s);
    setView("fixed");
  }

  useEffect(() => {
    document.body.style.background = th.bg;
    document.body.style.margin = "0";
  }, [dark]);

  async function loadConfig() {
    setSyncing(true);
    try {
      const [lm, lc] = await Promise.all([
        db ? fsOne(db, "meta", "saldo") : Promise.resolve(null),
        db ? fsOne(db, "config", "main") : Promise.resolve(null),
      ]);
      if (lc) {
        if (lc.expCats) setExpCats(lc.expCats);
        if (lc.incCats) setIncCats(lc.incCats);
        if (lc.users && lc.users.length) {
          setAppUsers(lc.users);
          setUc(lc.userColors || {});
          setUb(lc.userBg || {});
        } else {
          setAppUsers(DEF_NEW_USERS.map((u) => u.name));
          setUc(
            Object.fromEntries(DEF_NEW_USERS.map((u) => [u.name, u.color])),
          );
          setUb(Object.fromEntries(DEF_NEW_USERS.map((u) => [u.name, u.bg])));
        }
        if (lc.pageCfg) setPgs(lc.pageCfg);
      } else {
        setAppUsers(DEF_NEW_USERS.map((u) => u.name));
        setUc(Object.fromEntries(DEF_NEW_USERS.map((u) => [u.name, u.color])));
        setUb(Object.fromEntries(DEF_NEW_USERS.map((u) => [u.name, u.bg])));
      }
      setMeta(lm || { saldoTotal: 0, lastAccumulatedYM: "" });
    } catch (e) {
      console.error(e);
      toast("Erro ao carregar configuração.", "warn", 7000);
    }
    setSyncing(false);
    setReady(true);
  }
  async function loadMonth(mYM) {
    if (!db || loadedMonths.has(mYM)) return;
    setSyncing(true);
    try {
      const lt = await fsByMonth(db, mYM);
      setTxs((prev) => {
        const base = prev.filter((t) => !t.isFatura);
        const existIds = new Set(base.map((t) => t.id));
        const merged = [...base, ...lt.filter((t) => !existIds.has(t.id))];
        return rebuildFat(merged);
      });
      setLoadedMonths((prev) => new Set([...prev, mYM]));
      const cnt = lt.filter((t) => !t.isFatura).length;
      if (cnt > 0) toast(`${cnt} lançamento${cnt !== 1 ? "s" : ""} carregados`);
    } catch (e) {
      console.error(e);
      toast("Erro ao carregar lançamentos.", "warn", 7000);
    }
    setSyncing(false);
  }
  async function loadAllMonths() {
    if (!db || allMonthsLoaded) return;
    setSyncing(true);
    try {
      const lt = await fsAll(db, "lancamentos");
      setTxs(rebuildFat(lt));
      setLoadedMonths(new Set(lt.map((t) => t.date.slice(0, 7))));
      setAllMonthsLoaded(true);
      const cnt = lt.filter((t) => !t.isFatura).length;
      toast(`${cnt} lançamento${cnt !== 1 ? "s" : ""} carregados`);
    } catch (e) {
      console.error(e);
      toast("Erro ao carregar dados.", "warn", 7000);
    }
    setSyncing(false);
  }
  async function loadSection(sec) {
    if (!db || sectionsLoaded[sec]) return;
    setSyncing(true);
    try {
      if (sec === "recs") {
        const lr = await fsAll(db, "contasfixas");
        setRecs(lr);
      } else if (sec === "emps") {
        const le = await fsAll(db, "emprestimos");
        setEmps(le || []);
      } else if (sec === "fins") {
        const lf = await fsAll(db, "financiamentos");
        setFins(lf || []);
      }
      setSectionsLoaded((p) => ({ ...p, [sec]: true }));
    } catch (e) {
      console.error(e);
      toast("Erro ao carregar seção.", "warn", 7000);
    }
    setSyncing(false);
  }
  useEffect(() => {
    loadConfig();
  }, [db]);
  useEffect(() => {
    if (!ready) return;
    if (actualView === "transactions") loadMonth(selYM);
    if (actualView === "credit") {
      loadMonth(fatYM);
      loadMonth(addM(fatYM, -1));
      loadMonth(addM(fatYM, 1));
    }
    if (actualView === "overdue" || actualView === "dashboard") loadAllMonths();
    if (
      actualView === "recurring" ||
      actualView === "loans" ||
      actualView === "financing"
    )
      loadSection("recs");
    if (actualView === "loans") loadSection("emps");
    if (actualView === "financing") loadSection("fins");
  }, [actualView, ready]);
  useEffect(() => {
    if (!ready || !db) return;
    const cur = ym(now);
    if (meta.lastAccumulatedYM === cur) return;
    let py = now.getFullYear(),
      pm = now.getMonth() - 1;
    if (pm < 0) {
      pm = 11;
      py--;
    }
    const prevYM = `${py}-${pad(pm + 1)}`;
    if (!loadedMonths.has(prevYM) && !allMonthsLoaded) return;
    const prev = txs.filter((t) => t.date.slice(0, 7) === prevYM);
    const inc = prev
      .filter((t) => t.type === "income" && !t.isFatura)
      .reduce((a, t) => a + t.amount, 0);
    const exp = prev
      .filter((t) => t.type === "expense" && !t.creditBilled && !t.isFatura)
      .reduce((a, t) => a + t.amount, 0);
    const fat = prev
      .filter((t) => t.isFatura)
      .reduce((a, t) => a + t.amount, 0);
    const nm = {
      saldoTotal: (meta.saldoTotal || 0) + inc - exp - fat,
      lastAccumulatedYM: cur,
    };
    setMeta(nm);
    fsSave(db, "meta", "saldo", nm).catch(console.error);
  }, [ready, txs, loadedMonths, allMonthsLoaded]);

  const selYM = `${selYear}-${pad(selMonth + 1)}`;
  const pF = (t) => {
    const d = new Date(t.date + "T12:00");
    if (filterMode === "month")
      return d.getMonth() === selMonth && d.getFullYear() === selYear;
    return t.date >= dateFrom && t.date <= dateTo;
  };
  function chMonth(dir) {
    let m = selMonth + dir,
      y = selYear;
    if (m < 0) {
      m = 11;
      y--;
    }
    if (m > 11) {
      m = 0;
      y++;
    }
    setSelMonth(m);
    setSelYear(y);
  }

  const pTxs = txs.filter(pF);
  const dFlt = pTxs.filter(
    (t) =>
      (dashU.length === 0 || dashU.includes(t.user)) &&
      (dashC.length === 0 || dashC.includes(t.category)),
  );
  const totInc = dFlt
    .filter((t) => t.type === "income" && !t.isFatura)
    .reduce((a, t) => a + t.amount, 0);
  const totExp = dFlt
    .filter((t) => t.type === "expense" && !t.creditBilled && !t.isFatura)
    .reduce((a, t) => a + t.amount, 0);
  const fatP = dFlt.filter((t) => t.isFatura);
  const fatAmt = fatP.reduce((a, t) => a + t.amount, 0);
  const pagPost = dFlt
    .filter((t) => t.creditBilled && t.type === "expense")
    .reduce((a, t) => a + t.amount, 0);
  const totSaidas = totExp + fatAmt;
  const pagoDb =
    dFlt
      .filter(
        (t) =>
          t.paid &&
          t.type === "expense" &&
          !t.creditBilled &&
          !t.isFatura &&
          t.payMethod === "Débito",
      )
      .reduce((a, t) => a + t.amount, 0) +
    fatP.filter((t) => t.paid).reduce((a, t) => a + t.amount, 0);
  const totPend =
    dFlt
      .filter(
        (t) =>
          !t.paid && t.type === "expense" && !t.creditBilled && !t.isFatura,
      )
      .reduce((a, t) => a + t.amount, 0) +
    fatP.filter((t) => !t.paid).reduce((a, t) => a + t.amount, 0);
  const balP = totInc - totExp - fatAmt;
  const saldoPreview = (meta.saldoTotal || 0) + balP;
  const byCat = expCats
    .map((c) => ({
      ...c,
      total: dFlt
        .filter(
          (t) =>
            t.type === "expense" &&
            t.category === c.id &&
            !t.creditBilled &&
            !t.isFatura,
        )
        .reduce((a, t) => a + t.amount, 0),
    }))
    .filter((c) => c.total > 0);
  const fatYM = fatNav || selYM;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!ready) return;
    if (actualView === "transactions") loadMonth(selYM);
    if (actualView === "credit") {
      loadMonth(fatYM);
      loadMonth(addM(fatYM, -1));
      loadMonth(addM(fatYM, 1));
    }
  }, [selYM, fatYM]);
  const fatFech = txs.filter(
    (t) => t.creditBilled && t.ccInst?.some((p) => p.bym === fatYM),
  );
  const fatTx = txs.find((t) => t.isFatura && t.date.slice(0, 7) === fatYM);
  const fatTotal = fatTx?.amount || 0;
  const fatAberta = txs.filter(
    (t) =>
      t.creditBilled &&
      t.date.slice(0, 7) === fatYM &&
      t.ccInst?.every((p) => p.bym !== fatYM),
  );
  const curFatTotal =
    txs.find((t) => t.isFatura && t.date.slice(0, 7) === selYM)?.amount || 0;
  const nxtFatTotal =
    txs.find((t) => t.isFatura && t.date.slice(0, 7) === addM(selYM, 1))
      ?.amount || 0;
  const curFatItems = txs.filter(
    (t) => t.creditBilled && t.ccInst?.some((p) => p.bym === selYM),
  );
  const nxtFatItems = txs.filter(
    (t) => t.creditBilled && t.ccInst?.some((p) => p.bym === addM(selYM, 1)),
  );
  const fatPeriod = txs.filter((t) => t.isFatura && pF(t));
  const rawFltTxs = txs
    .filter(pF)
    .filter((t) => !t.isFatura)
    .filter(
      (t) =>
        (fltU.length === 0 || fltU.includes(t.user)) &&
        (fltC.length === 0 || fltC.includes(t.category)),
    )
    .filter(
      (t) => !fltS || (t.desc || "").toLowerCase().includes(fltS.toLowerCase()),
    );
  const fltTxs = useMemo(
    () => sortItems(rawFltTxs, fltSort),
    [rawFltTxs.length, fltSort],
  );
  const curYM2 = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  const rawOvd = txs
    .filter(
      (t) =>
        t.type === "expense" &&
        !t.paid &&
        t.date.slice(0, 7) < curYM2 &&
        !t.creditBilled &&
        !t.isFatura,
    )
    .filter(
      (t) => !ovdS || (t.desc || "").toLowerCase().includes(ovdS.toLowerCase()),
    );
  const ovdItems = useMemo(
    () => sortItems(rawOvd, ovdSort),
    [rawOvd.length, ovdSort],
  );
  const selTxObjs = fltTxs.filter(
    (t) => selIds.includes(t.id) && t.type === "expense" && !t.creditBilled,
  );
  const allSel =
    fltTxs.length > 0 && fltTxs.every((t) => selIds.includes(t.id));
  const empSt = emps.map((e) => {
    const pi = e.items.filter((p) => p.paid),
      pe = e.items.filter((p) => !p.paid);
    return {
      ...e,
      paidCnt: pi.length,
      pendCnt: pe.length,
      paidVal: pi.reduce((a, p) => a + p.value, 0),
      pendVal: pe.reduce((a, p) => a + p.value, 0),
      nextDate: pe[0]?.date || null,
    };
  });
  const activeEmps = empSt.filter((e) => e.pendCnt > 0),
    archEmps = empSt.filter((e) => e.pendCnt === 0);
  const finSt = fins.map((f) => {
    const pi = f.items.filter((p) => p.paid),
      pe = f.items.filter((p) => !p.paid);
    return {
      ...f,
      paidCnt: pi.length,
      pendCnt: pe.length,
      paidVal: pi.reduce((a, p) => a + p.value, 0),
      pendVal: pe.reduce((a, p) => a + p.value, 0),
      nextDate: pe[0]?.date || null,
    };
  });
  const activeFins = finSt.filter((f) => f.pendCnt > 0),
    archFins = finSt.filter((f) => f.pendCnt === 0);
  const sortedRecs = useMemo(
    () =>
      sortItems(
        recs.filter(
          (r) =>
            !recS || (r.desc || "").toLowerCase().includes(recS.toLowerCase()),
        ),
        recSort,
      ),
    [recs.length, recS, recSort],
  );
  const sortedEmps = useMemo(
    () =>
      sortItems(
        activeEmps.filter(
          (e) =>
            !empS || (e.desc || "").toLowerCase().includes(empS.toLowerCase()),
        ),
        empSort,
      ),
    [activeEmps.length, empS, empSort],
  );
  const sortedFins = useMemo(
    () =>
      sortItems(
        activeFins.filter(
          (f) =>
            !finS || (f.desc || "").toLowerCase().includes(finS.toLowerCase()),
        ),
        finSort,
      ),
    [activeFins.length, finS, finSort],
  );
  const sortedFatFech = useMemo(
    () =>
      sortItems(
        fatFech.filter(
          (t) =>
            (ccU.length === 0 || ccU.includes(t.user)) &&
            (ccC.length === 0 || ccC.includes(t.category)) &&
            (!ccS || (t.desc || "").toLowerCase().includes(ccS.toLowerCase())),
        ),
        ccSort,
      ),
    [fatFech.length, ccU, ccC, ccS, ccSort],
  );
  const sortedFatAberta = useMemo(
    () =>
      sortItems(
        fatAberta.filter(
          (t) =>
            (ccU.length === 0 || ccU.includes(t.user)) &&
            (ccC.length === 0 || ccC.includes(t.category)) &&
            (!ccS || (t.desc || "").toLowerCase().includes(ccS.toLowerCase())),
        ),
        ccSort,
      ),
    [fatAberta.length, ccU, ccC, ccS, ccSort],
  );
  const vC = pgColors[actualView] || null;

  // Charts
  useEffect(() => {
    if (actualView !== "dashboard" || !ready) return;
    const cL = [],
      cI = [],
      cE = [],
      cP = [];
    for (let i = -5; i <= 1; i++) {
      let m = selMonth + i,
        y = selYear;
      if (m < 0) {
        m += 12;
        y--;
      }
      if (m > 11) {
        m -= 12;
        y++;
      }
      const mYM = `${y}-${pad(m + 1)}`;
      const mT = txs
        .filter((t) => {
          const d = new Date(t.date + "T12:00");
          return d.getMonth() === m && d.getFullYear() === y;
        })
        .filter((t) => dashU.length === 0 || dashU.includes(t.user));
      const fatM = txs.find((t) => t.isFatura && t.date.slice(0, 7) === mYM);
      cL.push(MONTHS[m] + (i === 1 ? "→" : ""));
      cI.push(
        mT
          .filter((t) => t.type === "income" && !t.isFatura)
          .reduce((a, t) => a + t.amount, 0),
      );
      cE.push(
        mT
          .filter((t) => t.type === "expense" && !t.creditBilled && !t.isFatura)
          .reduce((a, t) => a + t.amount, 0) + (fatM?.amount || 0),
      );
      cP.push(
        mT
          .filter(
            (t) =>
              t.paid &&
              t.type === "expense" &&
              !t.creditBilled &&
              !t.isFatura &&
              t.payMethod === "Débito",
          )
          .reduce((a, t) => a + t.amount, 0) +
          mT
            .filter((t) => t.isFatura && t.paid)
            .reduce((a, t) => a + t.amount, 0),
      );
    }
    const build = () =>
      setTimeout(() => {
        if (chartRef.current) {
          if (cInst.current) cInst.current.destroy();
          cInst.current = new window.Chart(chartRef.current, {
            type: "bar",
            data: {
              labels: cL,
              datasets: [
                {
                  label: "Entradas",
                  data: cI,
                  backgroundColor: "rgba(90,138,58,0.7)",
                  borderColor: "#5A8A3A",
                  borderWidth: 1,
                  borderRadius: 3,
                  order: 2,
                },
                {
                  label: "Saídas",
                  data: cE,
                  backgroundColor: "rgba(138,58,58,0.7)",
                  borderColor: "#8A3A3A",
                  borderWidth: 1,
                  borderRadius: 3,
                  order: 3,
                },
                {
                  label: "Pago (Débito)",
                  data: cP,
                  type: "line",
                  borderColor: GOLD,
                  backgroundColor: "rgba(201,168,76,0.07)",
                  tension: 0.35,
                  pointRadius: 4,
                  pointBackgroundColor: GOLD,
                  fill: true,
                  borderWidth: 2,
                  order: 1,
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              interaction: { mode: "index", intersect: false },
              plugins: {
                legend: {
                  display: true,
                  position: "bottom",
                  labels: {
                    boxWidth: 10,
                    font: { size: 11, family: GEO },
                    padding: 14,
                    color: th.txt,
                  },
                },
              },
              scales: {
                x: {
                  ticks: { color: th.t2, font: { size: 11 } },
                  grid: { display: false },
                },
                y: {
                  beginAtZero: true,
                  ticks: {
                    color: th.t2,
                    font: { size: 11 },
                    callback: (v) => "R$" + v.toLocaleString("pt-BR"),
                  },
                  grid: {
                    color: dark
                      ? "rgba(201,168,76,0.05)"
                      : "rgba(154,122,46,0.05)",
                  },
                },
              },
            },
          });
        }
        if (pieRef.current && byCat.length) {
          if (pInst.current) pInst.current.destroy();
          pInst.current = new window.Chart(pieRef.current, {
            type: "doughnut",
            data: {
              labels: byCat.map((c) => c.label),
              datasets: [
                {
                  data: byCat.map((c) => c.total),
                  backgroundColor: byCat.map((c) => c.color),
                  borderWidth: 2,
                  borderColor: th.sur,
                },
              ],
            },
            options: {
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: false } },
            },
          });
        }
      }, 120);
    if (!window.Chart) {
      const s = document.createElement("script");
      s.src =
        "https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.js";
      s.onload = build;
      document.body.appendChild(s);
    } else build();
  }, [actualView, txs, selMonth, selYear, ready, dashU, dark]);

  // CRUD helpers
  async function togglePaid(tx) {
    if (!tx.paid) setShowPay(tx);
    else setShowUnpay(tx);
  }
  async function confirmPay(tx, pi) {
    if (
      pi.payMethod === "Crédito" &&
      tx.type === "expense" &&
      !tx.creditBilled &&
      !tx.isFatura
    ) {
      setShowPay(null);
      setShowCcInst(tx);
      return;
    }
    const up = txs.map((t) =>
      t.id === tx.id ? { ...t, paid: true, ...pi } : t,
    );
    setTxs(up);
    setShowPay(null);
    if (db)
      await fsSave(
        db,
        "lancamentos",
        tx.id,
        up.find((t) => t.id === tx.id),
      );
    toast("Registrado.");
  }
  async function confirmCcInst(src, { parcelas }) {
    const upd = {
      ...src,
      paid: true,
      payMethod: "Crédito",
      payDate: src.date,
      payTiming: "ontime",
      payAmount: src.amount,
      creditBilled: true,
      ccGroupId: gid(),
      ccInst: parcelas,
    };
    const newTxs = txs.map((t) => (t.id === src.id ? upd : t));
    const rb = rebuildFat(newTxs);
    setTxs(rb);
    setShowCcInst(null);
    if (db) {
      await fsSave(db, "lancamentos", src.id, upd);
      await Promise.all(
        rb
          .filter((t) => t.isFatura)
          .map((f) => fsSave(db, "lancamentos", f.id, f)),
      );
    }
    toast("Crédito registrado.");
  }
  async function confirmUnpay(t) {
    if (t.isFatura) {
      const up = txs.map((x) =>
        x.id === t.id
          ? {
              ...x,
              paid: false,
              payMethod: "",
              payDate: "",
              payTiming: "",
              payAmount: "",
            }
          : x,
      );
      setTxs(up);
      setShowUnpay(null);
      if (db)
        await fsSave(
          db,
          "lancamentos",
          t.id,
          up.find((x) => x.id === t.id),
        );
      toast("Fatura reaberta.");
      return;
    }
    if (t.creditBilled) {
      const up = txs.map((x) =>
        x.id === t.id
          ? {
              ...x,
              paid: false,
              payMethod: "",
              payDate: "",
              payTiming: "",
              payAmount: "",
              creditBilled: false,
              ccGroupId: "",
              ccInst: null,
            }
          : x,
      );
      const rb = rebuildFat(up);
      setTxs(rb);
      setShowUnpay(null);
      if (db) {
        const old = txs.filter((x) => x.isFatura);
        await Promise.all(old.map((f) => fsDel(db, "lancamentos", f.id)));
        await Promise.all(
          rb
            .filter((x) => x.isFatura)
            .map((f) => fsSave(db, "lancamentos", f.id, f)),
        );
        await fsSave(
          db,
          "lancamentos",
          t.id,
          rb.find((x) => x.id === t.id),
        );
      }
      toast("Crédito removido.");
      return;
    }
    const up = txs.map((x) =>
      x.id === t.id
        ? {
            ...x,
            paid: false,
            payMethod: "",
            payDate: "",
            payTiming: "",
            payAmount: "",
          }
        : x,
    );
    setTxs(up);
    setShowUnpay(null);
    if (db)
      await fsSave(
        db,
        "lancamentos",
        t.id,
        up.find((x) => x.id === t.id),
      );
    toast("Pagamento removido.");
  }
  const editTx = useCallback((t) => {
    if (t.isFatura) return;
    setForm({ ...t, amount: String(t.amount) });
    setEditId(t.id);
    setFormErr("");
    setShowForm(true);
  }, []);
  const delTx = useCallback(
    async (id) => {
      const t = txs.find((x) => x.id === id);
      if (!t) return;
      if (t.isFatura) {
        toast("Remova as compras no crédito.", "warn");
        return;
      }
      let newTxs;
      if (t.creditBilled) {
        const flt = txs.filter((x) => x.id !== id);
        newTxs = rebuildFat(flt);
        if (db) {
          const old = txs.filter((x) => x.isFatura);
          await Promise.all(old.map((f) => fsDel(db, "lancamentos", f.id)));
          await Promise.all(
            newTxs
              .filter((x) => x.isFatura)
              .map((f) => fsSave(db, "lancamentos", f.id, f)),
          );
        }
      } else newTxs = txs.filter((x) => x.id !== id);
      setTxs(newTxs);
      setSelIds((p) => p.filter((x) => x !== id));
      if (db) await fsDel(db, "lancamentos", id);
      toast("Removido.");
    },
    [db, txs],
  );
  const delRec = useCallback(
    async (id) => {
      const curYM = ym(now);
      const lk = txs.filter(
        (t) => t.recurringId === id && t.date.slice(0, 7) >= curYM,
      );
      setRecs((p) => p.filter((r) => r.id !== id));
      setTxs((p) =>
        p.filter((t) => !(t.recurringId === id && t.date.slice(0, 7) >= curYM)),
      );
      if (db) {
        await fsDel(db, "contasfixas", id);
        await Promise.all(lk.map((t) => fsDel(db, "lancamentos", t.id)));
      }
      toast("Conta fixa removida.");
    },
    [db, txs],
  );
  async function saveTx() {
    const f2 = form;
    if (!f2) return;
    const e = vld([
      { val: f2.desc, label: "Descrição" },
      { val: f2.amount, label: "Valor" },
    ]);
    if (e) {
      setFormErr(e);
      return;
    }
    const id = editId || gid(),
      data = {
        ...f2,
        id,
        amount: Number(f2.amount),
        payAmount: Number(f2.payAmount) || Number(f2.amount),
      };
    const newTxs = editId
      ? txs.map((t) => (t.id === editId ? data : t))
      : [...txs, data];
    setTxs(newTxs);
    setShowForm(false);
    setEditId(null);
    setForm(null);
    setFormErr("");
    if (db) await fsSave(db, "lancamentos", id, data);
    toast("Salvo.");
  }
  async function saveRec() {
    const f2 = recForm;
    if (!f2) return;
    const e = vld([
      { val: f2.desc, label: "Descrição" },
      { val: f2.amount, label: "Valor" },
    ]);
    if (e) {
      setRecErr(e);
      return;
    }
    const amt = Number(f2.amount);
    if (editRecId) {
      const nr = recs.map((r) =>
        r.id === editRecId ? { ...r, ...f2, amount: amt } : r,
      );
      const curYM = ym(now),
        nt = txs.map((t) =>
          t.recurringId === editRecId && t.date.slice(0, 7) >= curYM
            ? { ...t, amount: amt, obs: f2.obs || "" }
            : t,
        );
      setRecs(nr);
      setTxs(nt);
      if (db) {
        await fsSave(
          db,
          "contasfixas",
          editRecId,
          nr.find((r) => r.id === editRecId),
        );
        await Promise.all(
          nt
            .filter(
              (t) => t.recurringId === editRecId && t.date.slice(0, 7) >= curYM,
            )
            .map((t) => fsSave(db, "lancamentos", t.id, t)),
        );
      }
      toast("Conta fixa atualizada.");
    } else {
      const newId = gid(),
        nr = { ...f2, id: newId, amount: amt };
      const [sy, sm] = f2.startYM.split("-").map(Number);
      const inst = Array.from({ length: 13 }, (_, i) => {
        let m = sm - 1 + i,
          y = sy;
        if (m > 11) {
          m -= 12;
          y++;
        }
        return {
          id: gid(),
          type: nr.type,
          desc: nr.desc,
          amount: amt,
          category: nr.category,
          user: nr.user,
          date: `${y}-${pad(m + 1)}-01`,
          paid: false,
          payMethod: "Débito",
          payDate: "",
          payTiming: "",
          payAmount: "",
          obs: nr.obs || "",
          recurring: true,
          recurringId: newId,
          creditBilled: false,
        };
      });
      setRecs((p) => [...p, nr]);
      setTxs((p) => [...p, ...inst]);
      if (db) {
        await fsSave(db, "contasfixas", newId, nr);
        await Promise.all(inst.map((t) => fsSave(db, "lancamentos", t.id, t)));
      }
      toast("Conta fixa criada.");
    }
    setShowRecForm(false);
    setEditRecId(null);
    setRecForm(null);
    setRecErr("");
  }
  async function saveEmpData(d) {
    const iB = d.direction !== "lend";
    const main = {
      id: gid(),
      type: iB ? "income" : "expense",
      desc: `${iB ? "Empréstimo" : "Emprestado"}: ${d.desc}`,
      amount: d.totalOriginal,
      category: iB ? "outros_inc" : "outros_exp",
      user: d.user,
      date: d.entryDate,
      paid: true,
      payMethod: "Débito",
      payDate: d.entryDate,
      payTiming: "ontime",
      payAmount: d.totalOriginal,
      obs: "",
      recurring: false,
      recurringId: "",
      creditBilled: false,
      empId: d.id,
    };
    const inst = d.items.map((p, i) => ({
      id: gid(),
      type: iB ? "expense" : "income",
      desc: `${d.desc} — ${iB ? "devolução" : "recebimento"} ${i + 1}/${d.parcelas}`,
      amount: p.value,
      category: iB ? "outros_exp" : "outros_inc",
      user: d.user,
      date: p.date,
      paid: false,
      payMethod: "Débito",
      payDate: "",
      payTiming: "",
      payAmount: "",
      obs: "",
      recurring: false,
      recurringId: "",
      creditBilled: false,
      empId: d.id,
    }));
    setTxs((p) => [...p, main, ...inst]);
    setEmps((p) => [...p, d]);
    if (db) {
      await fsSave(db, "emprestimos", d.id, d);
      await Promise.all(
        [main, ...inst].map((t) => fsSave(db, "lancamentos", t.id, t)),
      );
    }
    setShowEmpForm(false);
    toast("Empréstimo registrado.");
  }
  async function saveFinData(d) {
    const inst = d.items.map((p, i) => ({
      id: gid(),
      type: "expense",
      desc: `${d.desc} — parcela ${i + 1}/${d.parcelas}`,
      amount: p.value,
      category: d.category,
      user: d.user,
      date: p.date,
      paid: false,
      payMethod: "Débito",
      payDate: "",
      payTiming: "",
      payAmount: "",
      obs: "",
      recurring: false,
      recurringId: "",
      creditBilled: false,
      finId: d.id,
    }));
    setTxs((p) => [...p, ...inst]);
    setFins((p) => [...p, d]);
    if (db) {
      await fsSave(db, "financiamentos", d.id, d);
      await Promise.all(inst.map((t) => fsSave(db, "lancamentos", t.id, t)));
    }
    setShowFinForm(false);
    toast("Financiamento registrado.");
  }
  async function toggleEmpP(empId, idx) {
    const emp = emps.find((e) => e.id === empId);
    if (!emp) return;
    const p = emp.items[idx];
    if (!p.paid)
      setShowPay({
        id: `${empId}_${idx}`,
        desc: emp.desc,
        date: p.date,
        type: "expense",
        amount: p.value,
        paid: false,
        payMethod: "Débito",
        payDate: "",
        _empId: empId,
        _empIdx: idx,
      });
    else {
      const ni = emp.items.map((p2, i) =>
        i === idx
          ? {
              ...p2,
              paid: false,
              payMethod: "",
              payDate: "",
              payTiming: "",
              payAmount: "",
            }
          : p2,
      );
      const u = { ...emp, items: ni };
      setEmps((p) => p.map((e) => (e.id === empId ? u : e)));
      if (db) await fsSave(db, "emprestimos", empId, u);
      toast("Parcela reaberta.");
    }
  }
  async function payEmpP(empId, idx, pi) {
    const emp = emps.find((e) => e.id === empId);
    if (!emp) return;
    const ni = emp.items.map((p, i) =>
      i === idx ? { ...p, paid: true, ...pi } : p,
    );
    const u = { ...emp, items: ni };
    setEmps((p) => p.map((e) => (e.id === empId ? u : e)));
    setShowPay(null);
    if (db) await fsSave(db, "emprestimos", empId, u);
    toast("Parcela paga.");
  }
  async function delEmp(id) {
    const r = txs.filter((t) => t.empId === id);
    setEmps((p) => p.filter((e) => e.id !== id));
    setTxs((p) => p.filter((t) => t.empId !== id));
    if (db) {
      await fsDel(db, "emprestimos", id);
      await Promise.all(r.map((t) => fsDel(db, "lancamentos", t.id)));
    }
    toast("Empréstimo removido.");
  }
  async function toggleFinP(finId, idx) {
    const fin = fins.find((f) => f.id === finId);
    if (!fin) return;
    const p = fin.items[idx];
    if (!p.paid) {
      const lt = txs.find(
        (t) => t.finId === finId && t.desc.includes(`parcela ${idx + 1}/`),
      );
      setShowPay(
        lt || {
          id: `${finId}_${idx}`,
          desc: fin.desc,
          date: p.date,
          type: "expense",
          amount: p.value,
          paid: false,
          payMethod: "Débito",
          payDate: "",
          _finId: finId,
          _finIdx: idx,
        },
      );
    } else {
      const ni = fin.items.map((p2, i) =>
        i === idx ? { ...p2, paid: false } : p2,
      );
      const u = { ...fin, items: ni };
      setFins((p) => p.map((f) => (f.id === finId ? u : f)));
      const lt = txs.find(
        (t) => t.finId === finId && t.desc.includes(`parcela ${idx + 1}/`),
      );
      if (lt && db) {
        const ut = txs.map((t) =>
          t.id === lt.id
            ? {
                ...t,
                paid: false,
                payMethod: "",
                payDate: "",
                payTiming: "",
                payAmount: "",
              }
            : t,
        );
        setTxs(ut);
        await fsSave(
          db,
          "lancamentos",
          lt.id,
          ut.find((t) => t.id === lt.id),
        );
      }
      if (db) await fsSave(db, "financiamentos", finId, u);
      toast("Parcela reaberta.");
    }
  }
  async function payFinP(finId, idx, pi) {
    const fin = fins.find((f) => f.id === finId);
    if (!fin) return;
    const ni = fin.items.map((p, i) =>
      i === idx ? { ...p, paid: true, ...pi } : p,
    );
    const u = { ...fin, items: ni };
    setFins((p) => p.map((f) => (f.id === finId ? u : f)));
    const lt = txs.find(
      (t) => t.finId === finId && t.desc.includes(`parcela ${idx + 1}/`),
    );
    if (lt) {
      const ut = txs.map((t) =>
        t.id === lt.id ? { ...t, paid: true, ...pi } : t,
      );
      setTxs(ut);
      if (db)
        await fsSave(
          db,
          "lancamentos",
          lt.id,
          ut.find((t) => t.id === lt.id),
        );
    }
    setShowPay(null);
    if (db) await fsSave(db, "financiamentos", finId, u);
    toast("Parcela paga.");
  }
  async function delFin(id) {
    const r = txs.filter((t) => t.finId === id);
    setFins((p) => p.filter((f) => f.id !== id));
    setTxs((p) => p.filter((t) => t.finId !== id));
    if (db) {
      await fsDel(db, "financiamentos", id);
      await Promise.all(r.map((t) => fsDel(db, "lancamentos", t.id)));
    }
    toast("Financiamento removido.");
  }
  async function bulkDel() {
    const ids = selIds;
    const n = rebuildFat(txs.filter((t) => !ids.includes(t.id)));
    setTxs(n);
    setSelIds([]);
    setShowBulkDel(false);
    if (db) await Promise.all(ids.map((id) => fsDel(db, "lancamentos", id)));
    toast(`${ids.length} removidos.`);
  }
  async function bulkPay(results) {
    const m = {};
    results.forEach((r) => {
      m[r.id] = {
        paid: true,
        payMethod: r.payMethod,
        payDate: r.payDate,
        payTiming: r.payTiming,
        payAmount: r.payAmount || r.amount,
      };
    });
    const n = txs.map((t) => (m[t.id] ? { ...t, ...m[t.id] } : t));
    setTxs(n);
    setSelIds([]);
    setShowBulkPay(false);
    if (db)
      await Promise.all(
        Object.keys(m).map((id) =>
          fsSave(
            db,
            "lancamentos",
            id,
            n.find((t) => t.id === id),
          ),
        ),
      );
    toast(`${results.length} pagos.`);
  }

  function TxBadge({ usr }) {
    return (
      <span
        style={{
          background: ub[usr] || "rgba(201,168,76,0.1)",
          color: uc[usr] || th.acc,
          borderRadius: 3,
          padding: "1px 6px",
          marginRight: 4,
          fontSize: 10,
          fontWeight: 600,
          fontFamily: GEO,
        }}
      >
        {usr}
      </span>
    );
  }
  function TxRow({ t, selected, onSel }) {
    const cat = allCats.find((c) => c.id === t.category);
    const isInc = t.type === "income";
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "11px 14px",
          background: th.sur,
          border: `1px solid ${t.isFatura ? "rgba(201,168,76,0.4)" : th.bdr}`,
          borderRadius: 8,
          marginBottom: 6,
          opacity: t.paid ? 0.65 : 1,
          borderLeft: `3px solid ${t.paid ? th.bdr : isInc ? "#5A8A3A" : "#8A3A3A"}`,
        }}
      >
        <input
          type="checkbox"
          checked={!!selected}
          onChange={() => onSel(t.id)}
          style={{
            accentColor: GOLD,
            width: 15,
            height: 15,
            flexShrink: 0,
            cursor: "pointer",
          }}
        />
        <span style={{ fontSize: 14, minWidth: 20, textAlign: "center" }}>
          {cat?.icon || "◈"}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: th.txt,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              textDecoration: t.paid ? "line-through" : "none",
              fontFamily: GEO,
            }}
          >
            {t.desc}
          </div>
          <div
            style={{
              fontSize: 11,
              color: th.t2,
              marginTop: 2,
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 3,
            }}
          >
            <TxBadge usr={t.user} />
            <span style={{ fontFamily: GEO }}>
              {cat?.label} ·{" "}
              {new Date(t.date + "T12:00").toLocaleDateString("pt-BR")}
            </span>
            {t.recurring && (
              <span style={{ color: th.acc, fontSize: 10, fontFamily: GEO }}>
                ↻ fixo
              </span>
            )}
            {t.creditBilled && (
              <span
                style={{
                  fontSize: 10,
                  background: "rgba(201,168,76,0.08)",
                  color: th.acc,
                  borderRadius: 3,
                  padding: "0 5px",
                  fontFamily: GEO,
                }}
              >
                crédito→{t.ccInst?.[0]?.bym}
              </span>
            )}
            {t.paid && t.payMethod && !t.creditBilled && (
              <span
                style={{
                  fontSize: 10,
                  color: th.t2,
                  borderRadius: 3,
                  padding: "0 5px",
                  fontFamily: GEO,
                  background: th.s2,
                }}
              >
                {t.payMethod}
              </span>
            )}
          </div>
          {t.obs && (
            <div
              style={{
                fontSize: 11,
                color: th.t2,
                marginTop: 3,
                fontStyle: "italic",
                fontFamily: GEO,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {t.obs}
            </div>
          )}
        </div>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: isInc ? "#5A8A3A" : "#8A3A3A",
            whiteSpace: "nowrap",
            flexShrink: 0,
            fontFamily: GEO,
          }}
        >
          {isInc ? "+" : "-"}
          {H(fmt(t.amount))}
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            togglePaid(t);
          }}
          style={{
            background: t.paid ? "rgba(90,138,58,0.1)" : "rgba(201,168,76,0.1)",
            border: `1px solid ${t.paid ? "rgba(90,138,58,0.3)" : th.ib}`,
            borderRadius: 5,
            padding: "5px 10px",
            cursor: "pointer",
            fontSize: 11,
            color: t.paid ? "#5A8A3A" : th.acc,
            flexShrink: 0,
            fontWeight: 600,
            fontFamily: GEO,
          }}
        >
          {t.paid ? "✓ Pago" : "Pagar"}
        </button>
        <button
          onClick={() => setShowDetail(t)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 12,
            opacity: 0.5,
            color: th.t2,
          }}
        >
          ◉
        </button>
        <button
          onClick={() => editTx(t)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 12,
            opacity: 0.5,
            color: th.t2,
          }}
        >
          ✎
        </button>
        <button
          onClick={() => delTx(t.id)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: 12,
            opacity: 0.5,
            color: "#8A3A3A",
          }}
        >
          ✕
        </button>
      </div>
    );
  }
  function SumBar({ items, fatTxs }) {
    const dP =
      items
        .filter(
          (t) =>
            t.paid &&
            t.type === "expense" &&
            !t.creditBilled &&
            t.payMethod === "Débito",
        )
        .reduce((a, t) => a + t.amount, 0) +
      fatTxs.filter((t) => t.paid).reduce((a, t) => a + t.amount, 0);
    const cr = items
      .filter((t) => t.creditBilled && t.type === "expense")
      .reduce((a, t) => a + t.amount, 0);
    const fat = fatTxs.reduce((a, t) => a + t.amount, 0);
    const pend =
      items
        .filter((t) => !t.paid && t.type === "expense" && !t.creditBilled)
        .reduce((a, t) => a + t.amount, 0) +
      fatTxs.filter((t) => !t.paid).reduce((a, t) => a + t.amount, 0);
    return (
      <div
        style={{
          background: th.sur,
          border: `1px solid ${th.bdr}`,
          borderRadius: 8,
          padding: "16px",
          marginTop: 12,
          borderTop: `2px solid ${GOLD}`,
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: th.acc,
            marginBottom: 12,
            textTransform: "uppercase",
            letterSpacing: "1px",
            fontFamily: GEO,
          }}
        >
          Resumo do Período
        </div>
        {[
          ["Pago via Débito", dP, "#5A8A3A"],
          ["Pagamento Posterior (Crédito)", cr, th.acc],
          ["Fatura do Cartão", fat, GOLD_D],
          ["Pendente", pend, "#8A3A3A"],
        ].map(([l, v, c]) => (
          <div
            key={l}
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "6px 0",
              borderBottom: `1px solid ${th.bdr}`,
            }}
          >
            <span style={{ fontSize: 12, color: th.t2, fontFamily: GEO }}>
              {l}
            </span>
            <span
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: c,
                fontFamily: GEO,
              }}
            >
              {H(fmt(v))}
            </span>
          </div>
        ))}
      </div>
    );
  }
  function PayModal({ tx, onClose }) {
    const isInc = tx?.type === "income";
    const isFat = !!tx?.isFatura;
    const sI2 = {
      width: "100%",
      padding: "8px 10px",
      borderRadius: 6,
      border: `1px solid ${th.ib}`,
      fontSize: 13,
      boxSizing: "border-box",
      background: th.inp,
      color: th.txt,
      outline: "none",
      fontFamily: GEO,
    };
    const [pm, setPm] = useState("Débito");
    const [sm, setSm] = useState(true);
    const [pd, setPd] = useState(tx?.date || ymd(now));
    const [amt, setAmt] = useState(String(tx?.amount || ""));
    const def = tx?.date?.slice(0, 7) || ym(now);
    const timing = () => {
      const t2 = tx?.date?.slice(0, 7) || ym(now),
        p2 = pd.slice(0, 7);
      if (p2 < t2) return "early";
      if (p2 > t2) return "late";
      return "ontime";
    };
    function doConfirm() {
      const pi = {
        payMethod: isInc ? "Entrada" : isFat ? "Débito" : pm,
        payDate: sm ? tx?.date || ymd(now) : pd,
        payTiming: timing(),
        payAmount: parseFloat(amt) || tx?.amount || 0,
      };
      if (tx._empId !== undefined) payEmpP(tx._empId, tx._empIdx, pi);
      else if (tx._finId !== undefined) payFinP(tx._finId, tx._finIdx, pi);
      else confirmPay(tx, pi);
    }
    const sBtn = (active, label, onClick) => (
      <button
        onClick={onClick}
        style={{
          flex: 1,
          padding: "9px",
          borderRadius: 6,
          border: `1px solid ${active ? GOLD : th.ib}`,
          cursor: "pointer",
          fontWeight: active ? 600 : 400,
          background: active ? "rgba(201,168,76,0.1)" : "transparent",
          color: active ? th.acc : th.t2,
          fontSize: 13,
          fontFamily: GEO,
        }}
      >
        {label}
      </button>
    );
    return (
      <Modal
        title={isInc ? "Confirmar Recebimento" : "Registrar Pagamento"}
        onClose={onClose}
        dark={dark}
        maxW={420}
      >
        {!isInc && !isFat && (
          <div style={{ marginBottom: 14 }}>
            <div
              style={{
                fontSize: 10,
                color: th.t2,
                marginBottom: 8,
                fontFamily: GEO,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
              }}
            >
              Método
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {sBtn(pm === "Débito", "Débito", () => setPm("Débito"))}
              {sBtn(pm === "Crédito", "Crédito", () => {
                setPm("Crédito");
                if (
                  !tx.creditBilled &&
                  !tx.isFatura &&
                  tx._empId === undefined &&
                  tx._finId === undefined
                ) {
                  onClose();
                  setShowCcInst(tx);
                }
              })}
            </div>
          </div>
        )}
        {isFat && (
          <div
            style={{
              background: "rgba(201,168,76,0.08)",
              borderRadius: 6,
              padding: "8px 12px",
              marginBottom: 14,
              fontSize: 12,
              color: th.acc,
              fontFamily: GEO,
              border: `1px solid ${th.bdr}`,
            }}
          >
            Fatura — pagamento via Débito
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <div
            style={{
              fontSize: 10,
              color: th.t2,
              marginBottom: 6,
              fontFamily: GEO,
              textTransform: "uppercase",
              letterSpacing: "0.6px",
            }}
          >
            Valor
          </div>
          <input
            type="number"
            value={amt}
            onChange={(e) => setAmt(e.target.value)}
            style={sI2}
          />
        </div>
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 10,
              color: th.t2,
              marginBottom: 8,
              fontFamily: GEO,
              textTransform: "uppercase",
              letterSpacing: "0.6px",
            }}
          >
            Data
          </div>
          <div style={{ display: "flex", gap: 8, marginBottom: sm ? 0 : 10 }}>
            {sBtn(sm, "No mês", () => setSm(true))}
            {sBtn(!sm, "Outra data", () => setSm(false))}
          </div>
          {!sm && (
            <input
              type="date"
              value={pd}
              onChange={(e) => setPd(e.target.value)}
              style={{ ...sI2, marginTop: 10 }}
            />
          )}
          {sm && (
            <div
              style={{
                fontSize: 11,
                color: th.t2,
                marginTop: 8,
                fontFamily: GEO,
              }}
            >
              Mês: <strong style={{ color: th.acc }}>{def}</strong>
            </div>
          )}
        </div>
        <Btn onClick={doConfirm} style={{ width: "100%", padding: "12px" }}>
          {isInc ? "Confirmar Recebimento" : "Confirmar Pagamento"}
        </Btn>
      </Modal>
    );
  }

  // ── Header & Nav ──────────────────────────────────────────
  const logoSrc = dark ? A.logoHD : A.logoHL;
  const NAV = [
    { id: "transactions", icon: "◈", label: "Lançamentos" },
    { id: "credit", icon: "◆", label: "Fatura" },
    { id: "overdue", icon: "⚠", label: "Em Atraso" },
    { id: "fixed", icon: "◉", label: "Fixo" },
    { id: "dashboard", icon: "▣", label: "Relatório" },
    { id: "settings", icon: "⚙", label: "Config" },
  ];
  const SUB = [
    { id: "recurring", label: "Contas Fixas" },
    { id: "loans", label: "Empréstimos" },
    { id: "financing", label: "Financiamentos" },
  ];
  const isFixed = view === "fixed";
  const navActive = (id) =>
    id === "fixed" ? isFixed : !isFixed && actualView === id;

  const f2 = form || blankTx();
  const rf = recForm || blankRec();

  if (!ready)
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: th.bg,
          fontFamily: GEO,
          flexDirection: "column",
          gap: 16,
        }}
      >
        <img
          src={logoSrc}
          alt="Fortuny One"
          style={{ height: 36, objectFit: "contain", opacity: 0.7 }}
        />
        <p
          style={{
            color: th.t2,
            fontSize: 12,
            letterSpacing: "1px",
            textTransform: "uppercase",
          }}
        >
          Carregando {account.name}...
        </p>
      </div>
    );

  const bgSrc = isMobile ? (dark ? A.bgDM : A.bgLM) : dark ? A.bgDD : A.bgLD;
  // Header height calculation
  const h1 = isMobile ? 72 : 114,
    h2 = isMobile ? 64 : 0,
    h3 = isFixed ? 38 : 0,
    h4 = actualView !== "home" ? 36 : 0;
  const headerH = h1 + h2 + h3 + h4;

  return (
    <div style={{ fontFamily: GEO, background: th.bg, minHeight: "100vh" }}>
      {/* Fixed Header */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 200,
          background: th.nav,
          backdropFilter: "blur(20px)",
          borderBottom: `1px solid ${th.bdr}`,
        }}
      >
        {/* Row 1: logo + actions */}
        <div
          style={{
            maxWidth: 1400,
            margin: "0 auto",
            padding: "0 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: h1,
          }}
        >
          <div
            style={{ cursor: "pointer" }}
            onClick={() => {
              setView("home");
              setSubView(null);
            }}
          >
            <img
              src={logoSrc}
              alt="Fortuny One"
              style={{
                height: isMobile ? 44 : 80,
                objectFit: "contain",
                objectPosition: "left",
                display: "block",
              }}
            />
          </div>
          {stMsg.msg && (
            <div
              style={{
                fontSize: 11,
                color: stMsg.type === "warn" ? "#8A6A3A" : th.acc,
                fontFamily: GEO,
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                background: th.sur,
                borderRadius: 20,
                padding: "4px 14px",
                border: `1px solid ${th.bdr}`,
                whiteSpace: "nowrap",
                pointerEvents: "none",
              }}
            >
              {stMsg.msg}
            </div>
          )}
          {/* Desktop nav - centered */}
          {!isMobile && (
            <div
              style={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 2,
              }}
            >
              {NAV.map((n) => {
                const act = navActive(n.id);
                return (
                  <button
                    key={n.id}
                    onClick={() => {
                      if (n.id === "fixed") {
                        setView("fixed");
                        if (!subView) setSubView("recurring");
                      } else {
                        setView(n.id);
                        setSubView(null);
                      }
                    }}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 4,
                      padding: "10px 20px",
                      borderRadius: 8,
                      border: "none",
                      cursor: "pointer",
                      background: act
                        ? "rgba(201,168,76,0.1)"
                        : "transparent",
                      minWidth: 88,
                      flexShrink: 0,
                      transition: "background 0.2s",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 28,
                        color: act ? th.acc : th.t2,
                        lineHeight: 1,
                      }}
                    >
                      {n.icon}
                    </span>
                    <span
                      style={{
                        fontSize: 13,
                        color: act ? th.acc : th.t2,
                        fontWeight: act ? 700 : 400,
                        letterSpacing: "0.3px",
                        whiteSpace: "nowrap",
                        fontFamily: GEO,
                      }}
                    >
                      {n.label}
                    </span>
                    {act && (
                      <div
                        style={{
                          width: 18,
                          height: 2,
                          background: GOLD,
                          borderRadius: 1,
                        }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
            {allUserAccs.length > 1 && (
              <button
                onClick={() => setShowAccMenu((s) => !s)}
                style={{
                  background: "rgba(201,168,76,0.1)",
                  border: `1px solid ${th.bdr}`,
                  borderRadius: 20,
                  padding: "5px 12px",
                  cursor: "pointer",
                  fontSize: 11,
                  color: th.acc,
                  fontFamily: GEO,
                }}
              >
                {account.label || account.name} ▾
              </button>
            )}
            <button
              onClick={() => setHidden((h) => !h)}
              style={{
                background: "transparent",
                border: `1px solid ${th.bdr}`,
                borderRadius: 8,
                width: 40,
                height: 40,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: th.t2,
                fontSize: 16,
              }}
            >
              {hidden ? "◎" : "◉"}
            </button>
            <button
              onClick={() => setDark((d) => !d)}
              style={{
                background: "transparent",
                border: `1px solid ${th.bdr}`,
                borderRadius: 8,
                width: 40,
                height: 40,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 0,
              }}
            >
              <img
                src={dark ? A.icL : A.icD}
                alt=""
                style={{ width: 20, height: 20, objectFit: "contain" }}
              />
            </button>
            <button
              onClick={onLogout}
              title="Sair"
              style={{
                background: "transparent",
                border: `1px solid ${th.bdr}`,
                borderRadius: 8,
                width: 40,
                height: 40,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#8A3A3A",
                fontSize: 16,
              }}
            >
              ⏻
            </button>
          </div>
        </div>
        {/* Account dropdown */}
        {showAccMenu && (
          <div
            style={{
              position: "absolute",
              top: h1,
              right: 20,
              background: th.sur,
              border: `1px solid ${th.bdr}`,
              borderRadius: 10,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
              zIndex: 300,
              minWidth: 220,
              padding: "8px 0",
            }}
          >
            {allUserAccs.map((acc) => (
              <button
                key={acc.id}
                onClick={async () => {
                  setShowAccMenu(false);
                  await onSwitchAccount(acc);
                }}
                style={{
                  width: "100%",
                  padding: "10px 16px",
                  textAlign: "left",
                  background:
                    acc.id === account.id
                      ? "rgba(201,168,76,0.08)"
                      : "transparent",
                  border: "none",
                  cursor: "pointer",
                  color: acc.id === account.id ? th.acc : th.txt,
                  fontSize: 13,
                  fontFamily: GEO,
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <div
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: "50%",
                    background: `linear-gradient(135deg,${GOLD_D},${GOLD})`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#0D1A00",
                    fontSize: 13,
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {acc.name[0]}
                </div>
                <div>
                  <div
                    style={{ fontWeight: acc.id === account.id ? 700 : 400 }}
                  >
                    {acc.name}
                  </div>
                  <div style={{ fontSize: 10, color: th.t2 }}>{acc.label}</div>
                </div>
                {acc.id === account.id && (
                  <span style={{ marginLeft: "auto", color: GOLD }}>✓</span>
                )}
              </button>
            ))}
          </div>
        )}
        {/* Row 2: nav icons - mobile only */}
        {isMobile && <div
          style={{
            display: "flex",
            alignItems: "stretch",
            height: h2,
            overflow: "hidden",
          }}
        >
          {NAV.map((n) => {
            const act = navActive(n.id);
            return (
              <button
                key={n.id}
                onClick={() => {
                  if (n.id === "fixed") {
                    setView("fixed");
                    if (!subView) setSubView("recurring");
                  } else {
                    setView(n.id);
                    setSubView(null);
                  }
                }}
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 3,
                  padding: "6px 2px",
                  borderRadius: 0,
                  border: "none",
                  cursor: "pointer",
                  background: act ? "rgba(201,168,76,0.1)" : "transparent",
                  transition: "background 0.2s",
                }}
              >
                <span
                  style={{
                    fontSize: isMobile ? 20 : 24,
                    color: act ? th.acc : th.t2,
                    lineHeight: 1,
                  }}
                >
                  {n.icon}
                </span>
                <span
                  style={{
                    fontSize: isMobile ? 9 : 11,
                    color: act ? th.acc : th.t2,
                    fontWeight: act ? 700 : 400,
                    letterSpacing: "0.3px",
                    whiteSpace: "nowrap",
                    fontFamily: GEO,
                  }}
                >
                  {n.label}
                </span>
                {act && (
                  <div
                    style={{
                      width: 18,
                      height: 2,
                      background: GOLD,
                      borderRadius: 1,
                    }}
                  />
                )}
              </button>
            );
          })}
        </div>}
        {/* Row 3: fixed sub-nav */}
        {isFixed && (
          <div
            style={{
              maxWidth: 1400,
              margin: "0 auto",
              padding: "0 20px",
              display: "flex",
              gap: 6,
              height: h3,
              alignItems: "center",
              borderTop: `1px solid ${th.bdr}`,
              background: dark ? "rgba(6,18,33,0.5)" : "rgba(245,240,232,0.7)",
            }}
          >
            {SUB.map((s) => {
              const act = subView === s.id;
              return (
                <button
                  key={s.id}
                  onClick={() => setSubView(s.id)}
                  style={{
                    padding: "4px 14px",
                    borderRadius: 20,
                    border: `1px solid ${act ? GOLD : th.bdr}`,
                    cursor: "pointer",
                    fontSize: 11,
                    fontWeight: act ? 700 : 400,
                    background: act ? "rgba(201,168,76,0.1)" : "transparent",
                    color: act ? th.acc : th.t2,
                    fontFamily: GEO,
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        )}
        {/* Row 4: period filter */}
        {actualView !== "home" && (
          <div
            style={{
              maxWidth: 1400,
              margin: "0 auto",
              padding: "4px 20px",
              display: "flex",
              alignItems: "center",
              gap: 8,
              borderTop: `1px solid ${th.bdr}`,
              flexWrap: "wrap",
              background: dark ? "rgba(6,18,33,0.3)" : "rgba(245,240,232,0.5)",
            }}
          >
            <select
              value={filterMode}
              onChange={(e) => setFilterMode(e.target.value)}
              style={{
                padding: "4px 8px",
                borderRadius: 5,
                border: `1px solid ${th.ib}`,
                fontSize: 11,
                background: th.inp,
                color: th.txt,
                outline: "none",
                fontFamily: GEO,
              }}
            >
              <option value="month">Por mês</option>
              <option value="custom">Período</option>
            </select>
            {filterMode === "month" ? (
              <>
                <button
                  onClick={() => chMonth(-1)}
                  style={{
                    background: "transparent",
                    border: `1px solid ${th.bdr}`,
                    borderRadius: 5,
                    width: 26,
                    height: 26,
                    cursor: "pointer",
                    fontSize: 13,
                    color: th.t2,
                  }}
                >
                  ‹
                </button>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    minWidth: 90,
                    textAlign: "center",
                    color: th.acc,
                    fontFamily: GEO,
                  }}
                >
                  {MONTHS[selMonth]} {selYear}
                </span>
                <button
                  onClick={() => chMonth(1)}
                  style={{
                    background: "transparent",
                    border: `1px solid ${th.bdr}`,
                    borderRadius: 5,
                    width: 26,
                    height: 26,
                    cursor: "pointer",
                    fontSize: 13,
                    color: th.t2,
                  }}
                >
                  ›
                </button>
                <button
                  onClick={() => {
                    setSelMonth(now.getMonth());
                    setSelYear(now.getFullYear());
                  }}
                  style={{
                    background: "rgba(201,168,76,0.08)",
                    border: `1px solid ${th.bdr}`,
                    borderRadius: 5,
                    padding: "3px 10px",
                    cursor: "pointer",
                    fontSize: 10,
                    color: th.acc,
                    fontFamily: GEO,
                  }}
                >
                  Hoje
                </button>
              </>
            ) : (
              <>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{
                    padding: "4px 8px",
                    borderRadius: 5,
                    border: `1px solid ${th.ib}`,
                    fontSize: 11,
                    background: th.inp,
                    color: th.txt,
                    outline: "none",
                  }}
                />
                <span style={{ fontSize: 11, color: th.t2 }}>–</span>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{
                    padding: "4px 8px",
                    borderRadius: 5,
                    border: `1px solid ${th.ib}`,
                    fontSize: 11,
                    background: th.inp,
                    color: th.txt,
                    outline: "none",
                  }}
                />
              </>
            )}
            {syncing && (
              <span
                style={{
                  fontSize: 10,
                  color: th.t2,
                  marginLeft: "auto",
                  fontFamily: GEO,
                }}
              >
                sincronizando...
              </span>
            )}
          </div>
        )}
      </div>

      {/* Content */}
      <div
        style={{
          paddingTop: headerH,
          maxWidth: 1400,
          margin: "0 auto",
          padding: isMobile ? `${headerH + 16}px 20px 60px` : `${headerH + 24}px 40px 80px`,
        }}
      >
        {/* HOME */}
        {actualView === "home" && (
          <div
            style={{
              position: "relative",
              minHeight: `calc(100vh - ${headerH + 76}px)`,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                position: "fixed",
                top: headerH,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 0,
                backgroundImage: `url(${bgSrc})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
                opacity: dark ? 0.3 : 0.2,
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "relative",
                zIndex: 1,
                textAlign: "center",
                padding: "2rem",
              }}
            >
              <div style={{ marginBottom: 32 }}>
                <h2
                  style={{
                    margin: "0 0 6px",
                    fontSize: isMobile ? 22 : 38,
                    fontWeight: 700,
                    color: th.txt,
                    fontFamily: GEO,
                  }}
                >
                  Olá, {user.name}!
                </h2>
                <p
                  style={{
                    margin: 0,
                    fontSize: isMobile ? 13 : 18,
                    color: th.t2,
                    fontFamily: GEO,
                  }}
                >
                  O que deseja fazer?
                </p>
              </div>
              <Btn
                onClick={() => {
                  setForm(blankTx());
                  setEditId(null);
                  setFormErr("");
                  setShowForm(true);
                }}
                style={{
                  padding: isMobile ? "16px 36px" : "22px 72px",
                  fontSize: isMobile ? 15 : 20,
                  letterSpacing: "1px",
                  textTransform: "uppercase",
                  boxShadow: `0 8px 32px rgba(201,168,76,0.3)`,
                }}
              >
                Novo Lançamento
              </Btn>
              <div
                style={{
                  marginTop: 28,
                  display: "flex",
                  gap: 16,
                  justifyContent: "center",
                  flexWrap: "wrap",
                }}
              >
                {[
                  ["Lançamentos", () => goTo("transactions")],
                  ["Relatório", () => goTo("dashboard")],
                ].map(([l, fn]) => (
                  <Btn
                    key={l}
                    onClick={fn}
                    variant="ghost"
                    style={{
                      padding: "9px 22px",
                      fontSize: 12,
                      color: dark ? GOLD_L : GOLD_D,
                      border: dark
                        ? "1px solid rgba(201,168,76,0.4)"
                        : "1px solid rgba(154,122,46,0.6)",
                    }}
                  >
                    {l}
                  </Btn>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* LANÇAMENTOS */}
        {actualView === "transactions" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: th.acc,
                  margin: 0,
                  fontFamily: GEO,
                }}
              >
                Lançamentos
              </h2>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  onClick={() =>
                    setSelIds(allSel ? [] : fltTxs.map((t) => t.id))
                  }
                  style={{
                    fontSize: 11,
                    background: "rgba(201,168,76,0.08)",
                    border: `1px solid ${th.bdr}`,
                    borderRadius: 5,
                    padding: "5px 10px",
                    cursor: "pointer",
                    color: th.t2,
                    fontFamily: GEO,
                  }}
                >
                  {allSel ? "Desmarcar" : "Selecionar todos"}
                </button>
                <Btn
                  onClick={() => {
                    setForm(blankTx());
                    setEditId(null);
                    setFormErr("");
                    setShowForm(true);
                  }}
                  style={{ padding: "8px 16px", fontSize: 12 }}
                >
                  + Novo
                </Btn>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
                marginBottom: 14,
              }}
            >
              <MultiSel
                label="Pessoa"
                options={appUsers.map((u) => ({ value: u, label: u }))}
                selected={fltU}
                onChange={setFltU}
                cmap={uc}
                dark={dark}
              />
              <MultiSel
                label="Categoria"
                options={[
                  ...incCats.map((c) => ({ value: c.id, label: c.label })),
                  ...expCats.map((c) => ({ value: c.id, label: c.label })),
                ]}
                selected={fltC}
                onChange={setFltC}
                dark={dark}
              />
              <input
                value={fltS}
                onChange={(e) => setFltS(e.target.value)}
                placeholder="Buscar..."
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: `1px solid ${th.ib}`,
                  fontSize: 12,
                  background: th.inp,
                  color: th.txt,
                  outline: "none",
                  minWidth: 140,
                  fontFamily: GEO,
                }}
              />
              <SortBar value={fltSort} onChange={setFltSort} dark={dark} />
              {(fltU.length > 0 || fltC.length > 0 || fltS) && (
                <button
                  onClick={() => {
                    setFltU([]);
                    setFltC([]);
                    setFltS("");
                  }}
                  style={{
                    fontSize: 11,
                    color: "#8A3A3A",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontFamily: GEO,
                  }}
                >
                  Limpar
                </button>
              )}
            </div>
            {fatPeriod.map((t) => (
              <div
                key={t.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "11px 14px",
                  background: th.sur,
                  border: `1px solid rgba(201,168,76,0.4)`,
                  borderRadius: 8,
                  marginBottom: 6,
                  borderLeft: `3px solid ${GOLD}`,
                  opacity: t.paid ? 0.65 : 1,
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: th.acc,
                      fontFamily: GEO,
                      textDecoration: t.paid ? "line-through" : "none",
                    }}
                  >
                    Fatura do Cartão
                  </div>
                  <div style={{ fontSize: 11, color: th.t2, fontFamily: GEO }}>
                    Compras de {addM(t.date.slice(0, 7), -1)} ·{" "}
                    {t.srcs?.length || 0} itens ·{" "}
                    <span
                      style={{
                        cursor: "pointer",
                        color: th.acc,
                        fontWeight: 600,
                      }}
                      onClick={() => {
                        setFatNav(t.date.slice(0, 7));
                        goTo("credit");
                      }}
                    >
                      Ver →
                    </span>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: th.acc,
                    fontFamily: GEO,
                  }}
                >
                  {H(fmt(t.amount))}
                </div>
                <button
                  onClick={() => togglePaid(t)}
                  style={{
                    background: t.paid
                      ? "rgba(90,138,58,0.1)"
                      : "rgba(201,168,76,0.1)",
                    border: `1px solid ${t.paid ? "rgba(90,138,58,0.3)" : th.ib}`,
                    borderRadius: 5,
                    padding: "5px 12px",
                    cursor: "pointer",
                    fontSize: 11,
                    color: t.paid ? "#5A8A3A" : th.acc,
                    fontWeight: 600,
                    fontFamily: GEO,
                  }}
                >
                  {t.paid ? "✓ Pago" : "Pagar"}
                </button>
              </div>
            ))}
            {fltTxs.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: th.t2,
                  padding: "4rem 0",
                  fontFamily: GEO,
                }}
              >
                Nenhum lançamento no período.
              </div>
            ) : (
              fltTxs.map((t) => (
                <TxRow
                  key={t.id}
                  t={t}
                  selected={selIds.includes(t.id)}
                  onSel={(id) =>
                    setSelIds((p) =>
                      p.includes(id) ? p.filter((x) => x !== id) : [...p, id],
                    )
                  }
                />
              ))
            )}
            <SumBar
              items={txs.filter(pF).filter((t) => !t.isFatura)}
              fatTxs={fatPeriod}
            />
            {selIds.length > 0 && (
              <div
                style={{
                  position: "fixed",
                  bottom: 24,
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: dark ? NAVY_M : "#1A1209",
                  borderRadius: 12,
                  padding: "12px 24px",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  boxShadow: "0 4px 24px rgba(0,0,0,0.5)",
                  zIndex: 150,
                  minWidth: 300,
                  border: `1px solid ${th.bdr}`,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    color: GOLD,
                    fontWeight: 600,
                    fontFamily: GEO,
                  }}
                >
                  {selIds.length} selecionados
                </span>
                <Btn
                  onClick={() => setShowBulkPay(true)}
                  style={{ padding: "7px 16px", fontSize: 12 }}
                >
                  Pagar
                </Btn>
                <Btn
                  onClick={() => setShowBulkDel(true)}
                  variant="danger"
                  style={{ padding: "7px 16px", fontSize: 12 }}
                >
                  Remover
                </Btn>
                <button
                  onClick={() => setSelIds([])}
                  style={{
                    background: "none",
                    color: th.t2,
                    border: "none",
                    cursor: "pointer",
                    fontSize: 18,
                  }}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        )}

        {/* FATURA */}
        {actualView === "credit" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: th.acc,
                  margin: 0,
                  fontFamily: GEO,
                }}
              >
                Fatura do Cartão
              </h2>
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <button
                  onClick={() => setFatNav(addM(fatYM, -1))}
                  style={{
                    background: th.s2,
                    border: `1px solid ${th.bdr}`,
                    borderRadius: 6,
                    width: 30,
                    height: 30,
                    cursor: "pointer",
                    fontSize: 14,
                    color: th.t2,
                  }}
                >
                  ‹
                </button>
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: th.acc,
                    minWidth: 70,
                    textAlign: "center",
                    fontFamily: GEO,
                  }}
                >
                  {fatYM}
                </span>
                <button
                  onClick={() => setFatNav(addM(fatYM, 1))}
                  style={{
                    background: th.s2,
                    border: `1px solid ${th.bdr}`,
                    borderRadius: 6,
                    width: 30,
                    height: 30,
                    cursor: "pointer",
                    fontSize: 14,
                    color: th.t2,
                  }}
                >
                  ›
                </button>
                <button
                  onClick={() => setFatNav(selYM)}
                  style={{
                    background: "rgba(201,168,76,0.08)",
                    border: `1px solid ${th.bdr}`,
                    borderRadius: 6,
                    padding: "4px 10px",
                    cursor: "pointer",
                    fontSize: 11,
                    color: th.acc,
                    fontFamily: GEO,
                  }}
                >
                  Atual
                </button>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                flexWrap: "wrap",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <MultiSel
                label="Pessoa"
                options={appUsers.map((u) => ({ value: u, label: u }))}
                selected={ccU}
                onChange={setCcU}
                cmap={uc}
                dark={dark}
              />
              <MultiSel
                label="Categoria"
                options={expCats.map((c) => ({ value: c.id, label: c.label }))}
                selected={ccC}
                onChange={setCcC}
                dark={dark}
              />
              <input
                value={ccS}
                onChange={(e) => setCcS(e.target.value)}
                placeholder="Buscar..."
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: `1px solid ${th.ib}`,
                  fontSize: 12,
                  background: th.inp,
                  color: th.txt,
                  outline: "none",
                  minWidth: 140,
                  fontFamily: GEO,
                }}
              />
              <SortBar value={ccSort} onChange={setCcSort} dark={dark} />
            </div>
            <div
              style={{
                ...sc,
                marginBottom: 16,
                borderTop: `3px solid ${GOLD}`,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: th.acc,
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  fontFamily: GEO,
                }}
              >
                Fatura Fechada
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: th.t2,
                  marginBottom: 12,
                  fontFamily: GEO,
                }}
              >
                Compras de <strong>{addM(fatYM, -1)}</strong> · Vencimento em{" "}
                <strong>{fatYM}</strong>
              </div>
              {fatTx && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "12px 14px",
                    background: th.s2,
                    borderRadius: 8,
                    marginBottom: 12,
                    border: `1px solid ${th.bdr}`,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: th.acc,
                        fontFamily: GEO,
                      }}
                    >
                      Total: {H(fmt(fatTotal))}
                    </div>
                    <div
                      style={{ fontSize: 11, color: th.t2, fontFamily: GEO }}
                    >
                      {fatTx.paid ? "Paga" : "Pendente"}
                    </div>
                  </div>
                  <button
                    onClick={() => togglePaid(fatTx)}
                    style={{
                      background: fatTx.paid
                        ? "rgba(90,138,58,0.1)"
                        : `linear-gradient(135deg,${GOLD_D},${GOLD})`,
                      border: fatTx.paid
                        ? "1px solid rgba(90,138,58,0.3)"
                        : "none",
                      borderRadius: 6,
                      padding: "8px 18px",
                      cursor: "pointer",
                      fontSize: 12,
                      color: fatTx.paid ? "#5A8A3A" : "#0D1A00",
                      fontWeight: 700,
                      fontFamily: GEO,
                    }}
                  >
                    {fatTx.paid ? "✓ Paga" : "Pagar Fatura"}
                  </button>
                </div>
              )}
              {sortedFatFech.map((t) => {
                const cat = allCats.find((c) => c.id === t.category);
                const ii = t.ccInst?.findIndex((p) => p.bym === fatYM) ?? -1;
                const inst = t.ccInst?.[ii];
                const tot = t.ccInst?.length || 1;
                return (
                  <div
                    key={t.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 14px",
                      background: th.sur,
                      border: `1px solid ${th.bdr}`,
                      borderRadius: 6,
                      marginBottom: 5,
                    }}
                  >
                    <span style={{ fontSize: 13 }}>{cat?.icon || "◆"}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          color: th.txt,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontFamily: GEO,
                        }}
                      >
                        {t.desc}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: th.t2,
                          display: "flex",
                          gap: 4,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            background: ub[t.user] || "rgba(201,168,76,0.1)",
                            color: uc[t.user] || th.acc,
                            borderRadius: 3,
                            padding: "0 5px",
                            fontSize: 10,
                            fontWeight: 600,
                            fontFamily: GEO,
                          }}
                        >
                          {t.user}
                        </span>
                        {cat?.label}
                        {tot > 1 && (
                          <span
                            style={{
                              background: "rgba(201,168,76,0.1)",
                              color: th.acc,
                              borderRadius: 3,
                              padding: "0 5px",
                              fontSize: 10,
                              fontFamily: GEO,
                            }}
                          >
                            {ii + 1}/{tot}
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: th.acc,
                        fontFamily: GEO,
                      }}
                    >
                      {H(fmt(inst?.value || t.amount))}
                    </div>
                    <button
                      onClick={() => setShowDetail(t)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 12,
                        opacity: 0.5,
                        color: th.t2,
                      }}
                    >
                      ◉
                    </button>
                  </div>
                );
              })}
              {fatFech.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    color: th.t2,
                    padding: "2rem 0",
                    fontSize: 13,
                    fontFamily: GEO,
                  }}
                >
                  Nenhuma compra para esta fatura.
                </div>
              )}
            </div>
            <div style={{ ...sc, borderTop: `3px solid ${GOLD_D}` }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: GOLD_D,
                  marginBottom: 4,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  fontFamily: GEO,
                }}
              >
                Fatura em Aberto
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: th.t2,
                  marginBottom: 12,
                  fontFamily: GEO,
                }}
              >
                Compras de <strong>{fatYM}</strong> — serão cobradas nos
                próximos meses
              </div>
              {sortedFatAberta.map((t) => {
                const cat = allCats.find((c) => c.id === t.category);
                const tot = t.ccInst?.length || 1;
                const fb = t.ccInst?.[0]?.bym;
                return (
                  <div
                    key={t.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 14px",
                      background: th.sur,
                      border: `1px solid ${th.bdr}`,
                      borderRadius: 6,
                      marginBottom: 5,
                    }}
                  >
                    <span style={{ fontSize: 13 }}>{cat?.icon || "◆"}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          color: th.txt,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontFamily: GEO,
                        }}
                      >
                        {t.desc}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: th.t2,
                          display: "flex",
                          gap: 4,
                          flexWrap: "wrap",
                        }}
                      >
                        <span
                          style={{
                            background: ub[t.user] || "rgba(201,168,76,0.1)",
                            color: uc[t.user] || th.acc,
                            borderRadius: 3,
                            padding: "0 5px",
                            fontSize: 10,
                            fontWeight: 600,
                            fontFamily: GEO,
                          }}
                        >
                          {t.user}
                        </span>
                        {tot}x
                        {fb && (
                          <span
                            style={{
                              background: "rgba(154,122,46,0.1)",
                              color: GOLD_D,
                              borderRadius: 3,
                              padding: "0 5px",
                              fontSize: 10,
                              fontFamily: GEO,
                            }}
                          >
                            cobra em {fb}
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: GOLD_D,
                        fontFamily: GEO,
                      }}
                    >
                      {H(fmt(t.amount))}
                    </div>
                    <button
                      onClick={() => setShowDetail(t)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 12,
                        opacity: 0.5,
                        color: th.t2,
                      }}
                    >
                      ◉
                    </button>
                  </div>
                );
              })}
              {fatAberta.length === 0 && (
                <div
                  style={{
                    textAlign: "center",
                    color: th.t2,
                    padding: "2rem 0",
                    fontSize: 13,
                    fontFamily: GEO,
                  }}
                >
                  Nenhuma em aberto.
                </div>
              )}
            </div>
          </div>
        )}

        {/* EM ATRASO */}
        {actualView === "overdue" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "#8A3A3A",
                  margin: 0,
                  fontFamily: GEO,
                }}
              >
                Em Atraso
              </h2>
              {ovdItems.length > 0 && (
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#8A3A3A",
                    fontFamily: GEO,
                  }}
                >
                  Total: {H(fmt(ovdItems.reduce((a, t) => a + t.amount, 0)))}
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 14,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <input
                value={ovdS}
                onChange={(e) => setOvdS(e.target.value)}
                placeholder="Buscar..."
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: `1px solid ${th.ib}`,
                  fontSize: 12,
                  background: th.inp,
                  color: th.txt,
                  outline: "none",
                  minWidth: 150,
                  fontFamily: GEO,
                }}
              />
              <SortBar value={ovdSort} onChange={setOvdSort} dark={dark} />
            </div>
            {ovdItems.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: "#5A8A3A",
                  padding: "4rem 0",
                  fontSize: 14,
                  fontFamily: GEO,
                }}
              >
                Nenhuma conta em atraso. ✓
              </div>
            ) : (
              ovdItems.map((t) => (
                <TxRow key={t.id} t={t} selected={false} onSel={() => {}} />
              ))
            )}
          </div>
        )}

        {/* CONTAS FIXAS */}
        {actualView === "recurring" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: th.acc,
                  margin: 0,
                  fontFamily: GEO,
                }}
              >
                Contas Fixas
              </h2>
              <Btn
                onClick={() => {
                  setRecForm(blankRec());
                  setEditRecId(null);
                  setRecErr("");
                  setShowRecForm(true);
                }}
                style={{ padding: "8px 16px", fontSize: 12 }}
              >
                + Nova
              </Btn>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 14,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <input
                value={recS}
                onChange={(e) => setRecS(e.target.value)}
                placeholder="Buscar..."
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: `1px solid ${th.ib}`,
                  fontSize: 12,
                  background: th.inp,
                  color: th.txt,
                  outline: "none",
                  minWidth: 150,
                  fontFamily: GEO,
                }}
              />
              <SortBar value={recSort} onChange={setRecSort} dark={dark} />
            </div>
            {sortedRecs.map((r) => {
              const cat = allCats.find((c) => c.id === r.category);
              return (
                <div
                  key={r.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "12px 16px",
                    background: th.sur,
                    border: `1px solid ${th.bdr}`,
                    borderRadius: 8,
                    marginBottom: 6,
                    borderLeft: `3px solid ${GOLD}`,
                  }}
                >
                  <span style={{ fontSize: 16 }}>{cat?.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: th.txt,
                        fontFamily: GEO,
                      }}
                    >
                      {r.desc}
                    </div>
                    <div style={{ fontSize: 11, color: th.t2 }}>
                      <span
                        style={{
                          background: ub[r.user] || "rgba(201,168,76,0.1)",
                          color: uc[r.user] || th.acc,
                          borderRadius: 3,
                          padding: "1px 6px",
                          marginRight: 4,
                          fontSize: 10,
                          fontWeight: 600,
                          fontFamily: GEO,
                        }}
                      >
                        {r.user}
                      </span>
                      <span style={{ fontFamily: GEO }}>
                        {cat?.label} · desde {r.startYM}
                      </span>
                    </div>
                  </div>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: th.acc,
                      marginRight: 8,
                      fontFamily: GEO,
                    }}
                  >
                    {H(fmt(r.amount))}
                    <span
                      style={{ fontSize: 10, color: th.t2, fontWeight: 400 }}
                    >
                      /mês
                    </span>
                  </span>
                  <button
                    onClick={() => {
                      setRecForm({ ...r, amount: String(r.amount) });
                      setEditRecId(r.id);
                      setRecErr("");
                      setShowRecForm(true);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      opacity: 0.5,
                      color: th.t2,
                    }}
                  >
                    ✎
                  </button>
                  <button
                    onClick={() => delRec(r.id)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      opacity: 0.5,
                      color: "#8A3A3A",
                    }}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {/* EMPRÉSTIMOS */}
        {actualView === "loans" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: th.acc,
                    margin: 0,
                    fontFamily: GEO,
                  }}
                >
                  Empréstimos
                </h2>
                <div
                  style={{
                    fontSize: 12,
                    color: th.t2,
                    marginTop: 2,
                    fontFamily: GEO,
                  }}
                >
                  Pendente:{" "}
                  <strong style={{ color: "#8A3A3A" }}>
                    {H(fmt(activeEmps.reduce((a, e) => a + e.pendVal, 0)))}
                  </strong>
                </div>
              </div>
              <Btn
                onClick={() => {
                  setEmpErr("");
                  setShowEmpForm(true);
                }}
                style={{ padding: "8px 16px", fontSize: 12 }}
              >
                + Novo
              </Btn>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 14,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <input
                value={empS}
                onChange={(e) => setEmpS(e.target.value)}
                placeholder="Buscar..."
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: `1px solid ${th.ib}`,
                  fontSize: 12,
                  background: th.inp,
                  color: th.txt,
                  outline: "none",
                  minWidth: 150,
                  fontFamily: GEO,
                }}
              />
              <SortBar value={empSort} onChange={setEmpSort} dark={dark} />
            </div>
            {sortedEmps.map((e) => {
              const pct = e.parcelas > 0 ? e.paidCnt / e.parcelas : 0;
              const iB = e.direction !== "lend";
              return (
                <div
                  key={e.id}
                  style={{
                    ...sc,
                    marginBottom: 14,
                    borderTop: `3px solid ${iB ? GOLD_D : "#5A8A3A"}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 10,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 700,
                          color: th.txt,
                          fontFamily: GEO,
                        }}
                      >
                        {e.desc}
                      </div>
                      <div style={{ fontSize: 11, color: th.t2 }}>
                        <span
                          style={{
                            background: ub[e.user] || "rgba(201,168,76,0.1)",
                            color: uc[e.user] || th.acc,
                            borderRadius: 3,
                            padding: "1px 6px",
                            marginRight: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            fontFamily: GEO,
                          }}
                        >
                          {e.user}
                        </span>
                        <span style={{ fontFamily: GEO }}>
                          {e.paidCnt}/{e.parcelas} parcelas
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => delEmp(e.id)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: 12,
                        opacity: 0.4,
                        color: "#8A3A3A",
                      }}
                    >
                      ✕
                    </button>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 12,
                      color: th.t2,
                      marginBottom: 8,
                      fontFamily: GEO,
                    }}
                  >
                    <span>
                      Pago:{" "}
                      <strong style={{ color: "#5A8A3A" }}>
                        {H(fmt(e.paidVal))}
                      </strong>
                    </span>
                    <span>
                      Pendente:{" "}
                      <strong style={{ color: "#8A3A3A" }}>
                        {H(fmt(e.pendVal))}
                      </strong>
                    </span>
                  </div>
                  <div
                    style={{
                      height: 4,
                      background: th.s2,
                      borderRadius: 4,
                      overflow: "hidden",
                      marginBottom: 12,
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: `${Math.round(pct * 100)}%`,
                        background: `linear-gradient(90deg,${GOLD_D},${GOLD})`,
                        borderRadius: 4,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill,minmax(110px,1fr))",
                      gap: 6,
                    }}
                  >
                    {e.items.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => toggleEmpP(e.id, i)}
                        style={{
                          background: p.paid ? "rgba(90,138,58,0.08)" : th.s2,
                          border: `1px solid ${p.paid ? "rgba(90,138,58,0.3)" : th.bdr}`,
                          borderRadius: 6,
                          padding: "8px",
                          cursor: "pointer",
                          textAlign: "left",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            color: th.t2,
                            fontFamily: GEO,
                          }}
                        >
                          {i + 1}ª ·{" "}
                          {new Date(p.date + "T12:00").toLocaleDateString(
                            "pt-BR",
                            { month: "short", year: "2-digit" },
                          )}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: p.paid ? "#5A8A3A" : "#8A3A3A",
                            fontFamily: GEO,
                          }}
                        >
                          {H(fmt(p.value))}
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: p.paid ? "#5A8A3A" : th.t2,
                            fontFamily: GEO,
                          }}
                        >
                          {p.paid ? "✓ Pago" : "Pendente"}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
            <button
              onClick={() => setShowArchEmp((a) => !a)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "none",
                border: `1px solid ${th.bdr}`,
                borderRadius: 8,
                padding: "10px 16px",
                cursor: "pointer",
                width: "100%",
                color: th.t2,
                fontSize: 12,
                fontWeight: 600,
                marginTop: 16,
                fontFamily: GEO,
              }}
            >
              <span>Arquivados ({archEmps.length})</span>
              <span style={{ marginLeft: "auto" }}>
                {showArchEmp ? "▲" : "▼"}
              </span>
            </button>
            {showArchEmp &&
              archEmps.map((e) => (
                <div
                  key={e.id}
                  style={{
                    ...sc,
                    marginBottom: 6,
                    marginTop: 5,
                    opacity: 0.7,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: th.txt,
                        fontFamily: GEO,
                      }}
                    >
                      ✓ {e.desc}
                    </div>
                    <div
                      style={{ fontSize: 11, color: th.t2, fontFamily: GEO }}
                    >
                      {e.parcelas}x · {H(fmt(e.paidVal))}
                    </div>
                  </div>
                  <button
                    onClick={() => delEmp(e.id)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      opacity: 0.5,
                      color: "#8A3A3A",
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
          </div>
        )}

        {/* FINANCIAMENTOS */}
        {actualView === "financing" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <div>
                <h2
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: th.acc,
                    margin: 0,
                    fontFamily: GEO,
                  }}
                >
                  Financiamentos
                </h2>
                <div
                  style={{
                    fontSize: 12,
                    color: th.t2,
                    marginTop: 2,
                    fontFamily: GEO,
                  }}
                >
                  Pendente:{" "}
                  <strong style={{ color: th.acc }}>
                    {H(fmt(activeFins.reduce((a, f) => a + f.pendVal, 0)))}
                  </strong>
                </div>
              </div>
              <Btn
                onClick={() => {
                  setFinErr("");
                  setShowFinForm(true);
                }}
                style={{ padding: "8px 16px", fontSize: 12 }}
              >
                + Novo
              </Btn>
            </div>
            <div
              style={{
                display: "flex",
                gap: 8,
                marginBottom: 14,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <input
                value={finS}
                onChange={(e) => setFinS(e.target.value)}
                placeholder="Buscar..."
                style={{
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: `1px solid ${th.ib}`,
                  fontSize: 12,
                  background: th.inp,
                  color: th.txt,
                  outline: "none",
                  minWidth: 150,
                  fontFamily: GEO,
                }}
              />
              <SortBar value={finSort} onChange={setFinSort} dark={dark} />
            </div>
            {sortedFins.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  color: th.t2,
                  padding: "3rem 0",
                  fontFamily: GEO,
                }}
              >
                Nenhum financiamento ativo.
              </div>
            ) : (
              sortedFins.map((f) => {
                const pct = f.parcelas > 0 ? f.paidCnt / f.parcelas : 0;
                const diff = f.totalParcelas - f.totalOriginal;
                return (
                  <div
                    key={f.id}
                    style={{
                      ...sc,
                      marginBottom: 14,
                      borderTop: `3px solid ${GOLD}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 10,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: th.txt,
                            fontFamily: GEO,
                          }}
                        >
                          {f.desc}
                        </div>
                        <div style={{ fontSize: 11, color: th.t2 }}>
                          <span
                            style={{
                              background: ub[f.user] || "rgba(201,168,76,0.1)",
                              color: uc[f.user] || th.acc,
                              borderRadius: 3,
                              padding: "1px 6px",
                              marginRight: 4,
                              fontSize: 10,
                              fontWeight: 600,
                              fontFamily: GEO,
                            }}
                          >
                            {f.user}
                          </span>
                          <span style={{ fontFamily: GEO }}>
                            {f.paidCnt}/{f.parcelas}x
                            {Math.abs(diff) > 0.01 && (
                              <span style={{ color: "#8A3A3A" }}>
                                {" "}
                                +{H(fmt(diff))} juros
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={() => delFin(f.id)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: 12,
                          opacity: 0.4,
                          color: "#8A3A3A",
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    <div
                      style={{
                        background: th.s2,
                        borderRadius: 6,
                        padding: "10px 14px",
                        marginBottom: 10,
                        display: "flex",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 8,
                        border: `1px solid ${th.bdr}`,
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: 9,
                            color: th.t2,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            fontFamily: GEO,
                          }}
                        >
                          Valor Financiado
                        </div>
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 800,
                            color: th.txt,
                            fontFamily: GEO,
                          }}
                        >
                          {H(fmt(f.totalOriginal))}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div
                          style={{
                            fontSize: 9,
                            color: th.t2,
                            fontWeight: 600,
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            fontFamily: GEO,
                          }}
                        >
                          Saldo Restante
                        </div>
                        <div
                          style={{
                            fontSize: 15,
                            fontWeight: 800,
                            color: th.acc,
                            fontFamily: GEO,
                          }}
                        >
                          {H(fmt(f.pendVal))}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        height: 4,
                        background: th.s2,
                        borderRadius: 4,
                        overflow: "hidden",
                        marginBottom: 12,
                      }}
                    >
                      <div
                        style={{
                          height: "100%",
                          width: `${Math.round(pct * 100)}%`,
                          background: `linear-gradient(90deg,${GOLD_D},${GOLD})`,
                          borderRadius: 4,
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill,minmax(110px,1fr))",
                        gap: 6,
                      }}
                    >
                      {f.items.map((p, i) => (
                        <button
                          key={i}
                          onClick={() => toggleFinP(f.id, i)}
                          style={{
                            background: p.paid ? "rgba(90,138,58,0.08)" : th.s2,
                            border: `1px solid ${p.paid ? "rgba(90,138,58,0.3)" : th.bdr}`,
                            borderRadius: 6,
                            padding: "8px",
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 10,
                              color: th.t2,
                              fontFamily: GEO,
                            }}
                          >
                            {i + 1}ª ·{" "}
                            {new Date(p.date + "T12:00").toLocaleDateString(
                              "pt-BR",
                              { month: "short", year: "2-digit" },
                            )}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: p.paid ? "#5A8A3A" : th.acc,
                              fontFamily: GEO,
                            }}
                          >
                            {H(fmt(p.value))}
                          </div>
                          <div
                            style={{
                              fontSize: 9,
                              color: p.paid ? "#5A8A3A" : th.t2,
                              fontFamily: GEO,
                            }}
                          >
                            {p.paid ? "✓ Pago" : "Pendente"}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
            <button
              onClick={() => setShowArchFin((a) => !a)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "none",
                border: `1px solid ${th.bdr}`,
                borderRadius: 8,
                padding: "10px 16px",
                cursor: "pointer",
                width: "100%",
                color: th.t2,
                fontSize: 12,
                fontWeight: 600,
                marginTop: 16,
                fontFamily: GEO,
              }}
            >
              <span>Arquivados ({archFins.length})</span>
              <span style={{ marginLeft: "auto" }}>
                {showArchFin ? "▲" : "▼"}
              </span>
            </button>
            {showArchFin &&
              archFins.map((f) => (
                <div
                  key={f.id}
                  style={{
                    ...sc,
                    marginBottom: 6,
                    marginTop: 5,
                    opacity: 0.7,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: th.txt,
                        fontFamily: GEO,
                      }}
                    >
                      ✓ {f.desc}
                    </div>
                    <div
                      style={{ fontSize: 11, color: th.t2, fontFamily: GEO }}
                    >
                      {f.parcelas}x · {H(fmt(f.totalOriginal))}
                    </div>
                  </div>
                  <button
                    onClick={() => delFin(f.id)}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: 12,
                      opacity: 0.5,
                      color: "#8A3A3A",
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
          </div>
        )}

        {/* DASHBOARD / RELATÓRIO */}
        {actualView === "dashboard" && (
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 8,
              }}
            >
              <h2
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: th.acc,
                  margin: 0,
                  fontFamily: GEO,
                }}
              >
                Relatório
              </h2>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <MultiSel
                  label="Pessoa"
                  options={appUsers.map((u) => ({ value: u, label: u }))}
                  selected={dashU}
                  onChange={setDashU}
                  cmap={uc}
                  dark={dark}
                />
                <MultiSel
                  label="Categoria"
                  options={[
                    ...incCats.map((c) => ({ value: c.id, label: c.label })),
                    ...expCats.map((c) => ({ value: c.id, label: c.label })),
                  ]}
                  selected={dashC}
                  onChange={setDashC}
                  dark={dark}
                />
                {(dashU.length > 0 || dashC.length > 0) && (
                  <button
                    onClick={() => {
                      setDashU([]);
                      setDashC([]);
                    }}
                    style={{
                      fontSize: 11,
                      color: "#8A3A3A",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontFamily: GEO,
                    }}
                  >
                    Limpar
                  </button>
                )}
              </div>
            </div>
            {/* Saldo card */}
            <div
              style={{
                background: `linear-gradient(135deg,${NAVY_M},${NAVY_L})`,
                borderRadius: 12,
                padding: "24px",
                marginBottom: 16,
                border: `1px solid rgba(201,168,76,0.3)`,
                boxShadow: `0 4px 24px rgba(0,0,0,0.3)`,
                position: "relative",
              }}
              onClick={() => setInfoTip(null)}
            >
              {/* Tooltip bubble */}
              {infoTip && (
                <div
                  style={{
                    position: "absolute",
                    top: infoTip === "periodo" ? 36 : 140,
                    left: infoTip === "projecao" ? "auto" : 24,
                    right: infoTip === "projecao" ? 24 : "auto",
                    zIndex: 10,
                    background: NAVY_M,
                    border: `1px solid rgba(201,168,76,0.4)`,
                    borderRadius: 8,
                    padding: "10px 14px",
                    maxWidth: 260,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ fontSize: 10, color: GOLD_L, lineHeight: 1.5, fontFamily: GEO }}>
                    {infoTip === "periodo" && "Valor de rendimentos do mês atual, com base no valor das entradas e saídas atuais."}
                    {infoTip === "acumulado" && "Valor que foi herdado de meses anteriores."}
                    {infoTip === "projecao" && 'Saldo que demonstra qual valor atual será repassado para o mês seguinte — soma do rendimento do mês atual com o valor total acumulado.'}
                  </div>
                </div>
              )}

              {/* Top: Saldo no período (destaque) */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                  <span style={{
                    fontSize: 9,
                    color: "rgba(201,168,76,0.7)",
                    fontWeight: 600,
                    textTransform: "uppercase",
                    letterSpacing: "1.5px",
                    fontFamily: GEO,
                  }}>
                    Saldo no período
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); setInfoTip(infoTip === "periodo" ? null : "periodo"); }}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(201,168,76,0.5)", fontSize: 11, padding: 0, lineHeight: 1 }}
                    title="Saiba mais"
                  >ℹ</button>
                </div>
                <div style={{
                  fontSize: isMobile ? 28 : 48,
                  fontWeight: 800,
                  color: balP >= 0 ? GOLD_L : "#C07070",
                  fontFamily: GEO,
                }}>
                  {H(fmt(balP))}
                </div>
              </div>

              {/* Bottom: Acumulado + Projeção */}
              <div style={{
                paddingTop: 12,
                borderTop: "1px solid rgba(201,168,76,0.2)",
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 8,
              }}>
                {/* Saldo acumulado (editável) */}
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                    <span style={{
                      fontSize: 9,
                      color: "rgba(201,168,76,0.6)",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      fontFamily: GEO,
                    }}>
                      Saldo acumulado
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setInfoTip(infoTip === "acumulado" ? null : "acumulado"); }}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(201,168,76,0.4)", fontSize: 10, padding: 0, lineHeight: 1 }}
                    >ℹ</button>
                  </div>
                  {editSaldo ? (
                    <div style={{ display: "flex", gap: 6 }} onClick={(e) => e.stopPropagation()}>
                      <input
                        autoFocus
                        value={saldoInp}
                        onChange={(e) => setSaldoInp(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            const v = parseFloat(saldoInp.replace(",", "."));
                            if (!isNaN(v)) {
                              const nm = { ...meta, saldoTotal: v };
                              setMeta(nm);
                              setEditSaldo(false);
                              if (db) fsSave(db, "meta", "saldo", nm);
                              toast("Saldo atualizado.");
                            }
                          }
                          if (e.key === "Escape") setEditSaldo(false);
                        }}
                        style={{
                          padding: "4px 10px",
                          fontSize: 16,
                          fontWeight: 700,
                          width: 160,
                          background: "rgba(255,255,255,0.1)",
                          color: GOLD_L,
                          border: `1px solid rgba(201,168,76,0.4)`,
                          borderRadius: 6,
                          outline: "none",
                          fontFamily: GEO,
                        }}
                      />
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const v = parseFloat(saldoInp.replace(",", "."));
                          if (!isNaN(v)) {
                            const nm = { ...meta, saldoTotal: v };
                            setMeta(nm);
                            setEditSaldo(false);
                            if (db) fsSave(db, "meta", "saldo", nm);
                            toast("Saldo atualizado.");
                          }
                        }}
                        style={{
                          background: "rgba(201,168,76,0.2)",
                          color: GOLD_L,
                          border: `1px solid rgba(201,168,76,0.4)`,
                          borderRadius: 6,
                          padding: "4px 12px",
                          cursor: "pointer",
                          fontWeight: 700,
                          fontFamily: GEO,
                        }}
                      >✓</button>
                    </div>
                  ) : (
                    <div
                      style={{ fontSize: 16, fontWeight: 700, color: GOLD_L, fontFamily: GEO, cursor: "pointer" }}
                      onClick={(e) => { e.stopPropagation(); setSaldoInp(String(meta.saldoTotal || 0)); setEditSaldo(true); }}
                      title="Clique para editar"
                    >
                      {H(fmt(meta.saldoTotal || 0))} ✎
                    </div>
                  )}
                </div>

                {/* Projeção acumulada */}
                <div style={{ textAlign: "right" }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, marginBottom: 2 }}>
                    <span style={{
                      fontSize: 9,
                      color: "rgba(201,168,76,0.6)",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      fontFamily: GEO,
                    }}>
                      Projeção acumulada
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); setInfoTip(infoTip === "projecao" ? null : "projecao"); }}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "rgba(201,168,76,0.4)", fontSize: 10, padding: 0, lineHeight: 1 }}
                    >ℹ</button>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "rgba(201,168,76,0.8)", fontFamily: GEO }}>
                    {H(fmt(saldoPreview))}
                  </div>
                </div>
              </div>
            </div>
            {/* KPIs */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(5,1fr)",
                gap: 8,
                marginBottom: 16,
              }}
            >
              {[
                ["Entradas", H(fmt(totInc)), "#5A8A3A", ""],
                [
                  "Saídas",
                  H(fmt(totSaidas)),
                  "#8A3A3A",
                  `Pago: ${H(fmt(pagoDb))}`,
                ],
                ["Crédito", H(fmt(pagPost)), GOLD_D, "p/ fatura futura"],
                ["Pendente", H(fmt(totPend)), "#8A6A3A", ""],
                ["Saldo", H(fmt(balP)), balP >= 0 ? "#5A8A3A" : "#8A3A3A", ""],
              ].map(([l, v, c, s]) => (
                <div
                  key={l}
                  style={{
                    ...sc,
                    gridColumn: l === "Saldo" && isMobile ? "span 2" : "auto",
                    borderTop: `2px solid ${c}`,
                  }}
                >
                  <div
                    style={{
                      fontSize: 9,
                      color: th.t2,
                      marginBottom: 6,
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.8px",
                      fontFamily: GEO,
                    }}
                  >
                    {l}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? 14 : 20,
                      fontWeight: 800,
                      color: c,
                      fontFamily: GEO,
                    }}
                  >
                    {v}
                  </div>
                  {s && (
                    <div
                      style={{
                        fontSize: 10,
                        color: th.t2,
                        marginTop: 3,
                        fontFamily: GEO,
                      }}
                    >
                      {s}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Fatura cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 10,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  ...sc,
                  borderTop: `2px solid ${GOLD}`,
                  cursor: "pointer",
                }}
                onClick={() => {
                  goTo("credit");
                  setFatNav(selYM);
                }}
              >
                <div
                  style={{
                    fontSize: 9,
                    color: th.acc,
                    fontWeight: 700,
                    marginBottom: 6,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    fontFamily: GEO,
                  }}
                >
                  Fatura Fechada · {MONTHS[selMonth]} {selYear}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: th.acc,
                    fontFamily: GEO,
                  }}
                >
                  {H(fmt(curFatTotal))}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: th.t2,
                    marginTop: 4,
                    fontFamily: GEO,
                  }}
                >
                  {curFatItems.length} compra
                  {curFatItems.length !== 1 ? "s" : ""} de {addM(selYM, -1)} ·
                  Ver →
                </div>
              </div>
              <div
                style={{
                  ...sc,
                  borderTop: `2px solid ${GOLD_D}`,
                  cursor: "pointer",
                }}
                onClick={() => {
                  goTo("credit");
                  setFatNav(addM(selYM, 1));
                }}
              >
                <div
                  style={{
                    fontSize: 9,
                    color: GOLD_D,
                    fontWeight: 700,
                    marginBottom: 6,
                    textTransform: "uppercase",
                    letterSpacing: "0.8px",
                    fontFamily: GEO,
                  }}
                >
                  Próxima Fatura · {MONTHS[(selMonth + 1) % 12]}
                </div>
                <div
                  style={{
                    fontSize: 22,
                    fontWeight: 800,
                    color: GOLD_D,
                    fontFamily: GEO,
                  }}
                >
                  {H(fmt(nxtFatTotal))}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: th.t2,
                    marginTop: 4,
                    fontFamily: GEO,
                  }}
                >
                  {nxtFatItems.length} compra
                  {nxtFatItems.length !== 1 ? "s" : ""} de {selYM} · Ver →
                </div>
              </div>
            </div>
            {/* Charts */}
            <div style={{ ...sc, marginBottom: 16 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  marginBottom: 4,
                  color: th.acc,
                  fontFamily: GEO,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Evolução Mensal
              </div>
              <div
                style={{ position: "relative", height: isMobile ? 160 : 200 }}
              >
                <canvas ref={chartRef} />
              </div>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 12,
              }}
            >
              <div
                style={{
                  ...sc,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    marginBottom: 10,
                    color: th.acc,
                    alignSelf: "flex-start",
                    fontFamily: GEO,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Pago via Débito
                </div>
                <Gauge paid={pagoDb} total={totSaidas} dark={dark} />
                <div style={{ marginTop: 12, width: "100%" }}>
                  {[
                    ["Pago (débito)", pagoDb, "#5A8A3A"],
                    ["Crédito", pagPost, GOLD_D],
                    ["Pendente", totPend, "#8A3A3A"],
                  ].map(([l, v, c]) => (
                    <div
                      key={l}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        fontSize: 12,
                        color: th.t2,
                        marginBottom: 5,
                      }}
                    >
                      <span style={{ fontFamily: GEO }}>{l}</span>
                      <span
                        style={{ fontWeight: 700, color: c, fontFamily: GEO }}
                      >
                        {H(fmt(v))}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={sc}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    marginBottom: 12,
                    color: th.acc,
                    fontFamily: GEO,
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                  }}
                >
                  Por Categoria
                </div>
                {byCat.length === 0 ? (
                  <div
                    style={{
                      color: th.t2,
                      fontSize: 13,
                      textAlign: "center",
                      paddingTop: 40,
                      fontFamily: GEO,
                    }}
                  >
                    Sem gastos no período.
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        position: "relative",
                        height: 120,
                        marginBottom: 10,
                      }}
                    >
                      <canvas ref={pieRef} />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "4px 12px",
                      }}
                    >
                      {byCat.map((c) => (
                        <span
                          key={c.id}
                          style={{
                            fontSize: 11,
                            color: th.t2,
                            display: "flex",
                            alignItems: "center",
                            gap: 4,
                            fontFamily: GEO,
                          }}
                        >
                          <span
                            style={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              background: c.color,
                              display: "inline-block",
                            }}
                          />
                          {c.label} {H(fmt(c.total))}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* CONFIGURAÇÕES */}
        {actualView === "settings" && (
          <div>
            <h2
              style={{
                fontSize: 18,
                fontWeight: 700,
                color: th.acc,
                margin: "0 0 20px",
                fontFamily: GEO,
              }}
            >
              Configurações
            </h2>
            <SettingsView
              expCats={expCats}
              setExpCats={setExpCats}
              incCats={incCats}
              setIncCats={setIncCats}
              appUsers={appUsers}
              setAppUsers={setAppUsers}
              uc={uc}
              setUc={setUc}
              ub={ub}
              setUb={setUb}
              pgs={pgs}
              setPgs={setPgs}
              pgColors={pgColors}
              setPgColors={setPgColors}
              db={db}
              adminDb={adminDb}
              userId={user.id}
              accountId={account.id}
              toast={toast}
              sc={sc}
              sI={sI}
              sL={sL}
              th={th}
            />
          </div>
        )}
      </div>

      {/* Modals */}
      {showPay && <PayModal tx={showPay} onClose={() => setShowPay(null)} />}
      {showCcInst && (
        <CcInstModal
          src={showCcInst}
          dark={dark}
          onConfirm={(d) => confirmCcInst(showCcInst, d)}
          onClose={() => setShowCcInst(null)}
        />
      )}
      {showUnpay && (
        <Modal
          title="Remover Pagamento"
          onClose={() => setShowUnpay(null)}
          dark={dark}
          maxW={380}
        >
          <p
            style={{
              fontSize: 14,
              color: th.txt,
              marginBottom: 10,
              fontFamily: GEO,
            }}
          >
            Remover pagamento de <strong>"{showUnpay.desc}"</strong>?
          </p>
          {showUnpay.creditBilled && (
            <div
              style={{
                background: "rgba(139,106,58,0.1)",
                border: "1px solid rgba(139,106,58,0.3)",
                borderRadius: 6,
                padding: "8px 12px",
                marginBottom: 12,
                fontSize: 12,
                color: "#8A6A3A",
                fontFamily: GEO,
              }}
            >
              Isto recalculará a fatura do cartão.
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <Btn
              onClick={() => setShowUnpay(null)}
              variant="ghost"
              style={{ flex: 1, padding: "11px" }}
            >
              Cancelar
            </Btn>
            <Btn
              onClick={() => confirmUnpay(showUnpay)}
              variant="danger"
              style={{ flex: 1, padding: "11px" }}
            >
              Confirmar
            </Btn>
          </div>
        </Modal>
      )}
      {showDetail && (
        <Modal
          title="Detalhes"
          onClose={() => setShowDetail(null)}
          dark={dark}
          maxW={420}
        >
          {(() => {
            const t = showDetail;
            const cat = allCats.find((c) => c.id === t.category);
            return (
              <>
                <div
                  style={{
                    background: th.s2,
                    borderRadius: 10,
                    padding: "16px",
                    marginBottom: 16,
                    textAlign: "center",
                    border: `1px solid ${th.bdr}`,
                  }}
                >
                  <div style={{ fontSize: 28, marginBottom: 6 }}>
                    {cat?.icon || "◈"}
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 800,
                      color: t.type === "income" ? "#5A8A3A" : "#8A3A3A",
                      fontFamily: GEO,
                    }}
                  >
                    {t.type === "income" ? "+" : "-"}
                    {H(fmt(t.amount))}
                  </div>
                  <div
                    style={{
                      fontSize: 13,
                      color: th.txt,
                      marginTop: 4,
                      fontFamily: GEO,
                    }}
                  >
                    {t.desc}
                  </div>
                </div>
                {[
                  ["Categoria", cat?.label],
                  ["Responsável", t.user],
                  [
                    "Data",
                    new Date(t.date + "T12:00").toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    }),
                  ],
                  ["Status", t.paid ? "Pago" : "Pendente"],
                  t.paid && ["Método", t.payMethod],
                  t.ccInst && [
                    "Parcelas CC",
                    `${t.ccInst.length}x · fatura de ${t.ccInst[0]?.bym}`,
                  ],
                ]
                  .filter(Boolean)
                  .map(([l, v]) =>
                    v ? (
                      <div
                        key={l}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          padding: "8px 0",
                          borderBottom: `1px solid ${th.bdr}`,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            color: th.t2,
                            fontFamily: GEO,
                          }}
                        >
                          {l}
                        </span>
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: th.txt,
                            fontFamily: GEO,
                          }}
                        >
                          {v}
                        </span>
                      </div>
                    ) : null,
                  )}
              </>
            );
          })()}{" "}
        </Modal>
      )}
      {showBulkPay && (
        <BulkPayModal
          items={selTxObjs}
          onConfirm={bulkPay}
          onClose={() => setShowBulkPay(false)}
          dark={dark}
        />
      )}
      {showBulkDel && (
        <Modal
          title="Confirmar Remoção"
          onClose={() => setShowBulkDel(false)}
          dark={dark}
          maxW={380}
        >
          <p
            style={{
              fontSize: 14,
              color: th.txt,
              marginBottom: 16,
              fontFamily: GEO,
            }}
          >
            Remover <strong>{selIds.length}</strong> lançamento
            {selIds.length > 1 ? "s" : ""}?
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn
              onClick={() => setShowBulkDel(false)}
              variant="ghost"
              style={{ flex: 1, padding: "11px" }}
            >
              Cancelar
            </Btn>
            <Btn
              onClick={bulkDel}
              variant="danger"
              style={{ flex: 1, padding: "11px" }}
            >
              Remover
            </Btn>
          </div>
        </Modal>
      )}
      {showForm && (
        <TxFormModal
          form={f2}
          setForm={setForm}
          editId={editId}
          formErr={formErr}
          onSave={saveTx}
          onClose={() => {
            setShowForm(false);
            setEditId(null);
            setForm(null);
            setFormErr("");
          }}
          dark={dark}
          expCats={expCats}
          incCats={incCats}
          appUsers={appUsers}
          uc={uc}
          th={th}
          isMobile={isMobile}
        />
      )}
      {showRecForm && (
        <RecFormModal
          recForm={rf}
          setRecForm={setRecForm}
          editRecId={editRecId}
          recErr={recErr}
          onSave={saveRec}
          onClose={() => {
            setShowRecForm(false);
            setEditRecId(null);
            setRecForm(null);
            setRecErr("");
          }}
          dark={dark}
          expCats={expCats}
          appUsers={appUsers}
          uc={uc}
          th={th}
        />
      )}
      {showEmpForm && (
        <EmpForm
          onSave={saveEmpData}
          onClose={() => {
            setShowEmpForm(false);
            setEmpErr("");
          }}
          dark={dark}
          appUsers={appUsers}
          uc={uc}
          empErr={empErr}
          setEmpErr={setEmpErr}
        />
      )}
      {showFinForm && (
        <FinForm
          onSave={saveFinData}
          onClose={() => {
            setShowFinForm(false);
            setFinErr("");
          }}
          dark={dark}
          appUsers={appUsers}
          uc={uc}
          expCats={expCats}
          finErr={finErr}
          setFinErr={setFinErr}
        />
      )}
    </div>
  );
}

// ── ROOT ─────────────────────────────────────────────────────
export default function App() {
  const [dark, setDark] = useState(sysDark());
  const [app, setApp] = useState(null);

  async function getAccsUsrs() {
    let accs = storeGet("accounts"),
      usrs = storeGet("users");
    try {
      const adb = dbA();
      const [la, lu] = await Promise.all([
        fsAll(adb, "accounts"),
        fsAll(adb, "users"),
      ]);
      if (la.length) {
        accs = la;
        _store.accounts = la.map((a) => ({ ...a }));
      }
      if (lu.length) {
        usrs = lu;
        _store.users = lu.map((u) => ({ ...u }));
      }
    } catch (e) {
      console.warn("Firebase Admin indisponível:", e.message);
    }
    return { accs, usrs };
  }
  async function adminSave(col, id, dat) {
    storeSave(col, id, dat);
    try {
      await fsSave(dbA(), col, id, dat);
    } catch (e) {
      console.warn(e);
    }
  }
  async function adminDel(col, id) {
    storeDel(col, id);
    try {
      await fsDel(dbA(), col, id);
    } catch {}
  }

  useEffect(() => {
    const s = loadSess();
    if (!s) return;
    (async () => {
      try {
        const { accs, usrs } = await getAccsUsrs();
        if (s.type === "admin") {
          setApp({ type: "admin", accounts: accs, users: usrs });
        } else if (s.type === "user") {
          const usr = usrs.find((u) => u.id === s.userId) ||
            usrs.find((u) => cleanCPF(u.cpf) === cleanCPF(s.cpf));
          const acc = accs.find((a) => a.id === s.accountId);
          if (!usr || !acc || usr.blocked || acc.blocked) {
            clearSess();
            return;
          }
          const allUA = accs.filter(
            (a) =>
              (usr.accountIds || []).includes(a.id) && a.active && !a.blocked,
          );
          const cfg = parseCfg(acc.firebaseConfig);
          let db = null,
            adminDb = null;
          if (cfg) {
            try {
              db = getDb(cfg);
              adminDb = dbA();
            } catch {}
          }
          let pgColors = {};
          if (adminDb) {
            try {
              const pref = await fsOne(
                adminDb,
                "userPrefs",
                `${usr.id}__${acc.id}`,
              );
              if (pref?.pgColors) pgColors = pref.pgColors;
            } catch {}
          }
          setApp({
            type: "user",
            user: usr,
            account: acc,
            allUserAccs: allUA,
            db,
            adminDb,
            pgColors,
          });
        }
      } catch {
        clearSess();
      }
    })();
  }, []);

  function onLogin(data) {
    if (data.type === "admin") {
      if (data.accounts) _store.accounts = data.accounts.map((a) => ({ ...a }));
      if (data.users) _store.users = data.users.map((u) => ({ ...u }));
    }
    setApp(data);
  }
  function onLogout() {
    clearSess();
    setApp(null);
  }
  async function onSwitchAccount(newAcc) {
    if (!app || app.type !== "user") return;
    if (newAcc.blocked) return;
    const cfg = parseCfg(newAcc.firebaseConfig);
    let db = null,
      adminDb = null;
    if (cfg) {
      try {
        db = getDb(cfg);
        adminDb = dbA();
      } catch {}
    }
    let pgColors = {};
    if (adminDb) {
      try {
        const pref = await fsOne(
          adminDb,
          "userPrefs",
          `${app.user.id}__${newAcc.id}`,
        );
        if (pref?.pgColors) pgColors = pref.pgColors;
      } catch {}
    }
    saveSess({ ...loadSess(), accountId: newAcc.id, accountName: newAcc.name });
    setApp((p) => ({ ...p, db, adminDb, account: newAcc, pgColors }));
  }

  if (!app)
    return <LoginScreen dark={dark} setDark={setDark} onLogin={onLogin} />;
  if (app.type === "admin")
    return (
      <AdminPanel
        dark={dark}
        setDark={setDark}
        initAccs={storeGet("accounts")}
        initUsrs={storeGet("users")}
        onSave={adminSave}
        onDelete={adminDel}
        onLogout={onLogout}
      />
    );
  return (
    <FinancialApp
      dark={dark}
      setDark={setDark}
      db={app.db}
      adminDb={app.adminDb}
      user={app.user}
      account={app.account}
      allUserAccs={app.allUserAccs}
      onLogout={onLogout}
      onSwitchAccount={onSwitchAccount}
      initPgColors={app.pgColors || {}}
    />
  );
}
